import { Box, Button, Fade, Grid, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import styles from "./homepage.module.css";
import { useContext, useEffect, useLayoutEffect, useState, useRef, useCallback } from "react";
import Footer from "src/components/Footer";
import FooterSignUp from "src/components/Footer/FooterSignUp";
import { WidgetContext } from "src/contexts/WidgetContext";
import SettingsSpeedDial from "src/components/SettingsLombard/SettingSpeedDial";
import BenchDrawer from "src/components/BenchDrawer/BenchDrawer";
import RateLimitDrawer from "src/components/BenchDrawer/RateLimitDrawer";
import WelcomeDrawer from "src/components/WelcomeDrawer/WelcomeDrawer";
import { getWaiver } from "src/api/getWaiver";
import SuspenseLoader from "src/components/SuspenseLoader/SuspenseLoader";
import { getNews } from "src/api/getNews";
import { getRosters } from "src/api/getRosters";
import { getRankings } from "src/api/getRankings";
import { getAllRankings } from "src/api/getAllRankings";
import { PlatformUserData, SelectedTeams } from "src/mockup/UserPreferencesData";
import { useAuth0 } from "@auth0/auth0-react";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { getPocToken } from "src/api/getPocToken";
import { postUserPreferences } from "src/api/postUserPreferences";
import { getUserPreferences } from "src/api/getUserPrefrences";
import { TeamsHomePageRef } from "../../components/PlatformSelector/TeamsDropdown";
import { validateTeamData } from "src/utils/teamValidation";
import { getUserLeagues } from "src/api/getUserLeagues";
import EndpointErrorsMessage from "src/components/ErrorsInScreen/EndpointErrorsMessage";
import LeagueSelector from "src/components/LeagueSelector/LeagueSelector";
import { isCacheValid } from "src/utils/isCacheValid";
import SectionHomePage from "src/components/SectionHomePage/SectionHomePage";
import AnimatedButton from "src/components/AnimatedButton/AnimatedButton";
import ArticlesPartnerList from "src/components/Articles/ArticlesPartnerList";
import ArticleIcon from "@mui/icons-material/Article";
import { getSuperFunction } from "src/api/getSuperFunction";
import { App } from "@capacitor/app";
import AudioSound from "src/components/AudioPlayer/AudioSound";
import { getWaiverStream } from "src/api/getWaiverStream";
import { getStartStream } from "src/api/getStartStream";
import { getRundownStream } from "src/api/getRundownStream";
import { getDraftAdviceStream } from "src/api/getDraftAdviceStream";
import { getCredits } from "src/api/getCredits";
import { claimAppDownloadBonus } from "src/api/claimAppDownloadBonus";
import { getNewNonLoggedUserStatus, dismissNewNonLoggedUser } from "src/api/newNonLoggedUserStatus";
import { Capacitor } from "@capacitor/core";
import { BackgroundTask } from "@capawesome/capacitor-background-task";
import { startPollingForCompletion } from "src/utils/streamPolling";
import { shouldHideSignForStreamDrawer } from "src/utils/streamDrawerSign";
import { frontEnvNumber, subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import { audioService, AudioPriority } from "src/services/audioService";
import { base64ToBlob } from "src/utils/audioUtils";
import { trackSlowOperation, trackAiSettingsSaved, setAnalyticsUser, trackCreditConsumed, trackEndpointResponseTime } from "src/utils/analyticsCapFirebase";
import { isIOS, isAndroid, isMobileWebBrowser, mobile } from "../../utils/platformUtils";
import ThoughtItem from "src/components/ThoughtItem/ToughtItem";
import ThoughtItemCommon from "src/components/ThoughtItem/ThoughtItemCommon";
import { getAllowedPlatforms } from "src/api/getAllowedPlatforms";
import AudioSlider from "src/components/AudioPlayer/AudioSlider";
import { getEstimatedTimes, EstimatedTimesResponse } from "src/api/getEstimatedTimes";
import { PcmChunkPlayer } from "src/utils/pcmChunkPlayer";
import { STREAMING_AUDIO_SAMPLE_RATE, DEFAULT_AUDIO_CHANNELS } from "src/constants/audioConfig";
import {
  applyLegacyLipSyncOrSkip,
  isCapacitorNative,
  isVegaLipSyncEnabled,
  prefetchVegaFromAudioUrl,
  prepareThenApplyVegaFromAudioUrl,
  prepareVegaHeadFromAudioUrl,
  subscribeVegaUrlUpdates,
  tryApplyVegaFromAudioUrlSync,
} from "src/lipsync/vegaLipSync";
import { isUnsyncedPlatform } from "src/constants/unsyncedLeague";
import TypingDots from "src/components/Loader/TypingDots";
import CharacterBubble2, { CharacterBubble2Ref } from "src/components/CharacterViewer/CharacterBubble2";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import RotoCoinIcon from "src/assets/RotoCoin.svg";
import RotoCoinIcon2 from "src/assets/RotoCoin2.svg";
import { getSeasonPassPrice } from "src/api/getSeasonPassPrice";
import SettingsHomePage from "src/components/SettingsHomePage/SettingsHomePage";
import NotificationsModal from "src/components/Notifications/NotificationsModal";
import { usePushNotifications } from "src/hooks/usePushNotifications";
import { savePlatformPermission } from "src/api/platformPermission";
import { getYahooUserInfo } from "src/api/getYahooUserInfo";
import { getAllowedButtonsSections } from "src/api/getAllowedButtonsSections";

// Custom error class to distinguish API errors from network errors.
class ApiError extends Error {
  constructor(message) {
    super(message);
    this.name = "ApiError";
  }
}

interface YahooTeamsData {
  leagues: any[];
}

interface HomePageProps {
  isLoggedInAuth0Experience: boolean;
}

function HomePage({ isLoggedInAuth0Experience }: HomePageProps) {
  const [, setFrontRuntimeBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setFrontRuntimeBump((n) => n + 1)), []);
  // console.log("HomePage isLoggedInAuth0Experience:", isLoggedInAuth0Experience);

  // Update WidgetContext global state with the prop value
  const { isLoggedInAuth0ExperienceGlobal, setIsLoggedInAuth0ExperienceGlobal, NON_LOGGED_IN_CONFIG } = useContext(WidgetContext);

  useEffect(() => {
    setIsLoggedInAuth0ExperienceGlobal(isLoggedInAuth0Experience);
  }, [isLoggedInAuth0Experience, setIsLoggedInAuth0ExperienceGlobal]);
  // DEV FLAG: Set to false to disable credit validation (allow app usage without credits)
  // TODO: Comment this line to disable paywall for dev/testing
  // Defaults to true if env var is not set (production behavior)
  //const ENABLE_CREDIT_VALIDATION = true;
  const ENABLE_CREDIT_VALIDATION = process.env.REACT_APP_ENABLE_CREDIT_VALIDATION === "true" || process.env.REACT_APP_ENABLE_CREDIT_VALIDATION === undefined;

  // console.log("ENABLE_CREDIT_VALIDATION", ENABLE_CREDIT_VALIDATION);
  // console.log("process.env.REACT_APP_ENABLE_CREDIT_VALIDATION", process.env.REACT_APP_ENABLE_CREDIT_VALIDATION);
  // import function of WidgetContext
  const {
    handlePartnerSelect,
    openDrawer,
    closeDrawer,
    selectedPartner,
    setSelectedPartner,
    pocToken,
    leaguePlatforms,
    setLeaguePlatforms,
    isTokenLoading,
    setMessage,
    teamsDataCache,
    setTeamsDataCache,
    userDataCache,
    setUserDataCache,
    isLoadingPreferences,
    selectedTeamInfo,
    updateSectionData,
    sectionData,
    setSectionData,
    selectedTeams,
    setSelectedTeams,
    setSelectedTeamInfo,
    setYahooToken,
    yahooToken,
    yahooLeagueId,
    isYahooLoading,
    setIsYahooLoading,
    leagueIdPlatform,
    setLeagueIdPlatform,
    dataLoadedFromTeamsPage,
    setDataLoadedFromTeamsPage,
    cancelPendingRequests,
    getValidYahooToken,
    updateYahooToken,
    isDrawerOpen,
    setCurrentAbortController,
    allowedPlatforms,
    setAllowedPlatforms,
    credits,
    setCredits,
    freeCredits,
    setFreeCredits,
    nextFreeCreditsResetTime,
    setNextFreeCreditsResetTime,
    calculatorSettingsMap,
    setCalculatorSettingsMap,
    aiSettings,
    setAiSettings,
    footerSignUpDismissedAt,
    setFooterSignUpDismissedAt,
    forceSavePreferences,
    setSeasonPassPrice,
    allowedButtonsSections,
    setAllowedButtonsSections,
  } = useContext(WidgetContext);

  // State to track the current team info
  const currentTeamInfoRef = useRef(selectedTeamInfo);
  useEffect(() => {
    currentTeamInfoRef.current = selectedTeamInfo;
  }, [selectedTeamInfo]);

  // Ref for credits box to calculate animation target
  const creditsBoxRef = useRef<HTMLDivElement>(null);

  // Ref for the LeagueSelector container — used by RundownPartnerListStream to compute collapsed drawer height
  const leagueSelectorBoxRef = useRef<HTMLDivElement>(null);

  // Function to get credits box position for animation
  const getCreditsBoxPosition = useCallback(() => {
    if (creditsBoxRef.current) {
      const rect = creditsBoxRef.current.getBoundingClientRect();
      return {
        x: rect.left + rect.width / 2, // Center X
        y: rect.top + rect.height / 2, // Center Y
      };
    }
    return null;
  }, []);

  // console.log("currentTeamInfoRef", currentTeamInfoRef.current, "and selectedTeamInfo", selectedTeamInfo);

  const [expanded, setExpanded] = useState<string>("");
  const [loggingInTo, setLoggingInTo] = useState<string | null>(null);
  const [isAddIcon, setIsAddIcon] = useState(true);
  const [showPartnerList, setShowPartnerList] = useState(false);
  const [selectedButton, setSelectedButton] = useState("");
  const [shouldPlayAudio, setShouldPlayAudio] = useState(false);
  const [isSliderOpen, setIsSliderOpen] = useState(false); // state to control the visibility of the slider
  // State to control the visibility of the AI settings dialog
  const [isAiDialogOpen, setIsAiDialogOpen] = useState(false);
  // Notifications consent modal: true = show (user has not answered yet), false = hide
  const [showNotificationsModal, setShowNotificationsModal] = useState(true);
  const [isUpdatingAiSettings, setIsUpdatingAiSettings] = useState(false);
  // aiSettings now comes from WidgetContext (persisted in preferences/Mongo)

  // Refs to keep latest drawer/button state inside streaming callbacks
  const isDrawerOpenRef = useRef(isDrawerOpen);
  useEffect(() => {
    isDrawerOpenRef.current = isDrawerOpen;
  }, [isDrawerOpen]);

  const selectedButtonRef = useRef(selectedButton);
  useEffect(() => {
    selectedButtonRef.current = selectedButton;
  }, [selectedButton]);

  // State for get userId from LoginForm Component
  const [userDataPlatform, setUserDataPlatform] = useState<PlatformUserData | null>(null); // State for get teams from handleLeague
  const [teamsData, setTeamsdata] = useState<YahooTeamsData | any[]>(null);
  //State to open or close the speed dial platform button
  const [isSpeedDialOpen, setIsSpeedDialOpen] = useState(false);
  //state to open or close the payment method swipeable
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [initialPaymentView, setInitialPaymentView] = useState("menu");
  //state to open or close the bench drawer
  const [isBenchDrawerOpen, setIsBenchDrawerOpen] = useState(false);
  //state to open or close the welcome drawer (new users only, once)
  const [isWelcomeDrawerOpen, setIsWelcomeDrawerOpen] = useState(false);
  //state to open or close the rate limit drawer
  const [isRateLimitDrawerOpen, setIsRateLimitDrawerOpen] = useState(false);
  // Timestamp (ms) of the next minute boundary when the rate limit window resets
  const [rateLimitRetryAt, setRateLimitRetryAt] = useState<number | null>(null);

  // State to avoid loading data while handling the Yahoo callback
  const isHandlingYahooCallback = useRef(false);

  // NEW: Granular state to track recovery for each stream.
  const [isRecoveringFromBackground, setIsRecoveringFromBackground] = useState({
    waiver: false,
    start: false,
    rundown: false,
    DraftAdvice: false,
  });

  //State to get waiver data
  const [waiverData, setWaiverData] = useState<any>(null);
  const [loadingWaiver, setLoadingWaiver] = useState(false);

  //State to get news data
  const [newsData, setNewsData] = useState(null);
  const [loadingNews, setLoadingNews] = useState(false);

  //State to get start data
  const [startData, setStartData] = useState(null);
  const [loadingStart, setLoadingStart] = useState(false);
  //State to get rosters data
  const [rostersData, setRostersData] = useState(null);
  const [loadingRoster, setLoadingRoster] = useState(false);
  //State to get rankings data
  const [rankingsData, setRankingsData] = useState(null);
  const [loadingRankings, setLoadingRankings] = useState(false);
  //State to get all rankings data
  const [allRankingsData, setAllRankingsData] = useState(null);
  const [loadingAllRankings, setLoadingAllRankings] = useState(false);
  //State to get rundown data
  const [rundownData, setRundownData] = useState(null);
  const [loadingRundown, setLoadingRundown] = useState(false);
  //State to get draft advice data
  const [DraftAdviceData, setDraftAdviceData] = useState(null);
  const [loadingDraftAdvice, setLoadingDraftAdvice] = useState(false);
  //state to get superFunction data
  const [superFunctionData, setSuperFunctionData] = useState(null);
  const [loadingSuperFunction, setLoadingSuperFunction] = useState(false);
  //State to get hasLoadingStarted
  const [hasLoadingStarted, setHasLoadingStarted] = useState(false);
  //State to get thoughtsCommon data
  const [thoughtsCommon, setThoughtsCommon] = useState<string>("");
  // Which in-drawer stream may show thoughts on Quan's sign (trade analyze / draft refresh only).
  // "trades-result" = trade response is visible → hide the sign so the user reads/listens with lipsync.
  const [signThoughtsDrawerSource, setSignThoughtsDrawerSource] = useState<null | "trades" | "DraftAdvice" | "trades-result">(null);
  const markSignThoughtsForDrawer = useCallback((source: "trades" | "DraftAdvice" | "trades-result") => {
    setSignThoughtsDrawerSource(source);
  }, []);
  const clearSignThoughtsDrawerSource = useCallback(() => {
    setSignThoughtsDrawerSource(null);
  }, []);
  const allowSignWhileDrawerOpen =
    isDrawerOpen &&
    !!thoughtsCommon &&
    signThoughtsDrawerSource !== null &&
    selectedButton === signThoughtsDrawerSource;
  // Hide Quan's sign once the trade response is visible (trade drawer open), so the user can read/hear it.
  const hideSignForTradeResult =
    isDrawerOpen && selectedButton === "trades" && signThoughtsDrawerSource === "trades-result";
  // Clear sign when SSE leaves thought/status phase (keeps sign + manual button in sync).
  // Preserve trade markers ("trades" = analyzing, "trades-result" = response visible) so a sibling
  // stream reaching result does not blank the sign while Analyze Trade is still producing thoughts.
  const clearSignThoughtsOnResultPhase = () => {
    setThoughtsCommon("");
    setSignThoughtsDrawerSource(prev => (prev === "trades-result" || prev === "trades" ? prev : null));
  };
  //State to get media query for mobile and tablet
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  //State to know if the user is in a mobile web browser
  const isMobileWeb = isMobileWebBrowser();
  //State to know if the super analysis is complete
  const [isSuperAnalysisComplete, setIsSuperAnalysisComplete] = useState(false);

  // Quan ready state
  const [isQuanReady, setIsQuanReady] = useState(false);
  const [isReturningFromBackground, setIsReturningFromBackground] = useState(false);
  const backgroundReturnTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Debounce guard: Capacitor iOS fires appStateChange twice on resume; ignore duplicates within 500ms
  const lastBackgroundResumeRef = useRef<number>(0);
  // Counter that increments on every background return. Using a boolean (isReturningFromBackground)
  // as useCallback dep fails when the user backgrounds twice before the 10s timeout clears it.
  const [backgroundReturnId, setBackgroundReturnId] = useState(0);

  // Re-create callback identity on every background return so CharacterBubble2's useEffect
  // re-fires and reports current loading state (avoids 10s stuck loader when no rebuild is needed).

  const handleQuanReadyStateChange = useCallback((isReady: boolean) => {
    setIsQuanReady(isReady);
  }, [backgroundReturnId]);

  const showBackgroundLoader = isReturningFromBackground && aiSettings.quan && !isQuanReady;

  // Pause all audio while the background return loader is visible; resume when it disappears.
  // Uses audioService overlay (welcome, funny, trade summary HTML, trade PCM via bridges) + endpoint PCM refs.
  // Also saves/restores character talking + lip sync state across the rebuild.
  const wasCharacterTalkingRef = useRef(false);
  const savedLipSyncAudioRef = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    if (!showBackgroundLoader) return;

    // Snapshot character talking state BEFORE pausing (character may rebuild and lose refs)
    const nowPlayingAudio = audioService.getNowPlayingAudio();
    const wasTalking = nowPlayingAudio && !nowPlayingAudio.paused;
    wasCharacterTalkingRef.current = !!wasTalking;
    if (wasTalking) {
      savedLipSyncAudioRef.current = nowPlayingAudio;
    }

    audioService.pauseForBackgroundOverlay();
    waiverPcmPlayerRef.current?.pause();
    startPcmPlayerRef.current?.pause();
    rundownPcmPlayerRef.current?.pause();
    DraftAdvicePcmPlayerRef.current?.pause();

    return () => {
      // On iOS Capacitor, the audio session may still be restoring when the overlay hides.
      // A short delay lets iOS finish restoring before we attempt play().
      const isNative = !!(window as any).Capacitor?.isNativePlatform?.();
      const delayMs = isNative ? 400 : 0;

      const doResume = () => {
        audioService.resumeAfterBackgroundOverlay();
        waiverPcmPlayerRef.current?.resume();
        startPcmPlayerRef.current?.resume();
        rundownPcmPlayerRef.current?.resume();
        DraftAdvicePcmPlayerRef.current?.resume();

        // Re-apply character talking state after resume (character may have rebuilt during overlay)
        if (wasCharacterTalkingRef.current && characterRef?.current) {
          const audioEl = savedLipSyncAudioRef.current;
          if (audioEl) {
            characterRef.current.setAudioElement(audioEl);
            characterRef.current.setIsTalking(true);
          }
        }
        wasCharacterTalkingRef.current = false;
        savedLipSyncAudioRef.current = null;
      };

      if (delayMs > 0) {
        setTimeout(doResume, delayMs);
      } else {
        doResume();
      }
    };
  }, [showBackgroundLoader]);

  // State to track selected model (FREE or PREMIUM) (ESTO AHORA ES UN FALLBACK, NO ES FUENTE DE LA VERDAD ABSOLUTA.. lo que si lo es, es para super analysis el siguiente estado de subscription type segun mongodb que viene de credits. Y para los 4 endpoints el CLICK (evita race condition que genera un estado en react))
  const [selectedModel, setSelectedModel] = useState<"RotoGPT 5.0" | "RotoGPT FREE">("RotoGPT 5.0");
  // console.log("selectedModel NOW", selectedModel);

  // State to track user subscription type for Super Analysis model selection
  const [userSubscription, setUserSubscription] = useState<string>("free");
  // console.log("userSubscription HOMEPAGE NOW", userSubscription);


  // useEffect(() => {
  //   // Dev-only: evita que afecte producción
  //   if (process.env.NODE_ENV === 'production') return;

  //   let cancelled = false;

  //   const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

  //   const testText = 'Requesting League data from Sleeper API';
  //   const openMs = 5500; // tiempo con thoughtsCommon != "" (debería cubrir anim-in + idle)
  //   const closeMs = 2500; // tiempo entre poner "" (anim-out) y el siguiente ciclo
  //   const cycles = Infinity; // cámbialo por un número si quieres N veces

  //   const run = async () => {
  //     for (let i = 0; i < cycles; i++) {
  //       if (cancelled) return;

  //       setThoughtsCommon(testText);
  //       await wait(openMs);

  //       if (cancelled) return;
  //       setThoughtsCommon('');
  //       await wait(closeMs);
  //     }
  //   };

  //   run();

  //   return () => {
  //     cancelled = true;
  //   };

  // }, []);


  const noDraftErrorMessage = "Rosters are empty as this league has not had it's draft yet. Try a different league.";
  // Combine all waiver stream states into a single object to ensure atomic updates
  // and prevent race conditions.
  const [waiverStreamState, setWaiverStreamState] = useState({
    status: "",
    thought: "Loading...",
    isGeneratingResult: false,
    isStreamActive: false,
    isWaitingForChart: false,
    isGeneratingAudio: false, // Add new state for audio loader
    teamKey: null as string | null, // NEW: To identify which team this stream belongs to
  });
  // Player for live PCM chunks (waiver)
  const waiverPcmPlayerRef = useRef<PcmChunkPlayer | null>(null);
  // Buffer for audio chunks while drawer is closed (waiver)
  const waiverBufferedChunksRef = useRef<Array<{ data: string; mimeType?: string }>>([]);
  // Timer for initial slow ramp (0% → 10%) before first thought (rundown)
  const waiverInitialRampTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Player for live PCM chunks (start)
  const startPcmPlayerRef = useRef<PcmChunkPlayer | null>(null);
  // Buffer for audio chunks while drawer is closed (start)
  const startBufferedChunksRef = useRef<Array<{ data: string; mimeType?: string }>>([]);
  // Timer for initial slow ramp (0% → 10%) before first thought (start)
  const startInitialRampTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Player for live PCM chunks (rundown)
  const rundownPcmPlayerRef = useRef<PcmChunkPlayer | null>(null);
  // Buffer for audio chunks while drawer is closed (rundown)
  const rundownBufferedChunksRef = useRef<Array<{ data: string; mimeType?: string }>>([]);
  // Timer for initial slow ramp (0% → 10%) before first thought (rundown)
  const rundownInitialRampTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Player for live PCM chunks (draft advice)
  const DraftAdvicePcmPlayerRef = useRef<PcmChunkPlayer | null>(null);
  const DraftAdviceBufferedChunksRef = useRef<Array<{ data: string; mimeType?: string }>>([]);
  const DraftAdviceInitialRampTimerRef = useRef<NodeJS.Timeout | null>(null);
  // State to track if the waiver animation has completed once
  const [hasWaiverAnimationCompleted, setHasWaiverAnimationCompleted] = useState(false);

  // States for Start stream
  const [startStreamState, setStartStreamState] = useState({
    status: "",
    thought: "",
    isGeneratingResult: false,
    isStreamActive: false,
    isWaitingForChart: false,
    isGeneratingAudio: false, // Add new state for audio loader
    teamKey: null as string | null, // NEW
  });
  // State to track if the start animation has completed once
  const [hasStartAnimationCompleted, setHasStartAnimationCompleted] = useState(false);

  // States for Rundown stream
  const [rundownStreamState, setRundownStreamState] = useState({
    status: "",
    thought: "",
    isGeneratingResult: false,
    isStreamActive: false,
    isWaitingForChart: false,
    isGeneratingAudio: false, // Add new state for audio loader
    teamKey: null as string | null, // NEW
  });
  // State to track if the rundown animation has completed once
  const [hasRundownAnimationCompleted, setHasRundownAnimationCompleted] = useState(false);

  // States for Draft Advice stream
  const [DraftAdviceStreamState, setDraftAdviceStreamState] = useState({
    status: "",
    thought: "",
    isGeneratingResult: false,
    isStreamActive: false,
    isWaitingForChart: false,
    isGeneratingAudio: false,
    teamKey: null as string | null,
  });
  const [hasDraftAdviceAnimationCompleted, setHasDraftAdviceAnimationCompleted] = useState(false);

  // --- NEW: Generic state and logic for all summary audios ---
  const [summaryAudios, setSummaryAudios] = useState({
    rundown: null as string | null, // Changed from Blob to string for the URL
    DraftAdvice: null as string | null,
    waiver: null as string | null, // Changed from Blob to string for the URL
    start: null as string | null, // Changed from Blob to string for the URL
  });

  /** Stop audio from other endpoints so only the given endpoint plays. Call before starting PCM for waiver/start/rundown/draft_advice. */
  const stopOtherEndpointPcmPlayersAndService = useCallback((forEndpoint: "waiver" | "start" | "rundown" | "DraftAdvice") => {
    const nowPlayingAudio = audioService.getNowPlayingAudio();
    const currentEndpointAudioSrc = summaryAudios[forEndpoint];

    if (nowPlayingAudio && currentEndpointAudioSrc && nowPlayingAudio.src === currentEndpointAudioSrc) {
      // Audio is from current endpoint - don't stop it, only stop other PCM players
    } else if (audioService.getNowPlayingAudio()) {
      // Stop welcome (LOW) / team-name (MEDIUM) / other endpoint (HIGH) so agent PCM is the only voice.
      audioService.stopCurrentAudio();
      characterRef?.current?.setIsTalking(false);
    }

    if (forEndpoint !== "waiver" && waiverPcmPlayerRef.current) {
      waiverPcmPlayerRef.current.stop();
      waiverPcmPlayerRef.current = null;
    }
    if (forEndpoint !== "start" && startPcmPlayerRef.current) {
      startPcmPlayerRef.current.stop();
      startPcmPlayerRef.current = null;
    }
    if (forEndpoint !== "rundown" && rundownPcmPlayerRef.current) {
      rundownPcmPlayerRef.current.stop();
      rundownPcmPlayerRef.current = null;
    }
    if (forEndpoint !== "DraftAdvice" && DraftAdvicePcmPlayerRef.current) {
      DraftAdvicePcmPlayerRef.current.stop();
      DraftAdvicePcmPlayerRef.current = null;
    }
  }, [summaryAudios]);

  /** Polling SSE includes audio_summary; play it like a cache URL (Vega analyzes the WAV). */
  const applyPolledAgentAudioUrl = useCallback((forEndpoint: "waiver" | "start" | "rundown" | "DraftAdvice", url: string) => {
    if (!url) return;
    hasAudioBeenPlayed.current[forEndpoint] = false;
    if (forEndpoint === "waiver" && waiverPcmPlayerRef.current) {
      waiverPcmPlayerRef.current.stop();
      waiverPcmPlayerRef.current = null;
    }
    if (forEndpoint === "start" && startPcmPlayerRef.current) {
      startPcmPlayerRef.current.stop();
      startPcmPlayerRef.current = null;
    }
    if (forEndpoint === "rundown" && rundownPcmPlayerRef.current) {
      rundownPcmPlayerRef.current.stop();
      rundownPcmPlayerRef.current = null;
    }
    if (forEndpoint === "DraftAdvice" && DraftAdvicePcmPlayerRef.current) {
      DraftAdvicePcmPlayerRef.current.stop();
      DraftAdvicePcmPlayerRef.current = null;
    }
    setSummaryAudios((prev) => ({ ...prev, [forEndpoint]: url }));
  }, []);

  // --- Lip sync data for character animations ---
  const [lipSyncData, setLipSyncData] = useState({
    rundown: null as any,
    DraftAdvice: null as any,
    waiver: null as any,
    start: null as any,
  });
  // Ref so we can read latest lip sync when PCM playback actually starts (avoids animation before audio)
  const lipSyncDataRef = useRef(lipSyncData);
  lipSyncDataRef.current = lipSyncData;
  // Store lip sync chunks by index so we merge in order regardless of arrival order (backend may send 3,2,1,0)
  const waiverLipSyncChunksRef = useRef<Record<number, { start: number; end: number; value: string }[]>>({});
  const startLipSyncChunksRef = useRef<Record<number, { start: number; end: number; value: string }[]>>({});
  const rundownLipSyncChunksRef = useRef<Record<number, { start: number; end: number; value: string }[]>>({});
  const rundownLipsyncReceiptCountRef = useRef(0);
  const DraftAdviceLipSyncChunksRef = useRef<Record<number, { start: number; end: number; value: string }[]>>({});
  const DraftAdviceLipsyncReceiptCountRef = useRef(0);

  const summaryAudioRefs = useRef({
    rundown: null as HTMLAudioElement | null,
    DraftAdvice: null as HTMLAudioElement | null,
    waiver: null as HTMLAudioElement | null,
    start: null as HTMLAudioElement | null,
  });

  const hasAudioBeenPlayed = useRef({
    rundown: false,
    DraftAdvice: false,
    waiver: false,
    start: false,
  });

  // When switching from PCM to WAV, seek WAV to PCM position so audio does not restart from 0
  const rundownPcmStartTimeRef = useRef<number | null>(null);
  const rundownSeekTimeRef = useRef<number | null>(null);
  const DraftAdvicePcmStartTimeRef = useRef<number | null>(null);
  const DraftAdviceSeekTimeRef = useRef<number | null>(null);
  // Blocks audio/lipsync while LoaderFacts overlay is showing during a forced refresh (footer button)
  const draftAdviceAudioBlockedForForcedRefreshRef = useRef(false);
  const waiverPcmStartTimeRef = useRef<number | null>(null);
  const waiverSeekTimeRef = useRef<number | null>(null);
  const startPcmStartTimeRef = useRef<number | null>(null);
  const startSeekTimeRef = useRef<number | null>(null);

  // State to store the progress of the typewriter effect for each stream
  const [streamProgress, setStreamProgress] = useState({
    waiver: "",
    start: "",
    rundown: "",
    DraftAdvice: "",
  });
  // NEW: Generic manual progress targets for stream buttons (0-100)
  const [streamProgressTargets, setStreamProgressTargets] = useState({
    waiver: 0,
    start: 0,
    rundown: 0,
    DraftAdvice: 0,
  });

  // State to control the platform change
  const [isChangingPlatform, setIsChangingPlatform] = useState(false);

  //get yahoo_token from url params of the callback setted in yahoo web
  // const { yahoo_token } = useParams();
  //get access token from Auth0
  const { isAuthenticated: auth0IsAuthenticated, user: auth0User } = useAuth0();
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();

  // Use global state from WidgetContext to determine which authId to use
  const isAuthenticated = isLoggedInAuth0ExperienceGlobal ? auth0IsAuthenticated : true;
  const user = isLoggedInAuth0ExperienceGlobal ? auth0User : null;

  // Use push notifications hook to request push permission on iOS
  usePushNotifications(getSafeAccessAuth0Token, auth0IsAuthenticated);

  // Show welcome drawer once for brand-new non-logged users (backend newNonLoggedUser flag)
  useEffect(() => {
    const checkNewNonLoggedUserStatus = async () => {
      // E2E: full-height slides block SpeedDial; skip only in Dockerfile.e2e builds
      if (process.env.REACT_APP_E2E_AUTH_BYPASS === "true") return;
      if (isLoggedInAuth0ExperienceGlobal || !isAuthenticated || !pocToken || isLoadingPreferences) return;

      try {
        const accessAuthToken = await getSafeAccessAuth0Token();
        if (!accessAuthToken) return;

        const { newNonLoggedUser } = await getNewNonLoggedUserStatus(accessAuthToken);
        if (newNonLoggedUser) {
          setIsWelcomeDrawerOpen(true);
        }
      } catch (error) {
        console.error("Error fetching new non-logged user status:", error);
      }
    };

    checkNewNonLoggedUserStatus();
  }, [isLoggedInAuth0ExperienceGlobal, isAuthenticated, pocToken, isLoadingPreferences, getSafeAccessAuth0Token]);

  const handleWelcomeDismiss = async () => {
    setIsWelcomeDrawerOpen(false);

    if (isLoggedInAuth0ExperienceGlobal) return;

    try {
      const accessAuthToken = await getSafeAccessAuth0Token();
      if (!accessAuthToken) return;
      await dismissNewNonLoggedUser(accessAuthToken);
    } catch (error) {
      console.error("Error dismissing new user onboarding:", error);
    }
  };


  // Function to fetch allowed buttons/sections from backend
  const fetchAllowedButtonsSections = async () => {
    const accessAuthToken = await getSafeAccessAuth0Token();
    if (!accessAuthToken) return;
    const buttonsSections = await getAllowedButtonsSections(accessAuthToken, pocToken);
    setAllowedButtonsSections(buttonsSections);
  };

  const selectedPlatformId = leaguePlatforms.find((p) => p.isSelected)?.id ?? "";

  // Use effect to fetch allowed buttons/sections from backend when user is authenticated and changes league/team
  useEffect(() => {
    // console.log("allowedButtonsSections", allowedButtonsSections);
    fetchAllowedButtonsSections();
    // console.log("selectedPlatformId", selectedPlatformId);
  }, [isAuthenticated, getSafeAccessAuth0Token, selectedTeamInfo, selectedPlatformId,]);


  const fetchCredits = async (accessAuthTokenParam?: string) => {
    if (!isAuthenticated) return;

    try {
      const accessAuthToken = accessAuthTokenParam ?? await getSafeAccessAuth0Token();
      if (!accessAuthToken) return;

      const creditsData = await getCredits(accessAuthToken);
      setCredits(creditsData.credits);
      setFreeCredits(creditsData.freeCredits);
      setNextFreeCreditsResetTime(creditsData.nextFreeCreditsResetTime || null);
      setUserSubscription(creditsData.subscription); // Store subscription for Super Analysis model selection
    } catch (error) {
      console.error("Error fetching credits:", error);
      // Keep default value if fetch fails
    }
  };
  // Fetch credits from backend when user is authenticated.
  // On native platforms, claim app bonus first and then read credits to avoid transient UI states.
  useEffect(() => {
    const loadCreditsAndNativeBonus = async () => {
      if (!isAuthenticated) return;

      try {
        const accessAuthToken = await getSafeAccessAuth0Token();
        if (!accessAuthToken) return;

        if (Capacitor.isNativePlatform()) {
          const result = await claimAppDownloadBonus(accessAuthToken);
          if (result.granted) {
            console.log(`🎁 HomePage: App download bonus granted! New freeCredits: ${result.freeCredits}`);
          }
        }

        await fetchCredits(accessAuthToken);
      } catch (error) {
        console.error("Error loading credits/native bonus:", error);
        // Keep default value if fetch fails
      }
    };

    loadCreditsAndNativeBonus();
  }, [isAuthenticated, getSafeAccessAuth0Token, selectedTeamInfo]);

  // Function to update credits after successful endpoint execution
  const updateCreditsAfterEndpoint = async (featureUsed = "endpoint") => {
    try {
      // Small delay to ensure MongoDB write is fully committed before reading
      // await new Promise((resolve) => setTimeout(resolve, 200));

      const accessAuthToken = await getSafeAccessAuth0Token();
      if (accessAuthToken) {
        const prevPaid = credits ?? 0;
        const prevFree = freeCredits ?? 0;
        const creditsData = await getCredits(accessAuthToken);
        setCredits(creditsData.credits);
        setFreeCredits(creditsData.freeCredits);
        setNextFreeCreditsResetTime(creditsData.nextFreeCreditsResetTime || null);
        setUserSubscription(creditsData.subscription); // Update subscription when credits are updated
        const paidDelta = prevPaid - (creditsData.credits ?? 0);
        const freeDelta = prevFree - (creditsData.freeCredits ?? 0);
        if (paidDelta > 0 || freeDelta > 0) {
          const usedPaid = paidDelta > 0;
          void trackCreditConsumed(
            (isLoggedInAuth0ExperienceGlobal ? user?.sub : NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID) || "unknown",
            usedPaid ? (creditsData.credits ?? 0) : (creditsData.freeCredits ?? 0),
            !!isLoggedInAuth0ExperienceGlobal,
            featureUsed,
            usedPaid ? "credits" : "free",
          );
        }
      }
    } catch (error) {
      console.error("Error updating credits after endpoint:", error);
    }
  };

  // State to track credits animation
  const [isCreditsAnimating, setIsCreditsAnimating] = useState(false);
  const prevCreditsRef = useRef<number | undefined>(undefined);
  const quanAutoDisabledRef = useRef(false);

  // Helper function to check if data is valid for display (similar to SectionHomePage)
  const isDataValidForDisplay = (data: any) => {
    if (!data || !data.context) {
      return false;
    }
    // Use global state to determine which authId to use
    const authId = isLoggedInAuth0ExperienceGlobal ? user?.sub : NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID;
    return data.context.platformId === selectedTeamInfo?.platformId && data.context.leagueId === selectedTeamInfo?.leagueId && data.context.userId === authId;
  };

  // Helper function to check if user has any valid cached data for premium endpoints
  const hasAnyValidCachedData = () => {
    return isDataValidForDisplay(waiverData) || isDataValidForDisplay(rundownData) || isDataValidForDisplay(startData) || isDataValidForDisplay(DraftAdviceData);
  };

  // CRITICAL: Effect ONLY for credits animation - triggers ONLY when credits value actually changes
  useEffect(() => {
    // Only trigger animation if credits actually changed (skip on initial mount)
    if (prevCreditsRef.current !== undefined && prevCreditsRef.current !== credits) {
      setIsCreditsAnimating(true);
      // Reset animation after 400ms
      const timer = setTimeout(() => {
        setIsCreditsAnimating(false);
      }, 400);

      return () => clearTimeout(timer);
    }
    // Update prevCreditsRef AFTER checking for changes
    prevCreditsRef.current = credits;
  }, [credits, freeCredits]); // CRITICAL: Only depend on credits, nothing else

  // Allow timeout fallback to run again if user enables Quan manually later.
  useEffect(() => {
    if (aiSettings?.quan) {
      quanAutoDisabledRef.current = false;
    }
  }, [aiSettings?.quan]);

  // Auto-disable Quan when 3D model never finishes loading on slow/unstable networks.
  const handleQuanLoadTimeout = useCallback(() => {
    if (!aiSettings?.quan || quanAutoDisabledRef.current) return;

    quanAutoDisabledRef.current = true;
    const nextSettings = { ...aiSettings, quan: false };
    setAiSettings(nextSettings);
    void forceSavePreferences({ aiSettings: nextSettings }).catch((error) => {
      console.error("Failed to persist Quan auto-disable after load timeout", error);
    });
  }, [aiSettings, setAiSettings, forceSavePreferences]);

  // Auto-disable Quan when CharacterEmbed2 reports a definitive load failure.
  const handleQuanLoadError = useCallback((error: Error) => {
    console.error("Character load failed. Auto-disabling Quan", error);
    if (!aiSettings?.quan || quanAutoDisabledRef.current) return;

    quanAutoDisabledRef.current = true;
    const nextSettings = { ...aiSettings, quan: false };
    setAiSettings(nextSettings);
    void forceSavePreferences({ aiSettings: nextSettings }).catch((persistError) => {
      console.error("Failed to persist Quan auto-disable after load error", persistError);
    });
  }, [aiSettings, setAiSettings, forceSavePreferences]);

  // When credits drop below Auto-Process minimum, turn off autoProcessAI so we don't run 4 endpoints or open drawer on team select
  const autoProcessMinCredits = frontEnvNumber("REACT_APP_AUTO_PROCESS_MIN_CREDITS", 40);
  useEffect(() => {
    // Wait until the actual credits have been fetched from the server.
    // The initial state of credits is null, while freeCredits is initialized to 5.
    // If we don't wait for credits to load, this effect will wrongly force autoProcessAI to false based on the default freeCredits.
    if (credits === null) return;

    const isFree = (userSubscription || "free").toLowerCase() === "free"; // check if user is free subscription
    const available = isFree ? (freeCredits ?? 0) : (credits ?? 0); // check if user has enough credits to run auto process
    // check if auto process should be off for this user
    // const shouldBeOff =
    //   available < AUTO_PROCESS_MIN_CREDITS ||
    //   isFree ||
    //   !isLoggedInAuth0ExperienceGlobal;
    const shouldBeOff =
      available < autoProcessMinCredits
    // ||
    // isFree ||
    // !isLoggedInAuth0ExperienceGlobal;
    // turn off auto process if shouldBeOff is true and auto process is on
    if (shouldBeOff && aiSettings?.autoProcessAI) {
      const next = { ...aiSettings, autoProcessAI: false };
      setAiSettings(next);
      void forceSavePreferences({ aiSettings: next });
    }
  }, [credits, freeCredits, userSubscription, aiSettings?.autoProcessAI, autoProcessMinCredits]);

  // Separate effect for paywall logic (runs when credits or other relevant dependencies change)
  useEffect(() => {
    // Open membership drawer when credits reach 0
    // DEV: Comment ENABLE_CREDIT_VALIDATION constant above to disable this behavior
    // Only open paywall if user has less than 9 credits (for the new x10 credits flow)  AND no valid cached data
    if (ENABLE_CREDIT_VALIDATION && credits < 9 && prevCreditsRef.current !== undefined && prevCreditsRef.current > 0 && !isPaymentOpen && !hasAnyValidCachedData()) {
      setInitialPaymentView("manageMembership");
      setIsPaymentOpen(true);
    }
  }, [credits, waiverData, rundownData, startData, DraftAdviceData, user, ENABLE_CREDIT_VALIDATION]);

  const loadSeasonPassPrice = async () => {
    try {
      const price = await getSeasonPassPrice();
      setSeasonPassPrice(price);
      console.log("HOMEPAGE: Loaded seasonPassPrice from backend:", price);
    } catch (error) {
      console.error("Error loading season pass price:", error);
      // Keep default value "1" if fetch fails
    }
  };
  // useEffect to load season pass price from backend on app start
  useEffect(() => {
    // Load season pass price immediately when component mounts (no auth required)
    loadSeasonPassPrice();
  }, []);

  // State to store if common league data failed
  const [commonLeagueDataFailed, setCommonLeagueDataFailed] = useState(false);

  // State to store estimated times from backend
  const [estimatedTimes, setEstimatedTimes] = useState<EstimatedTimesResponse["estimatedTime"] | null>(null);

  // State to store errors for each endpoint of the 6 sections for animatedbutton
  const [endpointErrors, setEndpointErrors] = useState({});

  //state to store error messages for EndpointErrorsMessage.tsx
  const [errorMessages, setErrorMessages] = useState({
    "League Data": "",
    Trades: "",
    "Analyze Trade": "",
    Rundown: "",
    DraftAdvice: "",
    Start: "",
    News: "",
    Rankings: "",
    AllRankings: "",
    Waiver: "",
  });

  //state to know if 1 endpoint section failed for the message "Rosters are empty.."
  const [errorMessageNoDraft, setErrorMessageNoDraft] = useState({
    waiver: false,
    news: false,
    start: false,
    rankings: false,
    allRankings: false,
    rosters: false,
    rundown: false,
    DraftAdvice: false,
  });

  const [yahooFinish, setYahooFinish] = useState(false);
  const [returnedFromBackground, setReturnedFromBackground] = useState(false);
  const returnedFromBackgroundRef = useRef(false);

  const [forceRefresh, setForceRefresh] = useState(false);
  const [refreshingLeagueData, setRefreshingLeagueData] = useState(false);

  // State for calculator_settings is now managed in WidgetContext (calculatorSettingsMap) to persist across sessions
  // This payload will be sent to endpoints when user saves league settings changes
  // const [calculatorSettings, setCalculatorSettings] = useState<any>(null); // Removed local state

  // Flag to indicate that league settings have changed and need to trigger a refresh
  const [leagueSettingsChanged, setLeagueSettingsChanged] = useState(false);

  // Derived calculatorSettings for the current team based on the map from WidgetContext
  const currentTeamKey = selectedTeamInfo ? `${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}` : null;
  const calculatorSettings = currentTeamKey ? calculatorSettingsMap[currentTeamKey] : null;

  // Wrapper for setCalculatorSettings to update the map in WidgetContext
  const setCalculatorSettings = (newSettings: any) => {
    if (!currentTeamKey) return;
    setCalculatorSettingsMap((prev) => {
      const next = { ...prev };
      if (newSettings === null) {
        delete next[currentTeamKey];
      } else {
        next[currentTeamKey] = newSettings;
      }
      console.log("🔍 HOMEPAGE: Updated calculatorSettingsMap:", JSON.stringify(next, null, 2));

      // Log notes values from all teams in calculatorSettingsMap
      const notesValues = Object.entries(next).reduce((acc, [teamKey, settings]) => {
        if (settings && typeof settings === "object" && "notes" in settings) {
          acc[teamKey] = settings.notes || "(empty)";
        }
        return acc;
      }, {} as Record<string, string>);
      // console.log("📝 HOMEPAGE: Notes from all teams:", notesValues);

      // Log notes value for the currently selected team
      const currentTeamNotes =
        currentTeamKey && next[currentTeamKey] && typeof next[currentTeamKey] === "object" && "notes" in next[currentTeamKey]
          ? next[currentTeamKey].notes || "(empty)"
          : "(no settings for this team)";
      // console.log(`📌 HOMEPAGE: Notes for current team (${currentTeamKey}):`, currentTeamNotes);

      return next;
    });
  };

  // Ref to store the teamsHomePageRef
  const teamsHomePageRef = useRef<TeamsHomePageRef>(null);
  const sectionHomePageRef = useRef<any>(null); // change this any juan!
  // const characterRef = useRef<CharacterBubbleRef>(null);
  const characterRef = useRef<CharacterBubble2Ref>(null);

  //extract authId from user auth0 data for cache key
  // Use global state to determine which authId to use
  const authId = isLoggedInAuth0ExperienceGlobal ? user?.sub : NON_LOGGED_IN_CONFIG.NON_LOGGED_IN_AUTH_ID;
  // console.log("🔄 authId in HomePage:", authId);

  useEffect(() => {
    if (!authId) return;
    const isAuth = !!isLoggedInAuth0ExperienceGlobal;
    const isPaid = (userSubscription || "free").toLowerCase() !== "free";
    const remaining = isPaid ? (credits ?? 0) : (freeCredits ?? 0);
    void setAnalyticsUser(authId, {
      user_tier: !isAuth ? "anonymous" : isPaid ? "paid_subscriber" : "registered_free",
      has_used_free_credit: sessionStorage.getItem("analytics_had_used_free_credit") === "true",
      credits_remaining: remaining ?? 0,
    });
  }, [authId, credits, freeCredits, userSubscription, isLoggedInAuth0ExperienceGlobal]);

  // Effect to detect URL parameter for opening payment drawer (from Capacitor Browser)
  // Also checks localStorage for openPaymentAfterLogin flag (from Go Premium button for non-logged users)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const openPayment = urlParams.get("openPayment");
    const fromApp = urlParams.get("fromApp");

    // IMPORTANT: Save fromApp to sessionStorage BEFORE cleaning URL
    // This persists across navigation within Capacitor Browser
    if (fromApp === "true") {
      sessionStorage.setItem("fromApp", "true");
      // console.log("🔍 Saved fromApp=true to sessionStorage");
    }

    if (openPayment && !isPaymentOpen) {
      // console.log("🔍 [Go Premium] [HomePage] Detected openPayment parameter in URL:", openPayment);
      // Set the initial view based on the parameter
      if (openPayment === "manageMembership") {
        // console.log("🔍 [Go Premium] [HomePage] Opening payment drawer with manageMembership view (from URL)");
        setInitialPaymentView("manageMembership");
        setIsPaymentOpen(true);
        // Clean URL - remove query parameter after opening drawer
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } else if (openPayment === "cancelMembership") {
        // console.log("🔍 [Go Premium] [HomePage] Opening payment drawer with cancelMembership view (from URL)");
        setInitialPaymentView("cancelMembership");
        setIsPaymentOpen(true);
        // Clean URL - remove query parameter after opening drawer
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    } else {
      // Check localStorage for openPaymentAfterLogin flag (from Go Premium button for non-logged users)
      // This works for both Web and Capacitor
      const openPaymentAfterLogin = localStorage.getItem("openPaymentAfterLogin");
      if (openPaymentAfterLogin) {
        // console.log("🔍 [Go Premium] [HomePage] Found openPaymentAfterLogin in localStorage:", openPaymentAfterLogin);
        // Always remove flag after reading (cleanup)
        localStorage.removeItem("openPaymentAfterLogin");
        // console.log("💾 [Go Premium] [HomePage] Removed openPaymentAfterLogin flag from localStorage");

        // Only open drawer if not already open and flag is manageMembership
        if (openPaymentAfterLogin === "manageMembership" && !isPaymentOpen) {
          // console.log("🔍 [Go Premium] [HomePage] Opening payment drawer with manageMembership view (from localStorage)");
          setInitialPaymentView("manageMembership");
          // setSelectedPaymentMethod("manageMembership");
          setIsPaymentOpen(true);
        }
      }
    }
  }, []); // Run once on mount

  // Listen for Stripe payment completion event to update credits in HomePage
  // This ensures credits are updated even if PaymentMethodSwipeable is not mounted
  useEffect(() => {
    let pollingInterval: NodeJS.Timeout | null = null;
    let pollingTimeout: NodeJS.Timeout | null = null;
    let isPollingActive = false;

    const handlePaymentComplete = async (event: CustomEvent) => {
      console.log("🔔 HomePage: Received stripePaymentComplete event:", event.detail);

      const { success, productId } = event.detail;

      // If payment was successful, start intelligent polling to detect credit changes
      if (success) {
        console.log("✅ HomePage: Payment successful, starting intelligent polling for credit updates...");

        // Store current credits value before polling starts
        const initialCredits = credits;
        console.log(`📊 HomePage: Initial credits value: ${initialCredits}`);
        // Also store current subscription to detect membership upgrades/downgrades without relying on credit delta
        const initialSubscription = userSubscription;
        console.log(`📊 HomePage: Initial subscription value: ${initialSubscription}`);

        // Clear any existing polling
        if (pollingInterval) {
          clearInterval(pollingInterval);
          pollingInterval = null;
        }
        if (pollingTimeout) {
          clearTimeout(pollingTimeout);
          pollingTimeout = null;
        }

        isPollingActive = true;

        // Poll every 2 seconds to check for credit updates
        pollingInterval = setInterval(async () => {
          if (!isPollingActive) {
            if (pollingInterval) clearInterval(pollingInterval);
            return;
          }

          try {
            const accessToken = await getSafeAccessAuth0Token();
            if (accessToken) {
              const creditsResponse = await getCredits(accessToken);
              const newCredits = creditsResponse.credits;
              const newSubscription = creditsResponse.subscription;
              console.log(
                `🔄 HomePage: Polling - Current credits in MongoDB: ${newCredits}, UI credits: ${initialCredits}, subscription: ${newSubscription} (was ${initialSubscription})`
              );

              const creditsIncreased = newCredits > initialCredits;
              const subscriptionChanged = newSubscription !== initialSubscription;

              // If either credits increased OR subscription changed (MongoDB was updated), update UI and stop polling
              if (creditsIncreased || subscriptionChanged) {
                console.log(
                  `✅ HomePage: MongoDB updated! credits: ${initialCredits} → ${newCredits} (increased: ${creditsIncreased}), subscription: ${initialSubscription} → ${newSubscription} (changed: ${subscriptionChanged}). Updating UI...`
                );
                setCredits(newCredits);
                setFreeCredits(creditsResponse.freeCredits);
                setNextFreeCreditsResetTime(creditsResponse.nextFreeCreditsResetTime || null);
                setUserSubscription(newSubscription);
                isPollingActive = false;
                if (pollingInterval) {
                  clearInterval(pollingInterval);
                  pollingInterval = null;
                }
                if (pollingTimeout) {
                  clearTimeout(pollingTimeout);
                  pollingTimeout = null;
                }
                console.log("✅ HomePage: Polling stopped - credits successfully updated");
              }
            } else {
              console.warn("⚠️ HomePage: No access token available during polling");
            }
          } catch (error) {
            console.error("❌ HomePage: Error during polling:", error);
          }
        }, 2000); // Poll every 2 seconds

        // Stop polling after 60 seconds maximum (safety timeout)
        pollingTimeout = setTimeout(() => {
          if (isPollingActive) {
            console.log("⏰ HomePage: Polling timeout reached (60s), stopping...");
            isPollingActive = false;
            if (pollingInterval) {
              clearInterval(pollingInterval);
              pollingInterval = null;
            }
          }
        }, 60000);
      }
    };

    // Add event listener
    window.addEventListener("stripePaymentComplete", handlePaymentComplete as EventListener);
    console.log("✅ HomePage: stripePaymentComplete listener registered");

    // Cleanup
    return () => {
      window.removeEventListener("stripePaymentComplete", handlePaymentComplete as EventListener);
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
      isPollingActive = false;
      console.log("🧹 HomePage: stripePaymentComplete listener removed and polling cleaned up");
    };
  }, [getSafeAccessAuth0Token, setCredits, setUserSubscription, credits, userSubscription]);

  // Listen for FREE plan activation event to update credits and subscription in HomePage
  // This ensures credits/subscription are updated even if PaymentMethodSwipeable is not mounted
  useEffect(() => {
    const handleFreePlanActivated = async (event: CustomEvent) => {
      console.log("🔔 HomePage: Received freePlanActivated event:", event.detail);

      const { success } = event.detail;

      // If FREE plan activation was successful, fetch updated credits and subscription
      if (success) {
        console.log("✅ HomePage: FREE plan activated, fetching updated credits and subscription...");
        try {
          // Wait for backend to process
          await new Promise((resolve) => setTimeout(resolve, 1000));

          const accessToken = await getSafeAccessAuth0Token();
          if (accessToken) {
            const creditsResponse = await getCredits(accessToken);
            console.log("✅ HomePage: Credits and subscription fetched from backend:", {
              credits: creditsResponse.credits,
              freeCredits: creditsResponse.freeCredits,
              subscription: creditsResponse.subscription,
            });
            setCredits(creditsResponse.credits);
            setFreeCredits(creditsResponse.freeCredits);
            setNextFreeCreditsResetTime(creditsResponse.nextFreeCreditsResetTime || null);
            setUserSubscription(creditsResponse.subscription);
          }
        } catch (error) {
          console.error("❌ HomePage: Failed to fetch updated credits/subscription:", error);
        }
      }
    };

    // Add event listener
    window.addEventListener("freePlanActivated", handleFreePlanActivated as EventListener);
    console.log("✅ HomePage: freePlanActivated listener registered");

    // Cleanup
    return () => {
      window.removeEventListener("freePlanActivated", handleFreePlanActivated as EventListener);
      console.log("🧹 HomePage: freePlanActivated listener removed");
    };
  }, [getSafeAccessAuth0Token, setCredits, setUserSubscription]);

  // Ref to store the dataLoadedRef
  const dataLoadedRef = useRef(false);

  const activeStreamRequests = useRef(new Set<string>());

  // Opens the rate limit drawer and sets retryAt to now + window seconds (so displayed time has real seconds, not always :00)
  const openRateLimitDrawer = (retryAfterSeconds?: number) => {
    const windowSeconds = retryAfterSeconds ?? 60;
    const retryAt = Date.now() + windowSeconds * 1000;
    setRateLimitRetryAt(retryAt);
    setIsRateLimitDrawerOpen(true);
  };

  // This effect handles the GLOBAL MUTE functionality for all summary audios.
  useEffect(() => {
    const handleMuteChange = (isMuted: boolean) => {
      Object.values(summaryAudioRefs.current).forEach((audioEl) => {
        if (audioEl) {
          audioEl.muted = isMuted;
        }
      });
    };
    const unsubscribe = audioService.subscribe(handleMuteChange);
    return unsubscribe; // Cleanup on unmount
  }, []);

  // Prefetch Vega for cache URLs (no playback). Open-drawer play stays sync.
  useEffect(() => {
    prefetchVegaFromAudioUrl(summaryAudios.rundown);
    prefetchVegaFromAudioUrl(summaryAudios.DraftAdvice);
    prefetchVegaFromAudioUrl(summaryAudios.waiver);
    prefetchVegaFromAudioUrl(summaryAudios.start);
  }, [summaryAudios.rundown, summaryAudios.DraftAdvice, summaryAudios.waiver, summaryAudios.start]);

  // --- Autoplay useEffect for RUNDOWN ---
  useEffect(() => {
    // Guard clauses: Do nothing if...
    if (
      !summaryAudios.rundown ||
      !isDrawerOpen ||
      selectedButton !== "rundown" ||
      hasAudioBeenPlayed.current.rundown ||
      rundownPcmPlayerRef.current // Do not auto-play URL if live PCM player is active
    ) {
      return;
    }

    const newAudio = new Audio(summaryAudios.rundown);
    newAudio.setAttribute("playsinline", "true");
    summaryAudioRefs.current.rundown = newAudio;

    const seekTime = rundownSeekTimeRef.current;
    if (seekTime != null) rundownSeekTimeRef.current = null;

    let stopVegaUpdates = () => { };
    let cancelled = false;

    const runSetup = (audioEl: HTMLAudioElement, usedVega: boolean) => {
      if (characterRef?.current) {
        console.log(`[Rundown Audio] Setting up lip sync with ${lipSyncData.rundown?.mouthCues?.length || 0} cues`);
        characterRef.current.setAudioElement(audioEl);
        characterRef.current.setIsTalking(true);
        if (!usedVega) applyLegacyLipSyncOrSkip(characterRef.current, lipSyncData.rundown, "rundown-url");
      }
      hasAudioBeenPlayed.current.rundown = true;
      console.log(`[Rundown Audio] Audio started - currentTime: ${audioEl.currentTime}`);
      audioEl.onended = () => {
        console.log('[Rundown Audio] Audio ended naturally');
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      };
      audioEl.addEventListener('play', () => console.log('[Rundown Audio] Audio play event fired'));
      audioEl.addEventListener('pause', () => console.log(`[Rundown Audio] Audio pause event fired - currentTime: ${audioEl.currentTime}`));
    };

    void (async () => {
      // Capacitor: start when the first ~2s of visemes exist (same moment as audio). Rest of the track fills in.
      if (isCapacitorNative()) {
        const headReady = await prepareVegaHeadFromAudioUrl(summaryAudios.rundown);
        if (cancelled || !headReady || !characterRef?.current) return;
        tryApplyVegaFromAudioUrlSync(summaryAudios.rundown, characterRef.current, "rundown-url");
        characterRef.current.setAudioElement(newAudio);
        const playedNow = audioService.playAudio(newAudio, AudioPriority.HIGH);
        runSetup(audioService.getNowPlayingAudio() ?? newAudio, true);
        if (!playedNow) characterRef.current.setIsTalking(false);
        stopVegaUpdates = subscribeVegaUrlUpdates(summaryAudios.rundown, (done) => {
          if (cancelled || !characterRef?.current) {
            stopVegaUpdates();
            return;
          }
          tryApplyVegaFromAudioUrlSync(summaryAudios.rundown, characterRef.current, "rundown-url");
          if (done) stopVegaUpdates();
        });
        return;
      }
      const usedVega = await prepareThenApplyVegaFromAudioUrl(
        summaryAudios.rundown,
        characterRef?.current,
        "rundown-url",
        () => {
          audioService.stopCurrentAudio();
          characterRef?.current?.setIsTalking(false);
        },
      );
      if (cancelled) return;

      if (seekTime != null && seekTime > 0) {
        const onCanPlay = () => {
          if (cancelled) return;
          newAudio.currentTime = Math.min(seekTime, newAudio.duration || seekTime);
          if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
          const played = audioService.playAudio(newAudio, AudioPriority.HIGH);
          const actual = audioService.getNowPlayingAudio() ?? newAudio;
          runSetup(actual, usedVega);
          if (!played && characterRef?.current) characterRef.current.setIsTalking(false);
        };
        newAudio.addEventListener('canplay', onCanPlay, { once: true });
      } else {
        if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
        const playedSuccessfully = audioService.playAudio(newAudio, AudioPriority.HIGH);
        const actualPlayingAudio = audioService.getNowPlayingAudio() ?? newAudio;
        runSetup(actualPlayingAudio, usedVega);
        if (!playedSuccessfully && characterRef?.current) characterRef.current.setIsTalking(false);
      }
    })();

    return () => {
      cancelled = true;
      stopVegaUpdates();
      newAudio.volume = 0;
      newAudio.pause();
      audioService.stopCurrentAudio();
    };
  }, [summaryAudios.rundown, selectedButton, isDrawerOpen]);

  // --- Autoplay useEffect for DRAFT ADVICE ---
  useEffect(() => {
    // Guard clauses: Do nothing if...
    if (
      !summaryAudios.DraftAdvice ||
      !isDrawerOpen ||
      selectedButton !== "DraftAdvice" ||
      draftAdviceAudioBlockedForForcedRefreshRef.current || // block while LoaderFacts is showing (forced refresh)
      hasAudioBeenPlayed.current.DraftAdvice ||
      DraftAdvicePcmPlayerRef.current // Do not auto-play URL if live PCM player is active
    ) {
      return;
    }

    const newAudio = new Audio(summaryAudios.DraftAdvice);
    newAudio.setAttribute("playsinline", "true");
    summaryAudioRefs.current.DraftAdvice = newAudio;

    const seekTime = DraftAdviceSeekTimeRef.current;
    if (seekTime != null) DraftAdviceSeekTimeRef.current = null;

    let stopVegaUpdates = () => { };
    let cancelled = false;

    const runSetup = (audioEl: HTMLAudioElement, usedVega: boolean) => {
      if (characterRef?.current) {
        console.log(`[Draft Advice Audio] Setting up lip sync with ${lipSyncData.DraftAdvice?.mouthCues?.length || 0} cues`);
        characterRef.current.setAudioElement(audioEl);
        characterRef.current.setIsTalking(true);
        if (!usedVega) applyLegacyLipSyncOrSkip(characterRef.current, lipSyncData.DraftAdvice, "DraftAdvice-url");
      }
      hasAudioBeenPlayed.current.DraftAdvice = true;
      console.log(`[Draft Advice Audio] Audio started - currentTime: ${audioEl.currentTime}`);
      audioEl.onended = () => {
        console.log('[Draft Advice Audio] Audio ended naturally');
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      };
      audioEl.addEventListener('play', () => console.log('[Draft Advice Audio] Audio play event fired'));
      audioEl.addEventListener('pause', () => console.log(`[Draft Advice Audio] Audio pause event fired - currentTime: ${audioEl.currentTime}`));
    };

    void (async () => {
      // Capacitor: start when the first ~2s of visemes exist (same moment as audio). Rest of the track fills in.
      if (isCapacitorNative()) {
        const headReady = await prepareVegaHeadFromAudioUrl(summaryAudios.DraftAdvice);
        if (cancelled || !headReady || !characterRef?.current) return;
        tryApplyVegaFromAudioUrlSync(summaryAudios.DraftAdvice, characterRef.current, "DraftAdvice-url");
        characterRef.current.setAudioElement(newAudio);
        const playedNow = audioService.playAudio(newAudio, AudioPriority.HIGH);
        runSetup(audioService.getNowPlayingAudio() ?? newAudio, true);
        if (!playedNow) characterRef.current.setIsTalking(false);
        stopVegaUpdates = subscribeVegaUrlUpdates(summaryAudios.DraftAdvice, (done) => {
          if (cancelled || !characterRef?.current) {
            stopVegaUpdates();
            return;
          }
          tryApplyVegaFromAudioUrlSync(summaryAudios.DraftAdvice, characterRef.current, "DraftAdvice-url");
          if (done) stopVegaUpdates();
        });
        return;
      }
      const usedVega = await prepareThenApplyVegaFromAudioUrl(
        summaryAudios.DraftAdvice,
        characterRef?.current,
        "DraftAdvice-url",
        () => {
          audioService.stopCurrentAudio();
          characterRef?.current?.setIsTalking(false);
        },
      );
      if (cancelled) return;

      if (seekTime != null && seekTime > 0) {
        const onCanPlay = () => {
          if (cancelled) return;
          newAudio.currentTime = Math.min(seekTime, newAudio.duration || seekTime);
          if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
          const played = audioService.playAudio(newAudio, AudioPriority.HIGH);
          const actual = audioService.getNowPlayingAudio() ?? newAudio;
          runSetup(actual, usedVega);
          if (!played && characterRef?.current) characterRef.current.setIsTalking(false);
        };
        newAudio.addEventListener('canplay', onCanPlay, { once: true });
      } else {
        if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
        const playedSuccessfully = audioService.playAudio(newAudio, AudioPriority.HIGH);
        const actualPlayingAudio = audioService.getNowPlayingAudio() ?? newAudio;
        runSetup(actualPlayingAudio, usedVega);
        if (!playedSuccessfully && characterRef?.current) characterRef.current.setIsTalking(false);
      }
    })();

    return () => {
      cancelled = true;
      stopVegaUpdates();
      newAudio.volume = 0;
      newAudio.pause();
      audioService.stopCurrentAudio();
    };
  }, [summaryAudios.DraftAdvice, selectedButton, isDrawerOpen, loadingDraftAdvice]);

  // Track previous state to detect real transitions (not initial mount)
  const prevDrawerStateRef = useRef(isDrawerOpen);
  const prevSelectedButtonRef = useRef<string | null>(null);
  const isInitialMountRef = useRef(true);

  // Stop audio ONLY when drawer closes or section changes (not on StrictMode cleanup or initial mount)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevDrawerStateRef.current = isDrawerOpen;
      prevSelectedButtonRef.current = selectedButton;
      return;
    }

    const prevDrawer = prevDrawerStateRef.current;
    const prevButton = prevSelectedButtonRef.current;

    // Only act on real transitions
    const drawerJustClosed = prevDrawer && !isDrawerOpen;
    const sectionJustChanged = prevDrawer && isDrawerOpen && prevButton !== null && prevButton !== selectedButton &&
      ["rundown", "DraftAdvice", "waiver", "start"].includes(prevButton) &&
      ["rundown", "DraftAdvice", "waiver", "start"].includes(selectedButton);

    if (drawerJustClosed || sectionJustChanged) {
      // Drawer stays mounted (keepMounted). Always stop URL + PCM so closing the panel actually silences Vega.
      console.log(`[Audio Stop] Stopping audio - drawerJustClosed: ${drawerJustClosed}, sectionJustChanged: ${sectionJustChanged}, wasPlaying: ${prevButton}, newButton: ${selectedButton}`);
      audioService.stopCurrentAudio();
      Object.values(summaryAudioRefs.current).forEach((audioEl) => {
        if (!audioEl) return;
        audioEl.volume = 0;
        audioEl.pause();
      });
      if (characterRef?.current) {
        characterRef.current.setIsTalking(false);
      }
      if (drawerJustClosed) {
        if (waiverPcmPlayerRef.current) {
          waiverPcmPlayerRef.current.stop();
          waiverPcmPlayerRef.current = null;
        }
        if (startPcmPlayerRef.current) {
          startPcmPlayerRef.current.stop();
          startPcmPlayerRef.current = null;
        }
        if (rundownPcmPlayerRef.current) {
          rundownPcmPlayerRef.current.stop();
          rundownPcmPlayerRef.current = null;
        }
        if (DraftAdvicePcmPlayerRef.current) {
          DraftAdvicePcmPlayerRef.current.stop();
          DraftAdvicePcmPlayerRef.current = null;
        }
      }
    }

    // Always update refs for next comparison
    prevDrawerStateRef.current = isDrawerOpen;
    prevSelectedButtonRef.current = selectedButton;
  }, [isDrawerOpen, selectedButton]);

  // Apply lip sync data when it arrives AFTER audio has already started (e.g. chunk 6 arrives after audio_summary).
  // Defer to next frame so we don't run in the same tick as setState/commit (avoids audio pause).
  useEffect(() => {
    if (!isDrawerOpen || selectedButton !== "rundown" || !lipSyncData.rundown || !characterRef?.current) return;

    const nowPlayingAudio = audioService.getNowPlayingAudio();
    if (!nowPlayingAudio) return;

    const expectedSrc = summaryAudios.rundown;
    if (nowPlayingAudio.src !== expectedSrc) return;

    const data = lipSyncData.rundown;
    const wasPlaying = !nowPlayingAudio.paused;
    const raf = requestAnimationFrame(() => {
      if (!characterRef?.current) return;
      const rundownCuesCount = data.mouthCues?.length || 0;
      console.log(`[Rundown Audio] Applying late-arriving lip sync data: ${rundownCuesCount} cues (lipsync only, deferred)`);
      applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
      if (wasPlaying) characterRef.current.setIsTalking(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [lipSyncData.rundown, isDrawerOpen, selectedButton, summaryAudios.rundown]);

  // Apply lip sync data when it arrives AFTER audio has already started (e.g. chunk 6 arrives after audio_summary).
  // Defer to next frame so we don't run in the same tick as setState/commit (avoids audio pause).
  useEffect(() => {
    if (!isDrawerOpen || selectedButton !== "DraftAdvice" || !lipSyncData.DraftAdvice || !characterRef?.current) return;

    const nowPlayingAudio = audioService.getNowPlayingAudio();
    if (!nowPlayingAudio) return;

    const expectedSrc = summaryAudios.DraftAdvice;
    if (nowPlayingAudio.src !== expectedSrc) return;

    const data = lipSyncData.DraftAdvice;
    const wasPlaying = !nowPlayingAudio.paused;
    const raf = requestAnimationFrame(() => {
      if (!characterRef?.current) return;
      const DraftAdviceCuesCount = data.mouthCues?.length || 0;
      console.log(`[Draft Advice Audio] Applying late-arriving lip sync data: ${DraftAdviceCuesCount} cues (lipsync only, deferred)`);
      applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
      if (wasPlaying) characterRef.current.setIsTalking(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [lipSyncData.DraftAdvice, isDrawerOpen, selectedButton, summaryAudios.DraftAdvice]);

  // --- Autoplay useEffect for WAIVER ---
  useEffect(() => {
    if (
      !summaryAudios.waiver ||
      !isDrawerOpen ||
      selectedButton !== "waiver" ||
      hasAudioBeenPlayed.current.waiver ||
      waiverPcmPlayerRef.current
    ) {
      return;
    }
    const newAudio = new Audio(summaryAudios.waiver);
    newAudio.setAttribute("playsinline", "true");
    summaryAudioRefs.current.waiver = newAudio;

    const seekTime = waiverSeekTimeRef.current;
    if (seekTime != null) waiverSeekTimeRef.current = null;

    let stopVegaUpdates = () => { };
    let cancelled = false;

    const runSetup = (audioEl: HTMLAudioElement, usedVega: boolean) => {
      if (characterRef?.current) {
        console.log(`[Waiver Audio] Setting up lip sync with ${lipSyncData.waiver?.mouthCues?.length || 0} cues`);
        characterRef.current.setAudioElement(audioEl);
        characterRef.current.setIsTalking(true);
        if (!usedVega) applyLegacyLipSyncOrSkip(characterRef.current, lipSyncData.waiver, "waiver-url");
      }
      hasAudioBeenPlayed.current.waiver = true;
      console.log(`[Waiver Audio] Audio started - currentTime: ${audioEl.currentTime}`);
      audioEl.onended = () => {
        console.log('[Waiver Audio] Audio ended naturally');
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      };
      audioEl.addEventListener('play', () => console.log('[Waiver Audio] Audio play event fired'));
      audioEl.addEventListener('pause', () => console.log(`[Waiver Audio] Audio pause event fired - currentTime: ${audioEl.currentTime}`));
    };

    void (async () => {
      // Capacitor: start when the first ~2s of visemes exist (same moment as audio). Rest of the track fills in.
      if (isCapacitorNative()) {
        const headReady = await prepareVegaHeadFromAudioUrl(summaryAudios.waiver);
        if (cancelled || !headReady || !characterRef?.current) return;
        tryApplyVegaFromAudioUrlSync(summaryAudios.waiver, characterRef.current, "waiver-url");
        characterRef.current.setAudioElement(newAudio);
        const playedNow = audioService.playAudio(newAudio, AudioPriority.HIGH);
        runSetup(audioService.getNowPlayingAudio() ?? newAudio, true);
        if (!playedNow) characterRef.current.setIsTalking(false);
        stopVegaUpdates = subscribeVegaUrlUpdates(summaryAudios.waiver, (done) => {
          if (cancelled || !characterRef?.current) {
            stopVegaUpdates();
            return;
          }
          tryApplyVegaFromAudioUrlSync(summaryAudios.waiver, characterRef.current, "waiver-url");
          if (done) stopVegaUpdates();
        });
        return;
      }
      const usedVega = await prepareThenApplyVegaFromAudioUrl(
        summaryAudios.waiver,
        characterRef?.current,
        "waiver-url",
        () => {
          audioService.stopCurrentAudio();
          characterRef?.current?.setIsTalking(false);
        },
      );
      if (cancelled) return;

      if (seekTime != null && seekTime > 0) {
        const onCanPlay = () => {
          if (cancelled) return;
          newAudio.currentTime = Math.min(seekTime, newAudio.duration || seekTime);
          if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
          const played = audioService.playAudio(newAudio, AudioPriority.HIGH);
          const actual = audioService.getNowPlayingAudio() ?? newAudio;
          runSetup(actual, usedVega);
          if (!played && characterRef?.current) characterRef.current.setIsTalking(false);
        };
        newAudio.addEventListener('canplay', onCanPlay, { once: true });
      } else {
        if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
        const playedSuccessfully = audioService.playAudio(newAudio, AudioPriority.HIGH);
        const actualPlayingAudio = audioService.getNowPlayingAudio() ?? newAudio;
        runSetup(actualPlayingAudio, usedVega);
        if (!playedSuccessfully && characterRef?.current) characterRef.current.setIsTalking(false);
      }
    })();

    return () => {
      cancelled = true;
      stopVegaUpdates();
      newAudio.volume = 0;
      newAudio.pause();
      audioService.stopCurrentAudio();
    };
  }, [summaryAudios.waiver, selectedButton, isDrawerOpen]);

  // Apply lip sync data when it arrives AFTER audio has already started. Defer to next frame to avoid audio pause.
  useEffect(() => {
    if (!isDrawerOpen || selectedButton !== "waiver" || !lipSyncData.waiver || !characterRef?.current) return;

    const nowPlayingAudio = audioService.getNowPlayingAudio();
    if (!nowPlayingAudio) return;

    const expectedSrc = summaryAudios.waiver;
    if (nowPlayingAudio.src !== expectedSrc) return;

    const data = lipSyncData.waiver;
    const wasPlaying = !nowPlayingAudio.paused;
    const raf = requestAnimationFrame(() => {
      if (!characterRef?.current) return;
      const waiverCuesCount = data.mouthCues?.length || 0;
      console.log(`[Waiver Audio] Applying late-arriving lip sync data: ${waiverCuesCount} cues (lipsync only, deferred)`);
      applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
      if (wasPlaying) characterRef.current.setIsTalking(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [lipSyncData.waiver, isDrawerOpen, selectedButton, summaryAudios.waiver]);

  // --- Autoplay useEffect for START/SIT ---
  useEffect(() => {
    if (
      !summaryAudios.start ||
      !isDrawerOpen ||
      selectedButton !== "start" ||
      hasAudioBeenPlayed.current.start ||
      startPcmPlayerRef.current
    ) {
      return;
    }
    const newAudio = new Audio(summaryAudios.start);
    newAudio.setAttribute("playsinline", "true");
    summaryAudioRefs.current.start = newAudio;

    const seekTime = startSeekTimeRef.current;
    if (seekTime != null) startSeekTimeRef.current = null;

    let stopVegaUpdates = () => { };
    let cancelled = false;

    const runSetup = (audioEl: HTMLAudioElement, usedVega: boolean) => {
      if (characterRef?.current) {
        console.log(`[Start Audio] Setting up lip sync with ${lipSyncData.start?.mouthCues?.length || 0} cues`);
        characterRef.current.setAudioElement(audioEl);
        characterRef.current.setIsTalking(true);
        if (!usedVega) applyLegacyLipSyncOrSkip(characterRef.current, lipSyncData.start, "start-url");
      }
      hasAudioBeenPlayed.current.start = true;
      console.log(`[Start Audio] Audio started - currentTime: ${audioEl.currentTime}`);
      audioEl.onended = () => {
        console.log('[Start Audio] Audio ended naturally');
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      };
      audioEl.addEventListener('play', () => console.log('[Start Audio] Audio play event fired'));
      audioEl.addEventListener('pause', () => console.log(`[Start Audio] Audio pause event fired - currentTime: ${audioEl.currentTime}`));
    };

    void (async () => {
      // Capacitor: start when the first ~2s of visemes exist (same moment as audio). Rest of the track fills in.
      if (isCapacitorNative()) {
        const headReady = await prepareVegaHeadFromAudioUrl(summaryAudios.start);
        if (cancelled || !headReady || !characterRef?.current) return;
        tryApplyVegaFromAudioUrlSync(summaryAudios.start, characterRef.current, "start-url");
        characterRef.current.setAudioElement(newAudio);
        const playedNow = audioService.playAudio(newAudio, AudioPriority.HIGH);
        runSetup(audioService.getNowPlayingAudio() ?? newAudio, true);
        if (!playedNow) characterRef.current.setIsTalking(false);
        stopVegaUpdates = subscribeVegaUrlUpdates(summaryAudios.start, (done) => {
          if (cancelled || !characterRef?.current) {
            stopVegaUpdates();
            return;
          }
          tryApplyVegaFromAudioUrlSync(summaryAudios.start, characterRef.current, "start-url");
          if (done) stopVegaUpdates();
        });
        return;
      }
      const usedVega = await prepareThenApplyVegaFromAudioUrl(
        summaryAudios.start,
        characterRef?.current,
        "start-url",
        () => {
          audioService.stopCurrentAudio();
          characterRef?.current?.setIsTalking(false);
        },
      );
      if (cancelled) return;

      if (seekTime != null && seekTime > 0) {
        const onCanPlay = () => {
          if (cancelled) return;
          newAudio.currentTime = Math.min(seekTime, newAudio.duration || seekTime);
          if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
          const played = audioService.playAudio(newAudio, AudioPriority.HIGH);
          const actual = audioService.getNowPlayingAudio() ?? newAudio;
          runSetup(actual, usedVega);
          if (!played && characterRef?.current) characterRef.current.setIsTalking(false);
        };
        newAudio.addEventListener('canplay', onCanPlay, { once: true });
      } else {
        if (isCapacitorNative() && usedVega && characterRef?.current) characterRef.current.setAudioElement(newAudio);
        const playedSuccessfully = audioService.playAudio(newAudio, AudioPriority.HIGH);
        const actualPlayingAudio = audioService.getNowPlayingAudio() ?? newAudio;
        runSetup(actualPlayingAudio, usedVega);
        if (!playedSuccessfully && characterRef?.current) characterRef.current.setIsTalking(false);
      }
    })();

    return () => {
      cancelled = true;
      stopVegaUpdates();
      newAudio.volume = 0;
      newAudio.pause();
      audioService.stopCurrentAudio();
    };
  }, [summaryAudios.start, selectedButton, isDrawerOpen]);

  // Apply lip sync data when it arrives AFTER audio has already started. Defer to next frame to avoid audio pause.
  useEffect(() => {
    if (!isDrawerOpen || selectedButton !== "start" || !lipSyncData.start || !characterRef?.current) return;

    const nowPlayingAudio = audioService.getNowPlayingAudio();
    if (!nowPlayingAudio) return;

    const expectedSrc = summaryAudios.start;
    if (nowPlayingAudio.src !== expectedSrc) return;

    const data = lipSyncData.start;
    const wasPlaying = !nowPlayingAudio.paused;
    const raf = requestAnimationFrame(() => {
      if (!characterRef?.current) return;
      const startCuesCount = data.mouthCues?.length || 0;
      console.log(`[Start Audio] Applying late-arriving lip sync data: ${startCuesCount} cues (lipsync only, deferred)`);
      applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
      if (wasPlaying) characterRef.current.setIsTalking(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [lipSyncData.start, isDrawerOpen, selectedButton, summaryAudios.start]);

  // Manejar apertura/cierre del drawer para reproducir chunks buffered cuando se abre
  useEffect(() => {
    if (isDrawerOpen && selectedButton === "waiver" && waiverBufferedChunksRef.current.length > 0) {
      if (summaryAudios.waiver) return; // Cache URL owns playback; do not overlap with live PCM buffer.
      // Only play from buffer when we already have at least first visemas (chunk 0)
      const hasFirstVisemas = isVegaLipSyncEnabled() || Object.prototype.hasOwnProperty.call(waiverLipSyncChunksRef.current, 0);
      if (!hasFirstVisemas) return;
      // Drawer abierto: si hay chunks buffered, inicializar player y reproducirlos
      // BUT: Only if summary audio is NOT playing (summary audio takes priority)
      if (!waiverPcmPlayerRef.current && !hasAudioBeenPlayed.current.waiver) {
        (async () => {
          stopOtherEndpointPcmPlayersAndService("waiver");
          // Apply visemas before any audio plays so we never show audio without lipsync
          if (characterRef?.current) {
            const chunks = waiverLipSyncChunksRef.current;
            const keys = Object.keys(chunks).map(Number).sort((a, b) => a - b);
            const mergedCues = keys.length > 0 ? keys.flatMap((k) => chunks[k] ?? []) : [];
            if (mergedCues.length > 0) {
              applyLegacyLipSyncOrSkip(characterRef.current, { mouthCues: mergedCues }, "buffered-pcm");
              if (!isVegaLipSyncEnabled()) {
                const t = performance.now() / 1000;
                waiverPcmStartTimeRef.current = t;
                characterRef.current.setPlaybackStartTime(t);
                characterRef.current.setIsTalking(true);
              }
            }
          }
          waiverPcmPlayerRef.current = new PcmChunkPlayer({
            sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
            channels: DEFAULT_AUDIO_CHANNELS,
            label: "waiver",
            vegaCharacterRef: characterRef,
            onPlaybackStarted: () => {
              if (!isVegaLipSyncEnabled() || !characterRef?.current) return;
              characterRef.current.setPlaybackStartTime(performance.now() / 1000);
              characterRef.current.setIsTalking(true);
            },
          });
          await waiverPcmPlayerRef.current.start();
          // Reproducir todos los chunks buffered
          for (const buffered of waiverBufferedChunksRef.current) {
            waiverPcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
          }
          waiverBufferedChunksRef.current = []; // Limpiar buffer
        })();
      }
    } else if (!isDrawerOpen && waiverPcmPlayerRef.current) {
      // Drawer cerrado: detener player (pero mantener chunks en buffer para la próxima apertura)
      waiverPcmPlayerRef.current.stop();
      waiverPcmPlayerRef.current = null;
      // PCM has no HTMLAudioElement; must clear lipsync clock (hasAudioBeenPlayed may still be false)
      if (characterRef?.current) characterRef.current.setIsTalking(false);
    }
    // Cleanup: si cambiamos de sección (no es waiver), limpiar buffer y player
    if (selectedButton !== "waiver") {
      if (waiverPcmPlayerRef.current) {
        waiverPcmPlayerRef.current.stop();
        waiverPcmPlayerRef.current = null;
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      }
      // Do not clear buffered chunks here; keep them so user can hear from the beginning when opening later.
    }
  }, [isDrawerOpen, selectedButton, stopOtherEndpointPcmPlayersAndService, summaryAudios.waiver]);

  // Manejar apertura/cierre del drawer para reproducir chunks buffered cuando se abre
  useEffect(() => {
    if (isDrawerOpen && selectedButton === "rundown" && rundownBufferedChunksRef.current.length > 0) {
      if (summaryAudios.rundown) return; // Cache URL owns playback; do not overlap with live PCM buffer.
      // Only play from buffer when we already have at least first visemas (chunk 0), so we never play audio without lipsync
      const hasFirstVisemas = isVegaLipSyncEnabled() || Object.prototype.hasOwnProperty.call(rundownLipSyncChunksRef.current, 0);
      if (!hasFirstVisemas) return;
      // Drawer abierto: si hay chunks buffered, inicializar player y reproducirlos
      // BUT: Only if summary audio is NOT playing (summary audio takes priority)
      if (!rundownPcmPlayerRef.current && !hasAudioBeenPlayed.current.rundown) {
        (async () => {
          stopOtherEndpointPcmPlayersAndService("rundown");
          // Apply visemas before any audio plays so we never show audio without lipsync
          if (characterRef?.current) {
            const chunks = rundownLipSyncChunksRef.current;
            const keys = Object.keys(chunks).map(Number).sort((a, b) => a - b);
            const mergedCues = keys.length > 0 ? keys.flatMap((k) => chunks[k] ?? []) : [];
            if (mergedCues.length > 0) {
              applyLegacyLipSyncOrSkip(characterRef.current, { mouthCues: mergedCues }, "buffered-pcm");
              if (!isVegaLipSyncEnabled()) {
                const t = performance.now() / 1000;
                rundownPcmStartTimeRef.current = t;
                characterRef.current.setPlaybackStartTime(t);
                characterRef.current.setIsTalking(true);
              }
            }
          }
          rundownPcmPlayerRef.current = new PcmChunkPlayer({
            sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
            channels: DEFAULT_AUDIO_CHANNELS,
            label: "rundown",
            vegaCharacterRef: characterRef,
            onPlaybackStarted: () => {
              if (!isVegaLipSyncEnabled() || !characterRef?.current) return;
              characterRef.current.setPlaybackStartTime(performance.now() / 1000);
              characterRef.current.setIsTalking(true);
            },
          });
          await rundownPcmPlayerRef.current.start();
          // Reproducir todos los chunks buffered
          for (const buffered of rundownBufferedChunksRef.current) {
            rundownPcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
          }
          rundownBufferedChunksRef.current = []; // Limpiar buffer
        })();
      }
    } else if (!isDrawerOpen && rundownPcmPlayerRef.current) {
      // Drawer cerrado: detener player (pero mantener chunks en buffer para la próxima apertura)
      rundownPcmPlayerRef.current.stop();
      rundownPcmPlayerRef.current = null;
      if (characterRef?.current) characterRef.current.setIsTalking(false);
    }
    // Cleanup: si cambiamos de sección (no es rundown), limpiar buffer y player
    if (selectedButton !== "rundown") {
      if (rundownPcmPlayerRef.current) {
        rundownPcmPlayerRef.current.stop();
        rundownPcmPlayerRef.current = null;
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      }
      // Do not clear buffered chunks here; keep them so user can hear from the beginning when opening later.
    }
  }, [isDrawerOpen, selectedButton, stopOtherEndpointPcmPlayersAndService, summaryAudios.rundown]);

  //  Handle drawer open/close to play buffered chunks when opened
  useEffect(() => {
    if (isDrawerOpen && selectedButton === "DraftAdvice" && DraftAdviceBufferedChunksRef.current.length > 0 && !draftAdviceAudioBlockedForForcedRefreshRef.current) {
      if (summaryAudios.DraftAdvice) return; // Cache URL owns playback; do not overlap with live PCM buffer.
      // Only play from buffer when we already have at least first visemas (chunk 0), so we never play audio without lipsync
      const hasFirstVisemas = isVegaLipSyncEnabled() || Object.prototype.hasOwnProperty.call(DraftAdviceLipSyncChunksRef.current, 0);
      if (!hasFirstVisemas) return;
      // Drawer abierto: si hay chunks buffered, inicializar player y reproducirlos
      // BUT: Only if summary audio is NOT playing (summary audio takes priority)
      if (!DraftAdvicePcmPlayerRef.current && !hasAudioBeenPlayed.current.DraftAdvice) {
        (async () => {
          stopOtherEndpointPcmPlayersAndService("DraftAdvice");
          // Apply visemas before any audio plays so we never show audio without lipsync
          if (characterRef?.current) {
            const chunks = DraftAdviceLipSyncChunksRef.current;
            const keys = Object.keys(chunks).map(Number).sort((a, b) => a - b);
            const mergedCues = keys.length > 0 ? keys.flatMap((k) => chunks[k] ?? []) : [];
            if (mergedCues.length > 0) {
              applyLegacyLipSyncOrSkip(characterRef.current, { mouthCues: mergedCues }, "buffered-pcm");
              if (!isVegaLipSyncEnabled()) {
                const t = performance.now() / 1000;
                DraftAdvicePcmStartTimeRef.current = t;
                characterRef.current.setPlaybackStartTime(t);
                characterRef.current.setIsTalking(true);
              }
            }
          }
          DraftAdvicePcmPlayerRef.current = new PcmChunkPlayer({
            sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
            channels: DEFAULT_AUDIO_CHANNELS,
            label: "DraftAdvice",
            vegaCharacterRef: characterRef,
            onPlaybackStarted: () => {
              if (!isVegaLipSyncEnabled() || !characterRef?.current) return;
              characterRef.current.setPlaybackStartTime(performance.now() / 1000);
              characterRef.current.setIsTalking(true);
            },
          });
          await DraftAdvicePcmPlayerRef.current.start();
          // Reproducir todos los chunks buffered
          for (const buffered of DraftAdviceBufferedChunksRef.current) {
            DraftAdvicePcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
          }
          DraftAdviceBufferedChunksRef.current = []; // Limpiar buffer
        })();
      }
    } else if (!isDrawerOpen && DraftAdvicePcmPlayerRef.current) {
      // Drawer cerrado: detener player (pero mantener chunks en buffer para la próxima apertura)
      DraftAdvicePcmPlayerRef.current.stop();
      DraftAdvicePcmPlayerRef.current = null;
      if (characterRef?.current) characterRef.current.setIsTalking(false);
    }
    // Cleanup: si cambiamos de sección (no es DraftAdvice), limpiar buffer y player
    if (selectedButton !== "DraftAdvice") {
      if (DraftAdvicePcmPlayerRef.current) {
        DraftAdvicePcmPlayerRef.current.stop();
        DraftAdvicePcmPlayerRef.current = null;
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      }
      // Do not clear buffered chunks here; keep them so user can hear from the beginning when opening later.
    }
  }, [isDrawerOpen, selectedButton, stopOtherEndpointPcmPlayersAndService, summaryAudios.DraftAdvice]);

  // Manejar apertura/cierre del drawer para reproducir chunks buffered cuando se abre
  useEffect(() => {
    if (isDrawerOpen && selectedButton === "start" && startBufferedChunksRef.current.length > 0) {
      if (summaryAudios.start) return; // Cache URL owns playback; do not overlap with live PCM buffer.
      // Only play from buffer when we already have at least first visemas (chunk 0)
      const hasFirstVisemas = isVegaLipSyncEnabled() || Object.prototype.hasOwnProperty.call(startLipSyncChunksRef.current, 0);
      if (!hasFirstVisemas) return;
      // Drawer abierto: si hay chunks buffered, inicializar player y reproducirlos
      // BUT: Only if summary audio is NOT playing (summary audio takes priority)
      if (!startPcmPlayerRef.current && !hasAudioBeenPlayed.current.start) {
        (async () => {
          stopOtherEndpointPcmPlayersAndService("start");
          // Apply visemas before any audio plays so we never show audio without lipsync
          if (characterRef?.current) {
            const chunks = startLipSyncChunksRef.current;
            const keys = Object.keys(chunks).map(Number).sort((a, b) => a - b);
            const mergedCues = keys.length > 0 ? keys.flatMap((k) => chunks[k] ?? []) : [];
            if (mergedCues.length > 0) {
              applyLegacyLipSyncOrSkip(characterRef.current, { mouthCues: mergedCues }, "buffered-pcm");
              if (!isVegaLipSyncEnabled()) {
                const t = performance.now() / 1000;
                startPcmStartTimeRef.current = t;
                characterRef.current.setPlaybackStartTime(t);
                characterRef.current.setIsTalking(true);
              }
            }
          }
          startPcmPlayerRef.current = new PcmChunkPlayer({
            sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
            channels: DEFAULT_AUDIO_CHANNELS,
            label: "start",
            vegaCharacterRef: characterRef,
            onPlaybackStarted: () => {
              if (!isVegaLipSyncEnabled() || !characterRef?.current) return;
              characterRef.current.setPlaybackStartTime(performance.now() / 1000);
              characterRef.current.setIsTalking(true);
            },
          });
          await startPcmPlayerRef.current.start();
          // Reproducir todos los chunks buffered
          for (const buffered of startBufferedChunksRef.current) {
            startPcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
          }
          startBufferedChunksRef.current = []; // Limpiar buffer
        })();
      }
    } else if (!isDrawerOpen && startPcmPlayerRef.current) {
      // Drawer cerrado: detener player (pero mantener chunks en buffer para la próxima apertura)
      startPcmPlayerRef.current.stop();
      startPcmPlayerRef.current = null;
      if (characterRef?.current) characterRef.current.setIsTalking(false);
    }
    // Cleanup: si cambiamos de sección (no es start), limpiar buffer y player
    if (selectedButton !== "start") {
      if (startPcmPlayerRef.current) {
        startPcmPlayerRef.current.stop();
        startPcmPlayerRef.current = null;
        if (characterRef?.current) characterRef.current.setIsTalking(false);
      }
      // Do not clear buffered chunks here; keep them so user can hear from the beginning when opening later.
    }
  }, [isDrawerOpen, selectedButton, stopOtherEndpointPcmPlayersAndService, summaryAudios.start]);

  // When user switches to an endpoint tab, stop any other endpoint audio immediately so we don't hear wrong audio
  // Defer to next tick so summary audio playback can start first; then we only stop if it's not from current endpoint
  useEffect(() => {
    if (selectedButton === "waiver" || selectedButton === "start" || selectedButton === "rundown" || selectedButton === "DraftAdvice") {
      const timeoutId = setTimeout(() => {
        const nowPlayingAudio = audioService.getNowPlayingAudio();
        const currentEndpointAudioSrc = summaryAudios[selectedButton];

        if (nowPlayingAudio && currentEndpointAudioSrc && nowPlayingAudio.src === currentEndpointAudioSrc) {
          // Audio is from current endpoint - only stop other PCM players, not the service
          if (selectedButton !== "waiver" && waiverPcmPlayerRef.current) {
            waiverPcmPlayerRef.current.stop();
            waiverPcmPlayerRef.current = null;
          }
          if (selectedButton !== "start" && startPcmPlayerRef.current) {
            startPcmPlayerRef.current.stop();
            startPcmPlayerRef.current = null;
          }
          if (selectedButton !== "rundown" && rundownPcmPlayerRef.current) {
            rundownPcmPlayerRef.current.stop();
            rundownPcmPlayerRef.current = null;
          }
          if (selectedButton !== "DraftAdvice" && DraftAdvicePcmPlayerRef.current) {
            DraftAdvicePcmPlayerRef.current.stop();
            DraftAdvicePcmPlayerRef.current = null;
          }
        } else {
          stopOtherEndpointPcmPlayersAndService(selectedButton);
        }
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedButton, stopOtherEndpointPcmPlayersAndService, summaryAudios]);

  useEffect(() => {
    // Keep the ref updated
    dataLoadedRef.current = dataLoadedFromTeamsPage;
  }, [dataLoadedFromTeamsPage]);

  // Load estimated times from backend when component mounts
  useEffect(() => {
    const loadEstimatedTimes = async () => {
      try {
        const accessToken = await getSafeAccessAuth0Token();
        if (accessToken) {
          const response = await getEstimatedTimes(accessToken);
          setEstimatedTimes(response.estimatedTime);
        }
      } catch (error) {
        console.error("Error loading estimated times:", error);
        // Use default values as fallback
        // setEstimatedTimes({
        //   waiver: 90000,
        //   start: 90000,
        //   rundown: 90000,
        //   news: 13200,
        //   rankings: 13200,
        //   rosters: 13200,
        // });
      }
    };

    if (isAuthenticated) {
      loadEstimatedTimes();
    }
  }, [isAuthenticated, getSafeAccessAuth0Token]);

  // Get the current platform from the leaguePlatforms
  const currentPlatform = leaguePlatforms.find((p) => p.isSelected)?.id;

  //--------------------------------------------
  // Ref to track if a team selection is being handled
  const isHandlingTeamSelection = useRef(false);

  const yahooCallbackAbortController = useRef<AbortController | null>(null);

  //--------------------------------------------
  // Effect to handle Yahoo callback
  useEffect(() => {
    const handleYahooCallback = async () => {
      // Logic is now the same for web and mobile
      const hasCallbackActive = localStorage.getItem("yahoo_callback_active") === "true";

      // CRITICAL: Wait for preferences to load before processing Yahoo callback
      // This ensures teamsDataCache is populated before we try to use it
      if (hasCallbackActive && isLoadingPreferences) {
        console.log("[YAHOO CALLBACK] Waiting for preferences to load...");
        return; // Exit early, will retry when isLoadingPreferences becomes false
      }

      if (hasCallbackActive) {
        const tokenToUse = localStorage.getItem("yahoo_token");

        if (!tokenToUse) {
          // If no token is found, something is wrong. Clean up and exit.
          console.error("Yahoo callback triggered, but no token was found in localStorage.");
          localStorage.removeItem("yahoo_callback_active");
          return;
        }

        // const success = await lockPlatformTransition("Yahoo", async () => {
        try {
          if (yahooCallbackAbortController.current) {
            yahooCallbackAbortController.current.abort();
          }

          yahooCallbackAbortController.current = new AbortController();
          const signal = yahooCallbackAbortController.current.signal;

          //to show circular progress in ui in the meantime
          setIsChangingPlatform(true);
          setYahooFinish(true);
          // Cancel pending requests
          cancelPendingRequests();

          //this is the key to set in localstorage and yahooToken state, the value of yahoo token from localStorage
          //update yahoo token in context with the token from localStorage
          updateYahooToken(tokenToUse);

          const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
          if (!accessTokenFromAuth0) {
            // Logout is in progress, stop execution.
            return;
          }

          const currentPreferences = await getUserPreferences(accessTokenFromAuth0, signal);

          // Restore caches from preferences before updating them with Yahoo data
          if (currentPreferences) {
            setTeamsDataCache(currentPreferences.teamsData || {});
            setUserDataCache(currentPreferences.userDataPlatform || {});
            setSelectedTeams(currentPreferences.selectedTeams || {});
          }

          console.log("HANDLE YAHOO CALLBACK: currentPreferences", currentPreferences);
          console.log("HANDLE YAHOO CALLBACK: teamsDataCache", teamsDataCache);
          console.log("HANDLE YAHOO CALLBACK: userDataCache", userDataCache);
          console.log("HANDLE YAHOO CALLBACK: selectedTeams", selectedTeams);

          // Ensure POC token is ready in backend (Redis); backend returns only { success }
          const pocResult = await getPocToken(accessTokenFromAuth0, signal);
          if (!pocResult?.success) {
            throw new Error("POC token not available");
          }
          const yahooUserDataPlatform = {
            integration_name: "yahoo",
            token: tokenToUse,
            sport: "NFL",
            // year: "2025",
            user_id: "",
            error: null,
          };

          // Backend resolves pocToken from Redis; we pass context pocToken for compatibility
          const yahooTeamsData = await getUserLeagues(yahooUserDataPlatform, accessTokenFromAuth0, pocToken, signal);

          setUserDataPlatform(yahooUserDataPlatform);
          setTeamsdata(yahooTeamsData);
          setIsChangingPlatform(false);

          // Keep all existing platforms and update Yahoo
          const updatedPlatforms = leaguePlatforms.map((p) => {
            if (p.id === "Yahoo") {
              return {
                ...p,
                isLoggedIn: true,
                isSelected: true,
              };
            }
            // Keep the exact state of the other platforms
            const existingPlatform = currentPreferences?.loggedPlatforms?.find((platform) => platform.id === p.id);
            if (existingPlatform) {
              return {
                ...p,
                ...existingPlatform,
                isSelected: false,
              };
            }
            return p;
          });

          // CRITICAL: Update caches with Yahoo data BEFORE setLeaguePlatforms
          // This ensures loadPlatformData reads correct data when triggered
          setTeamsDataCache((prev) => ({
            ...prev,
            Yahoo: yahooTeamsData,
          }));

          setUserDataCache((prev) => ({
            ...prev,
            Yahoo: yahooUserDataPlatform,
          }));

          setLeaguePlatforms(updatedPlatforms);

          //open teams acordion automatically if there are teams in the yahoo account

          const hasYahooTeams = Array.isArray(yahooTeamsData)
            ? yahooTeamsData.length > 0
            : Boolean(yahooTeamsData?.leagues?.length);
          setExpanded(hasYahooTeams ? "panel1" : "");


          // IMPORTANT: Preserve the existing sectionData if it is valid

          if (currentPreferences?.sectionData?.commonLeagueData?.timestamp) {
            const cacheTime = new Date(currentPreferences.sectionData.commonLeagueData.timestamp).getTime();
            const currentTime = new Date().getTime();
            const oneHour = 60 * 60 * 1000;
            // Check if the cache is less than one hour old and if it has all the required data
            if (currentTime - cacheTime < oneHour) {
              setSectionData(currentPreferences.sectionData);
            } else {
              // If the cache is older than one hour or doesn't have all the required data, reset the sectionData
              setSectionData({
                commonLeagueData: null,
                // waiverSuggestions: null,
                waiver: null,
                news: null,
                start: null,
                rosters: null,
                rankings: null,
                allRankings: null,
                rundown: null,
                DraftAdvice: null,
                superFunction: null,
              });
            }
          } else {
            // If there is no cache, reset the sectionData
            setSectionData({
              commonLeagueData: null,
              // waiverSuggestions: null,
              waiver: null,
              news: null,
              start: null,
              rosters: null,
              rankings: null,
              allRankings: null,
              rundown: null,
              DraftAdvice: null,
              superFunction: null,
            });
          }

          // Keep other platforms' last selected teams, but do NOT restore a Yahoo league.
          // Auto-select of a Yahoo team is intentionally disabled (handlePartnerClick below stays commented).
          const updatedSelectedTeams: SelectedTeams = {
            ...selectedTeams,
            ...currentPreferences?.selectedTeams,
            Yahoo: null,
          };
          setSelectedTeams(updatedSelectedTeams);

          // Clear stale Un-Synced / prior-platform selection so UI shows "Select Team" on Yahoo tab
          setSelectedTeamInfo(null);
          setSelectedPartner("");
          setLeagueIdPlatform("");

          // await postUserPreferences(accessTokenFromAuth0, {
          //   loggedPlatforms: updatedPlatforms,
          //   teamsData: {
          //     ...teamsDataCache,
          //     ...currentPreferences?.teamsData,
          //   },
          //   userDataPlatform: {
          //     ...userDataCache,
          //     ...currentPreferences?.userDataPlatform,
          //   },
          //   selectedTeams: {
          //     ...selectedTeams,
          //     ...currentPreferences?.selectedTeams,
          //   },
          // });

          // Construct the data to be saved by manually adding the new Yahoo data
          // to the existing preferences. This avoids using stale state from context.
          const teamsDataToSave = {
            ...teamsDataCache,
            ...currentPreferences?.teamsData,
            Yahoo: yahooTeamsData,
          };
          const userDataToSave = {
            ...userDataCache,
            ...currentPreferences?.userDataPlatform,
            Yahoo: yahooUserDataPlatform,
          };

          // Update backend keeping existing data
          await postUserPreferences(accessTokenFromAuth0, {
            loggedPlatforms: updatedPlatforms,
            teamsData: teamsDataToSave,
            userDataPlatform: userDataToSave,
            selectedTeams: updatedSelectedTeams,
          });

          // Save platform permission for Yahoo in mongo (use Yahoo user email when available)
          try {
            const { email: yahooEmail } = await getYahooUserInfo(accessTokenFromAuth0);
            const accountKey =
              (yahooEmail && yahooEmail.trim()) ||
              "yahoo_connected";
            await savePlatformPermission(accessTokenFromAuth0, {
              platform: "yahoo",
              accountKey,
              platformUserId: accountKey,
            });
          } catch (e) {
            console.error("Error saving Yahoo platform permission:", e);
          }
          // Replace URL: / for non-logged flow (former /home-free), /home for Auth0
          if (!isLoggedInAuth0Experience) {
            window.history.replaceState({}, "", "/");
          } else {
            window.history.replaceState({}, "", "/home");
          }

          // if (teamsDataCache["Yahoo"]?.leagues?.length > 0) {
          //   const firstTeam = teamsDataCache["Yahoo"].leagues[0];

          // if there are teams in the yahoo account, then select the first team
          if (yahooTeamsData?.leagues?.length > 0) {
            if (teamsHomePageRef.current) {
              // await new Promise((resolve) => setTimeout(resolve, 100));
              // const currentOperationId = ++latestPlatformLoadIdRef.current;
              // console.log(`HomePage_handleYahooCallback: Para equipo ${firstTeam.name}. ID de Operación: ${currentOperationId}. Ref: ${latestPlatformLoadIdRef.current}`);
              // await teamsHomePageRef.current.handlePartnerClick(firstTeam.name, firstTeam.league_id, "Yahoo", signal);
            }
          } else {
            // Explicitly handle the case when there are no teams
            const updatedSelectedTeams: SelectedTeams = {
              ...selectedTeams,
              Yahoo: null,
            };
            setSelectedTeams(updatedSelectedTeams);
            // setSelectedTeamInfo(null);
            // setSelectedPartner("");
            // setLeagueIdPlatform("");
            setMessage("No teams in your account");
          }

          // CRITICAL: Turn off loader AFTER everything is ready
          // This ensures loader stays visible until ALL state updates complete
          setIsChangingPlatform(false);

          setYahooFinish(false);
          localStorage.removeItem("yahoo_callback_active");
        } catch (error) {
          console.error("Error in Yahoo callback:", error);
          setMessage("Error connecting to Yahoo. Please try again.");
          // await new Promise((resolve) => setTimeout(resolve, 1000));
          setIsYahooLoading(false);
          setIsChangingPlatform(false); // make sure to hide the circular progress in ui
          // throw error;
        } finally {
          // isHandlingYahooCallback.current = false;
          localStorage.removeItem("yahoo_callback_active");
          yahooCallbackAbortController.current = null;
          setYahooFinish(false);
        }
      }
    };

    handleYahooCallback();

    return () => {
      if (yahooCallbackAbortController.current) {
        yahooCallbackAbortController.current.abort();
      }
    };
  }, [getSafeAccessAuth0Token, isLoadingPreferences]);

  // Function to confirm the AI settings (updates context and persists to Mongo via forceSavePreferences)
  const handleConfirmAiSettings = async (nextSettings: Record<string, boolean>) => {
    // Only settings that changed (compare with current aiSettings before updating) for analytics
    const changedSettings = Object.fromEntries(
      Object.entries(nextSettings).filter(([key, value]) => aiSettings[key] !== value)
    ) as Record<string, boolean>;

    setIsUpdatingAiSettings(true);
    try {
      // Merge to avoid dropping keys not controlled by the dialog (e.g. aiSettings.mute)
      const mergedSettings = { ...aiSettings, ...nextSettings };
      setAiSettings(mergedSettings);
      // Persist immediately so the value is stored in Mongo (override avoids stale state from batching)
      await forceSavePreferences({ aiSettings: mergedSettings });
      // Track only changed settings so analytics reflects actual user toggles, not full state
      if (Object.keys(changedSettings).length > 0) {
        const platformId = selectedTeamInfo?.platformId ?? "unknown";
        const leagueId = selectedTeamInfo?.leagueId ?? "unknown";
        const teamName = selectedTeamInfo?.teamName ?? "unknown";
        void trackAiSettingsSaved(
          authId ?? "unknown",
          platformId,
          leagueId,
          teamName,
          changedSettings,
          mergedSettings.quanDefault,
        );
      }
    } catch (error) {
      console.error("Failed to update AI settings", error);
    } finally {
      setIsUpdatingAiSettings(false);
      setIsAiDialogOpen(false);
    }
  };

  //--------------------------------------------

  // Function to detect selected partner and match it to the WidgetContext, then close the accordion when a partner is selected
  const handlePartnerSelected = (partnerName: string) => {
    // setTimeout(() => {
    handlePartnerSelect(partnerName);
    setSelectedPartner(partnerName);
    // Reset super-analysis state ONLY when the actual selected team changes.
    const currentPlatform = leaguePlatforms.find((p) => p.isSelected)?.id;
    console.log("handlePartnerSelected: currentPlatform", currentPlatform);
    console.log("handlePartnerSelected: selectedTeamInfo", selectedTeamInfo);
    console.log("handlePartnerSelected: partnerName", partnerName);
    const isSameTeam = selectedTeamInfo && selectedTeamInfo.platformId === currentPlatform && selectedTeamInfo.teamName === partnerName;
    if (!isSameTeam) {
      setIsSuperAnalysisComplete(false); // true team change
    }
    // Accordion close moved to TeamsDropdown handleTeamSelectionClick (manual pick only).
    // Auto-restore / platform switch call this via handlePartnerClick and must keep panel1 open.
    // setExpanded("");
    // }, 300);
  };

  // Function to select which of the 6 sections was selected and then turn orange
  const handleButtonSelected = (buttonName) => {
    selectedButtonRef.current = buttonName;
    setSelectedButton(buttonName);
  };

  // Articles before league selection: same drawer flow as SectionHomePage, without mutating selectedPartner
  const handleStandaloneArticlesClick = () => {
    if (!(allowedButtonsSections?.showArticles ?? true)) return;
    handleButtonSelected("articles");
    setShowPartnerList(true);
    setIsPaymentOpen(false);
    openDrawer();
    setTimeout(() => {
      scrollHomeToTop();
    }, 0);
  };

  // Function to open the swipeable
  const handlePartnerSelectAndOpenDrawer = (partner: string) => {
    // Do not reset super-analysis state if we're only opening the drawer.
    // Only update selected partner when it actually changes.
    console.log("handlePartnerSelectAndOpenDrawer: partner", partner);
    console.log("handlePartnerSelectAndOpenDrawer: selectedPartner", selectedPartner);
    if (partner !== selectedPartner) {
      handlePartnerSelected(partner);
    }

    setShowPartnerList(true);
    setIsPaymentOpen(false);
    // Hide thought sign only for stream drawers (waiver/start/rundown/draft advice).
    if (shouldHideSignForStreamDrawer(selectedButtonRef.current)) {
      setThoughtsCommon("");
      setSignThoughtsDrawerSource(null);
    }
    openDrawer();
    // Scroll to top immediately after opening
    // requestAnimationFrame(() => {
    //   scrollHomeToTop();
    // });
    setTimeout(() => {
      scrollHomeToTop();
    }, 0);
  };

  // Function to close the swipeable
  const handlePartnerSelectAndCloseDrawer = useCallback(() => {
    // handlePartnerSelected(partner);
    if (shouldHideSignForStreamDrawer(selectedButtonRef.current)) {
      setThoughtsCommon("");
      setSignThoughtsDrawerSource(null);
    }
    setShowPartnerList(false);
    closeDrawer();
    setShouldPlayAudio(false);
    setIsPaymentOpen(false);
  }, [closeDrawer, setShowPartnerList, setShouldPlayAudio]);

  // Function to get the league_id of TeamsHomePage component
  const handleLeagueId = (leagueId: string) => {
    // console.log("Setting leagueId in HomePage:", leagueId);
    setLeagueIdPlatform(leagueId);
  };

  //--------------------------------------------

  useEffect(() => {
    if (yahooLeagueId && leaguePlatforms.find((p) => p.name.toLowerCase() === "yahoo" && p.isLoggedIn)) {
      setLeagueIdPlatform(yahooLeagueId);
    }
  }, [yahooLeagueId, leaguePlatforms]);

  //--------------------------------------------

  // Function to fetch News data
  const fetchNewsData = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any
  ) => {
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `news-${platformId}-${leagueId}`;
    // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
    const currentUserDataPlatform = userDataCache[platformId];

    try {
      setLoadingNews(true);
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        // Logout is in progress. Stop execution and ensure loading is false.
        setLoadingNews(false);
        return null;
      }

      const startTime = Date.now(); // Start timer for initial feedback
      // console.log("News: accessTokenFromAuth0", accessTokenFromAuth0);
      const data = await getNews(authId, currentUserDataPlatform, leagueId, teamName, pocToken, accessTokenFromAuth0, signal, yahooToken, forceRefresh, calculatorSettings, platformId);

      setNewsData(data);
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
      updateSectionData("news", data);
      setLoadingNews(false);
      // Set shouldPlayAudio to true if selectedButton is "news"
      // if (selectedButton === "news") {
      //   setShouldPlayAudio(false);
      //   setTimeout(() => setShouldPlayAudio(true), 10);
      // }

      const durationSeconds = (Date.now() - startTime) / 1000;
      if (durationSeconds > 30) {
        void trackSlowOperation(authId, "fetch_news", durationSeconds, platformId, leagueId, teamName);
      }
      return data;
    } catch (error) {
      // Rate limit: open drawer and mark button as failed so retry icon shows
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingNews(false);
        return null;
      }
      if (error.name !== "AbortError") {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, News: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, news: true }));
        }
        setNewsData(null);
        setLoadingNews(false);
        return null;
      }
      throw error;
    } finally {
      // if (Capacitor.isNativePlatform() && taskId) {
      //   console.log(`News: Finishing background task: ${taskId}`);
      //   BackgroundTask.finish({ taskId });
      // }
    }
  };

  //--------------------------------------------
  //NO BORRAR LO SIGUIENTE, ES GETWAIVER REAL
  //--------------------------------------------

  const fetchWaiverData = async (leagueId: string, signal: AbortSignal, yahooToken?: string) => {
    setLoadingWaiver(true);

    try {
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        setLoadingWaiver(false);
        return null;
      }

      const data = await getWaiver(authId, userDataPlatform, leagueId, pocToken, accessTokenFromAuth0, signal, yahooToken);
      setWaiverData(data);
      setEndpointErrors((prev) => ({ ...prev, waiver: false }));
      updateSectionData("waiver", data);
      setLoadingWaiver(false);
      return data;
    } catch (error) {
      if (error.name !== "AbortError") {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Waiver: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, waiver: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, waiver: true }));
        }
        setWaiverData(null);
        setLoadingWaiver(false);
        // Set shouldPlayAudio to true if selectedButton is "waiver"
        if (selectedButton === "waiver") {
          setShouldPlayAudio(false);
          setTimeout(() => setShouldPlayAudio(true), 10);
        }
        return null;
      }
      throw error;
    }
  };

  //--------------------------------------------

  // Function to fetch Waiver data stream
  const fetchWaiverDataStream = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any,
    modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE",
    checkCacheOnly = false
  ) => {
    // CAPTURE CONTEXT: Take a snapshot of the user, team, and platform this request is for.
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `waiver-${platformId}-${leagueId}`;
    const teamKey = `${platformId}-${leagueId}`; // NEW: Create a unique key for the team

    // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
    const currentUserDataPlatform = userDataCache[platformId];

    // 1. Create a unique key for this request.
    const requestKey = `${authId}-waiver-${platformId}-${leagueId}`;
    console.log("Request key FOR WAIVER HOME:", requestKey);
    // const requestKey = `${authId}-waiver-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;

    // 2. The guardian: if a request for this key is already running, do nothing.
    if (activeStreamRequests.current.has(requestKey)) {
      console.log(`Request for ${requestKey} is already in flight. Skipping new request.`);
      return;
    }

    // This flag controls who is responsible for cleaning up the lock.
    let pollingIsResponsibleForCleanup = false;

    try {
      // It's a new request, so we are no longer in a recovery state for this stream.
      setIsRecoveringFromBackground((prev) => ({ ...prev, waiver: false }));
      // 3. Register the new request to block duplicates.
      activeStreamRequests.current.add(requestKey);

      // Show immediate frontend placeholder thought before first backend thought arrives.
      // setThoughtsCommon("Loading...");

      // Only reset if we are not coming from background or if there is valid content
      const hasValidContent = waiverData?.waiver_analysis && waiverData.waiver_analysis.length > 0;
      const shouldSkipReset = returnedFromBackground && hasValidContent && isDrawerOpen;

      if (!shouldSkipReset) {
        // Reset states only when necessary
        if (!checkCacheOnly) {
          setLoadingWaiver(true);
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
          setStreamProgress((prev) => ({ ...prev, waiver: "" }));
          // Must reset target to 0 like Start/Rundown/Draft — otherwise prev.waiver stays 100 and the initial ramp exits immediately
          setStreamProgressTargets((prev) => ({ ...prev, waiver: 0 }));

          setWaiverStreamState({
            status: "",
            thought: "",
            isGeneratingResult: false,
            isStreamActive: false,
            isWaitingForChart: false,
            isGeneratingAudio: false,
            teamKey: teamKey, // NEW: Associate the stream with the team
          });
          setHasWaiverAnimationCompleted(false);
          setWaiverData({ waiver_analysis: "", chart_data: [], request_id: null });
          // updateSectionData("waiver", null);
          setSummaryAudios((prev) => ({ ...prev, waiver: null })); // Reset the audio blob
          hasAudioBeenPlayed.current.waiver = false; // CRITICAL: Reset the played flag for the new analysis
        }
      }

      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        // if we return here, the `finally` will execute and clean up the lock. Correct.
        return;
      }

      const startTime = Date.now(); // Start timer for initial feedback

      // Start a gentle initial ramp from 0% → 10% while waiting first thought
      const stopInitialWaiverRamp = () => {
        if (waiverInitialRampTimerRef.current) {
          clearInterval(waiverInitialRampTimerRef.current);
          waiverInitialRampTimerRef.current = null;
        }
      };

      if (!checkCacheOnly) {
        // Ensure any previous ramp is stopped before starting a new one
        stopInitialWaiverRamp();
        waiverInitialRampTimerRef.current = setInterval(() => {
          setStreamProgressTargets((prev) => {
            const current = prev.waiver || 0;
            if (current >= 10) {
              stopInitialWaiverRamp();
              return prev;
            }
            const next = Math.min(10, current + 0.2); // +0.2% for each 300ms → ~15s from 0% to 10%
            return { ...prev, waiver: next };
          });
        }, 300); // 300ms = 0.3s means 0.3s between each increment
      }

      // 3. Call the backend API to get the stream
      // Use modelOverride if provided, otherwise use selectedModel state
      const modelToUse = modelOverride || selectedModel;
      const response = await getWaiverStream(
        authId,
        currentUserDataPlatform,
        leagueId,
        teamName,
        pocToken,
        accessTokenFromAuth0,
        signal,
        yahooToken,
        aiSettings.audio,
        forceRefresh,
        calculatorSettings,
        modelToUse,
        checkCacheOnly
      );

      // NEW, ROBUST LOGIC:
      // If the response is INCOMPLETE, hand off control to the polling function.
      if (response === "INCOMPLETE") {
        console.log("Waiver stream received INCOMPLETE. Starting polling.");

        // transfer responsibility to the polling function:
        // tell the polling function that it will be responsible for the cleanup.
        pollingIsResponsibleForCleanup = true;

        startPollingForWaiverCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          // When the polling finishes (success or failure), clean up its own lock.
          // THIS IS NO LONGER NEEDED HERE. The polling function will manage its own lifecycle.
          activeStreamRequests.current.delete(requestKey);
          console.log(`Polling for ${requestKey} finished. Lock released.`);
        });
        // We exit, knowing that the polling is in charge.
        return "INCOMPLETE";
      }

      if (response === "NOT_CACHED") {
        console.log("Waiver data not cached. checkCacheOnly=true returned NOT_CACHED.");
        if (!checkCacheOnly) setLoadingWaiver(false);
        stopInitialWaiverRamp();
        return "NOT_CACHED";
      }

      // If we are here and checkCacheOnly is true, it means data IS cached and we are receiving a stream.
      // Don't set setLoadingWaiver(true) or start ramp timer — this avoids triggering isAnyLoadingActive
      // in AnimatedButton, which would block shouldBeWhite for ALL buttons until all 4 endpoints finish.
      // The stream data will accumulate silently and AnimatedButton's useEffect will detect valid data
      // and jump directly to 100% white/clickable (fluid transition, no grey-at-100% flash).
      if (checkCacheOnly) {
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
        setStreamProgress((prev) => ({ ...prev, waiver: "" }));
        setStreamProgressTargets((prev) => ({ ...prev, waiver: 0 }));

        setWaiverStreamState({
          status: "",
          thought: "",
          isGeneratingResult: false,
          isStreamActive: false,
          isWaitingForChart: false,
          isGeneratingAudio: false,
          teamKey: teamKey,
        });
        setHasWaiverAnimationCompleted(false);
        setWaiverData({ waiver_analysis: "", chart_data: [], request_id: null });
        setSummaryAudios((prev) => ({ ...prev, waiver: null }));
        hasAudioBeenPlayed.current.waiver = false;
      }

      // Type guard: ensure response is Response type, not a different string.
      if (!response) {
        if (response === undefined) return; // Aborted, error handled in catch block
        throw new Error("Streaming request failed");
      } else if (typeof response === "string") {
        // This should not happen now due to the lock, but as a safeguard:
        throw new Error(`Unexpected error response`);
      } else if (!response.body) {
        throw new Error("Streaming request failed - no body");
      }

      // Update credits/freeCredits after successful waiver execution
      // Premium models consume credits, FREE model consumes freeCredits
      updateCreditsAfterEndpoint("waiver");

      // 4. Setup stream processing logic
      const reader = response.body.getReader();
      // Initialize the decoder to decode the data chunks
      const decoder = new TextDecoder();
      // Initialize the buffer to store the data chunks
      let buffer = "";
      // finalData accumulates the full response for caching
      let finalData: {
        waiver_analysis: string;
        chart_data: any[];
        request_id: string | null;
        fromCache?: boolean;
        context?: { platformId: string; leagueId: string; userId: string | undefined };
        model?: "RotoGPT 5.0" | "RotoGPT FREE"; // Track which model generated this response (for UI rendering of UpgradeSubscriptionFree, NOT for polling)
      } = {
        waiver_analysis: "",
        chart_data: [],
        request_id: null,
      };

      // Read and process the stream until it's complete
      const readStream = async () => {
        waiverLipSyncChunksRef.current = {};
        while (true) {
          // Read the next chunk of data from the stream
          const { done, value } = await reader.read();
          // Append this chunk to buffer before checking done so we never skip api_error in the last chunk
          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }
          // 6. Process incoming data chunks (chunks of data from the stream)
          // Split the buffer into complete SSE messages
          const parts = buffer.split("\n\n");
          // Keep incomplete message in buffer (to avoid storing the event: end_stream message)
          buffer = parts.pop() || "";

          // Iterate through the parts (each part is a complete SSE message)
          for (const part of parts) {
            // If the part does not start with 'data:', skip it
            if (!part.startsWith("data: ")) continue;

            let event;
            try {
              // Remove the "data: " from the part
              const jsonData = part.substring(6);
              // Parse the part as JSON
              event = JSON.parse(jsonData);
            } catch (e) {
              console.error("Failed to parse JSON part from stream:", e, part);
              continue; // Skip this malformed message part
            }

            // 7. Update state based on the event type using functional updates
            // to prevent stale state issues within the stream's closure.
            switch (event.type) {
              // If the event type is cache_status, set fromCache flag immediately
              case "cache_status":
                const fromCache = Boolean(event?.payload?.fromCache);
                setWaiverData((prev) => ({
                  ...prev,
                  fromCache: fromCache,
                }));
                finalData.fromCache = fromCache; // Update finalData
                break;
              // If the event type is status, set the stream status
              case "status":
                // Only track slow operation on the very first status message.
                if (event.payload === "Starting conversation with RotoGPT...") {
                  const durationSeconds = (Date.now() - startTime) / 1000;
                  if (durationSeconds > 30) {
                    void trackSlowOperation(authId, "fetch_waiver", durationSeconds, platformId, leagueId, teamName);
                  }
                }
                // If the stream is already generating result, return the previous state
                setWaiverStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const statusText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, status: statusText, isStreamActive: true };
                  // return { ...prev, status: statusText };
                });
                break;
              case "thought":
                // Stop the initial ramp once first thought arrives
                if (waiverInitialRampTimerRef.current) {
                  clearInterval(waiverInitialRampTimerRef.current);
                  waiverInitialRampTimerRef.current = null;
                }
                // If the stream is already generating result, return the previous state
                setWaiverStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const thoughtText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, thought: thoughtText, isStreamActive: true };
                  // return { ...prev, thought: thoughtText };
                });

                //set the thought to the common thoughts state
                setThoughtsCommon(event.payload);
                // Each thought nudges +1% up to 90% (smooth target)
                setStreamProgressTargets((prev) => {
                  const current = prev.waiver || 0;
                  const next = Math.min(Math.max(current, 10) + 1, 90);
                  return { ...prev, waiver: next };
                });
                break;
              case "text_chunk":
                // Stop the initial ramp once first thought arrives
                if (waiverInitialRampTimerRef.current) {
                  clearInterval(waiverInitialRampTimerRef.current);
                  waiverInitialRampTimerRef.current = null;
                }
                // Each text chunk nudges +1.5% up to 90% (smooth target)
                setStreamProgressTargets((prev) => {
                  const current = prev.waiver || 0;
                  const next = Math.min(Math.max(current, 10) + 1.5, 90);
                  return { ...prev, waiver: next };
                });
                // If the chunk has actual content, switch to result mode
                if (event.payload) {
                  setWaiverStreamState((prev) => {
                    if (!prev.isGeneratingResult) {
                      clearSignThoughtsOnResultPhase();
                      return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                      // return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false, teamKey };
                    }
                    return { ...prev, isStreamActive: true, isWaitingForChart: false };
                    // return { ...prev, isStreamActive: true, isWaitingForChart: false, teamKey };
                  });
                  // Update the waiver data
                  setWaiverData((prev) => {
                    const updatedData = {
                      ...prev,
                      context: contextAtRequestStart,
                      waiver_analysis: (prev.waiver_analysis || "") + event.payload,
                    };
                    finalData.waiver_analysis = updatedData.waiver_analysis; // Update finalData
                    return updatedData;
                  });
                }
                break;
              // case "final_text":
              //   if (event.payload) {
              //     setWaiverStreamState((prev) => ({
              //       ...prev,
              //       status: "",
              //       thought: "",
              //       isGeneratingResult: true,
              //       isStreamActive: true,
              //       isWaitingForChart: false,
              //     }));
              //     setWaiverData((prev) => {
              //       const updatedData = {
              //         ...prev,
              //         context: contextAtRequestStart,
              //         waiver_analysis: event.payload,
              //       };
              //       finalData.waiver_analysis = updatedData.waiver_analysis;
              //       return updatedData;
              //     });
              //   }
              //   break;
              case "chart_data":
                setWaiverStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                    // return { ...prev, status: "", thought: "", isGeneratingResult: true, isWaitingForChart: false };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: false };
                  // return { ...prev, isWaitingForChart: false };
                });
                setWaiverData((prev) => {
                  const updatedData = {
                    ...prev,
                    context: contextAtRequestStart,
                    // The text already contains the correct [CHART:key] placeholders from the 'text_chunk' event.
                    // We no longer need to add artificial placeholders like [chart_0].
                    // waiver_analysis: (prev.waiver_analysis || "") + `[chart_${prev.chart_data.length}]`,
                    chart_data: [...prev.chart_data, event.payload], // Add the chart data to the waiver data
                  };
                  // We no longer update finalData.waiver_analysis here.
                  finalData.chart_data = updatedData.chart_data;
                  return updatedData;
                });
                break;
              case "tool_call":
                setWaiverStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: true };
                    // return { ...prev, status: "", thought: "", isGeneratingResult: true, isWaitingForChart: true };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: true };
                  // return { ...prev, isWaitingForChart: true };
                });
                break;

              case "audio_generation_start":
                // Stop the initial ramp once first thought arrives
                if (waiverInitialRampTimerRef.current) {
                  clearInterval(waiverInitialRampTimerRef.current);
                  waiverInitialRampTimerRef.current = null;
                }
                setWaiverStreamState((prev) => ({ ...prev, isGeneratingAudio: true }));
                break;
              case "lipsync_data": {
                // Store chunks by chunkIndex and merge in ascending order (chunks may arrive 3,2,1,0)
                const payload = event.payload || {};
                const chunkIndex = payload.chunkIndex;
                const mouthCues = payload.mouthCues || [];
                const currentMergedCount = Object.keys(waiverLipSyncChunksRef.current).length
                  ? Object.keys(waiverLipSyncChunksRef.current).map(Number).sort((a, b) => a - b).flatMap((k) => waiverLipSyncChunksRef.current[k] ?? []).length
                  : 0;
                const sel = selectedButtonRef.current;
                const applyToChar = characterRef?.current && sel === "waiver";
                console.log(`[LipsyncTrace] Waiver client lipsync_data chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} mergedBefore=${currentMergedCount} selectedButton=${sel} applyToCharacter=${!!applyToChar}`);
                if (chunkIndex === 0) console.log(`[LipSyncDebug] Waiver: CLIENT_RECEIVED first lipsync_data chunkIndex=0 cues=${mouthCues.length}`);
                console.log(`[Waiver Stream] lipsync_data chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} currentMergedCues=${currentMergedCount}`);
                if (chunkIndex === undefined) {
                  // We no longer expect a full replacement from backend; keep accumulated cues
                  if (mouthCues.length > 0) {
                    console.warn(
                      `[Waiver Stream] Ignoring lipsync_data without chunkIndex (backend sent ${mouthCues.length} cues, we have ${currentMergedCount} merged) – using accumulated chunk cues only`,
                    );
                  }
                } else {
                  waiverLipSyncChunksRef.current[chunkIndex] = mouthCues;
                  const keys = Object.keys(waiverLipSyncChunksRef.current).map(Number).sort((a, b) => a - b);
                  const mergedCues = keys.flatMap((k) => waiverLipSyncChunksRef.current[k] ?? []);
                  const merged = { mouthCues: mergedCues };
                  setLipSyncData((prev) => {
                    const next = { ...prev, waiver: merged };
                    if (characterRef?.current && selectedButtonRef.current === "waiver") {
                      console.log(`[LipSyncDebug] Waiver: CHARACTER_VISEMAS_APPLIED cues=${mergedCues.length}`);
                      applyLegacyLipSyncOrSkip(characterRef.current, merged, "stream lipsync_data");
                    }
                    console.log(`[Waiver Stream] Merged chunks [${keys.join(",")}] → ${mergedCues.length} cues`);
                    return next;
                  });
                }
                break;
              }
              case "audio_summary_chunk": {
                // Stop the initial ramp once first thought arrives
                if (waiverInitialRampTimerRef.current) {
                  clearInterval(waiverInitialRampTimerRef.current);
                  waiverInitialRampTimerRef.current = null;
                }
                const payload = event.payload || event; // support {data,...} or raw
                const base64Data = payload?.data || payload;
                const mimeType = payload?.mimeType || payload?.mime_type || "audio/pcm";
                if (typeof base64Data === "string" && base64Data.length > 0) {
                  // Mark first audio chunk arrived (used by button gating)
                  setWaiverData((prev) => ({ ...prev, hasAudioChunk: true }));
                  // Jump target from <=90 to 100 when first audio chunk arrives
                  setStreamProgressTargets((prev) => {
                    return { ...prev, waiver: 100 };
                  });
                  // Simple gating: solo reproducir si el drawer de Waivers está abierto y es el botón activo
                  // IMPORTANT: use refs to avoid stale closure inside long-running stream
                  const canPlayLiveWaiver = isDrawerOpenRef.current && selectedButtonRef.current === "waiver";
                  console.log(`[LipsyncTrace] Waiver client audio_summary_chunk drawerOpen=${isDrawerOpenRef.current} selectedButton=${selectedButtonRef.current} canPlayLive=${canPlayLiveWaiver} creatingPlayer=${!waiverPcmPlayerRef.current}`);
                  if (!canPlayLiveWaiver) {
                    // Drawer cerrado: guardar chunk en buffer para reproducir después
                    waiverBufferedChunksRef.current.push({ data: base64Data, mimeType });
                    break;
                  }
                  // Ensure player exists
                  if (!waiverPcmPlayerRef.current) {
                    // Stop other endpoints' audio so only this endpoint plays (fixes wrong audio at start)
                    stopOtherEndpointPcmPlayersAndService("waiver");
                    waiverPcmPlayerRef.current = new PcmChunkPlayer({
                      sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
                      channels: 1,
                      label: "waiver",
                      vegaCharacterRef: characterRef,
                      onPlaybackStarted: () => {
                        console.log("[LipSyncDebug] Waiver: PCM_PLAYBACK_STARTED");
                        hasAudioBeenPlayed.current.waiver = true;
                        const chunks = waiverLipSyncChunksRef.current;
                        const keys = Object.keys(chunks).map(Number).sort((a, b) => a - b);
                        const mergedCues = keys.length > 0 ? keys.flatMap((k) => chunks[k] ?? []) : (lipSyncDataRef.current?.waiver?.mouthCues ?? []);
                        const waiverStart = performance.now() / 1000;
                        waiverPcmStartTimeRef.current = waiverStart;
                        console.log(`[LipsyncTrace] Waiver client onPlaybackStarted mergedCues=${mergedCues.length} characterRef=${!!characterRef?.current} calling setLipSyncData setPlaybackStartTime setIsTalking`);
                        // Use merged chunks from ref so we don't overwrite with stale lipSyncDataRef (React batching)
                        if (characterRef?.current) {
                          const data = { mouthCues: mergedCues };
                          console.log(`[LipSyncDebug] Waiver: CHARACTER_VISEMAS_APPLIED onPlaybackStarted cues=${mergedCues.length}`);
                          applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
                          characterRef.current.setPlaybackStartTime(waiverStart);
                          characterRef.current.setIsTalking(true);
                          console.log("[Waiver Stream] Playback started - syncing lipsync with", mergedCues.length, "cues");
                        }
                      },
                      onPlaybackEnded: () => {
                        console.log("[LipSyncDebug] Waiver: PCM_PLAYBACK_ENDED");
                        if (characterRef?.current) {
                          characterRef.current.setIsTalking(false);
                        }
                      },
                    });
                    await waiverPcmPlayerRef.current.start();
                    // Reproducir chunks buffered primero (si hay)
                    if (waiverBufferedChunksRef.current.length > 0) {
                      for (const buffered of waiverBufferedChunksRef.current) {
                        waiverPcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
                      }
                      // waiverBufferedChunksRef.current = []; // Limpiar buffer
                    }
                  }
                  waiverPcmPlayerRef.current.addChunkBase64(base64Data, mimeType);
                  // Audio is arriving; stop the loader
                  setWaiverStreamState((prev) => ({ ...prev, isGeneratingAudio: false, isStreamActive: true }));
                }
                break;
              }
              case "audio_summary": {
                const waiverCues = lipSyncDataRef.current?.waiver?.mouthCues?.length ?? 0;
                console.log(`[LipsyncTrace] Waiver client audio_summary received switching to WAV lipSyncCuesAvailable=${waiverCues}`);
                console.log("[LipSyncDebug] Waiver: CLIENT_RECEIVED audio_summary (endpoint has URL)");
                // Stop the initial ramp once first thought arrives
                if (waiverInitialRampTimerRef.current) {
                  clearInterval(waiverInitialRampTimerRef.current);
                  waiverInitialRampTimerRef.current = null;
                }
                // --- TIMING LOG END ---
                if (event.timestamp) {
                  const networkTime = Date.now() - event.timestamp;
                  const seconds = networkTime / 1000;
                  console.log(`[Waiver Audio] Network delivery took ${seconds.toFixed(2)}s.`);
                }
                // IMPORTANT: Do NOT stop live PCM player here.
                // We no longer switch from live PCM to final WAV mid-stream.
                // The final WAV is only used for later replays (e.g. user opens endpoint after everything finished).
                // Clear buffered chunks (final URL is available now)
                // waiverBufferedChunksRef.current = [];
                // Convert the Base64 string to a Blob
                // const waiverAudioBlob = base64ToBlob(event.payload);
                // setSummaryAudios((prev) => ({ ...prev, waiver: waiverAudioBlob })); // Store the audio data in state.
                setSummaryAudios((prev) => ({ ...prev, waiver: event.payload })); // Store the audio URL directly
                // Ensure target is 100 on final audio summary
                // setStreamProgres sTargets((prev) => ({ ...prev, waiver: 100 }));
                // Store the audio URL in the waiver data
                setWaiverData((prev) => ({
                  ...prev,
                  audioUrl: event.payload,
                }));
                // When audio arrives, stop the loader.
                setWaiverStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                break;
              }
              case "share_url":
                setWaiverData((prev) => ({ ...prev, share_url: event.payload }));
                console.log("Received share_url from POC waiver:", event.payload);
                break;
              // If the event type is complete, set the request id
              case "complete":
                // Stop the initial ramp once first thought arrives
                if (waiverInitialRampTimerRef.current) {
                  clearInterval(waiverInitialRampTimerRef.current);
                  waiverInitialRampTimerRef.current = null;
                }
                if (waiverPcmPlayerRef.current) {
                  waiverPcmPlayerRef.current.signalEndOfPcmStream();
                }
                // Set the request id to the waiver data
                setWaiverData((prev) => {
                  // const updatedData = { ...prev, request_id: event.payload.request_id };
                  const alreadyHasAudio = prev?.hasAudioChunk === true;
                  const updatedData = {
                    ...prev,
                    request_id: event.payload.request_id,
                    // If no valid audio_summary_chunk arrived, mark explicitly that there's no audio (fallback)
                    ...(alreadyHasAudio ? {} : { hasAudioChunk: false }),
                  };
                  finalData.request_id = updatedData.request_id; // Update finalData
                  // finalData.fromCache = updatedData.fromCache; // Preserve fromCache flag for sectionData
                  return updatedData;
                });
                // Safety cleanup for audio loader
                setWaiverStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                // As a safeguard, if no audio chunk was detected, still move to 100% on complete.
                setStreamProgressTargets((prev) => ({ ...prev, waiver: 100 }));
                // Clear buffered chunks when stream completes (cleanup)
                // waiverBufferedChunksRef.current = [];
                // Clear thoughts when stream completes
                setThoughtsCommon("");

                break;
              case "error":
                // This handles other generic errors (e.g., chart generation failure)
                // reader.cancel();
                throw new Error(event.payload); // Throws a generic error
              case "api_error":
                // This handles our specific, fatal API error from the backend
                // reader.cancel();
                throw new ApiError(event.payload); // Throws our custom ApiError
            }
          }
          // 5. Handle the end of the stream only after processing buffer (so api_error in last chunk is not skipped)
          if (done) {
            setLoadingWaiver(false); // Stop the loading animation
            setWaiverStreamState((prev) => ({ ...prev, isStreamActive: false })); // Deactivate stream button
            // Cache the final data when the stream completes
            finalData.context = contextAtRequestStart;
            finalData.model = modelToUse; // Store which model generated this response (for UI rendering, NOT polling)
            // Update local state with model + context so AnimatedButton's hasValidDataForCurrentTeam() returns true immediately
            setWaiverData((prev) => ({ ...prev, model: modelToUse, context: contextAtRequestStart }));
            updateSectionData("waiver", finalData);
            if (!finalData.fromCache) {
              void trackEndpointResponseTime(authId, "waiver", (Date.now() - startTime) / 1000, platformId, leagueId);
            }
            break;
          }
        }
      };

      // Read the stream
      await readStream();
    } catch (error) {

      // If stream fails, clear stale thought bubble/sign immediately.
      setThoughtsCommon("");
      console.log("=== ERROR DEBUG ===");
      console.log("Error name:", error.name);
      console.log("Error type:", typeof error);
      console.log("Error instanceof ApiError:", error instanceof ApiError);
      console.log("Error message:", error.message);
      console.log("Full error:", error);
      console.log("==================");
      // Rate limit: open drawer and mark button as failed so retry icon shows, never poll
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingWaiver(false);
        setWaiverStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        return;
      }
      // NEW, SURGICAL LOGIC FOR MOBILE BACKGROUNDING:
      if (Capacitor.isNativePlatform() && error.name !== "AbortError" && !(error instanceof ApiError)) {
        // ANTES de iniciar el polling, verificamos si el error previo fue por no tener draft.
        if (error.message && error.message.trim() === noDraftErrorMessage) {
          console.error("[HomePage fetchWaiverDataStream] Native error is 'no draft'. Aborting recovery poll.", error);
          setErrorMessages((prev) => ({ ...prev, Waiver: noDraftErrorMessage }));
          setErrorMessageNoDraft((prev) => ({ ...prev, waiver: true }));
          // Not call setEndpointErrors(true) to not show the Retry button.
          setLoadingWaiver(false);
          setWaiverData({ waiver_analysis: "", chart_data: [], request_id: null });
          setWaiverStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
          return; // Stop execution here. DO NOT DO POLLING.
        } else {
          // Hand off responsibility to the polling function, just like in the "INCOMPLETE" case.
          pollingIsResponsibleForCleanup = true;
          startPollingForWaiverCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform);
        } // Important: we return here to prevent the generic error handling below from running.
        return;
      }

      // If the request was cancelled by the user or a capacitor error (probably typerror instead of aborterror), clean up the state and stop.
      if (error.name === "AbortError" || error.name === "TypeError") {
        console.log("Waiver stream request was aborted by the user.");
        // We MUST clean up the state, otherwise the UI will be stuck in a loading state.
        // setLoadingWaiver(false);
        // setWaiverData({ waiver_analysis: "", chart_data: [], request_id: null });
        // setWaiverStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
        // setWaiverStreamState((prev) => ({ ...prev, isStreamActive: false, status: "", thought: "" }));
        // Hand off responsibility to the polling function, just like in the "INCOMPLETE" case.
        pollingIsResponsibleForCleanup = true;

        // CRITICAL: Pass UNDEFINED as the signal to decouple polling from UI navigation.
        startPollingForWaiverCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          activeStreamRequests.current.delete(requestKey);
          console.log(`Polling (from abort) for ${requestKey} finished. Lock released.`);
        });
        // No setWaiverData(null). This is crucial to not delete the data
        // that a polling in the background could have completed successfully.
        // if (!activeStreamRequests.current.has(requestKey)) {
        //   setLoadingWaiver(false);
        //   setWaiverStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        // }
        return; // Stop further execution.
      }

      if (error instanceof ApiError) {
        // This is our specific API error. Show the message AND the Retry button.
        console.error("[HomePage fetchWaiverDataStream] Displaying API error:", error);
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Waiver: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, waiver: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("waiver"); }, 1500);
      } else if (error.name !== "TypeError") {
        // Any other error, like a parsing issue or generic stream error.
        // It's not the freeze bug, so show the Retry button.
        console.error("[HomePage fetchWaiverDataStream] Displaying generic stream error:", error);
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Waiver: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, waiver: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("waiver"); }, 1500);
      } else {
        // This is a "TypeError", likely the iOS freeze bug.
        // We only log it for debugging. We DO NOT show an error message to the user,
        // as the polling mechanism will handle the recovery silently.
        console.error("[HomePage fetchWaiverDataStream] Network error (freeze bug) detected. Polling will handle recovery.", error);
      }

      // In ALL error cases, we must stop the loader and clean up the state.
      setLoadingWaiver(false);
      setWaiverData({ waiver_analysis: "", chart_data: [], request_id: null });
      setWaiverStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
    } finally {

      // 4. Clean up the lock.
      // This block will only run if polling did not take over AND the request was not aborted.
      if (!pollingIsResponsibleForCleanup) {
        activeStreamRequests.current.delete(requestKey);
      }
    }
  };

  //--------------------------------------------

  // Function to fetch Start data stream
  const fetchStartDataStream = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any,
    modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE",
    checkCacheOnly = false
  ) => {
    // CAPTURE CONTEXT: Take a snapshot of the user, team, and platform this request is for.
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `start-${platformId}-${leagueId}`;
    const teamKey = `${platformId}-${leagueId}`; // NEW: Create a unique key for the team

    // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
    const currentUserDataPlatform = userDataCache[platformId];

    // 1. Create a unique key for this request.
    const requestKey = `${authId}-start-${platformId}-${leagueId}`;

    // 2. The guardian: if a request for this key is already running, do nothing.
    if (activeStreamRequests.current.has(requestKey)) {
      console.log(`Request for ${requestKey} is already in flight. Skipping new request.`);
      return;
    }

    // This flag controls who is responsible for cleaning up the lock.
    let pollingIsResponsibleForCleanup = false;

    try {
      // It's a new request, so we are no longer in a recovery state for this stream.
      setIsRecoveringFromBackground((prev) => ({ ...prev, start: false }));
      // 3. Register the new request to block duplicates.
      activeStreamRequests.current.add(requestKey);

      // Check if we should skip resetting states when returning from background with valid content
      const hasValidContent = startData?.start_analysis && startData.start_analysis.length > 0;
      const shouldSkipReset = returnedFromBackground && hasValidContent && isDrawerOpen;

      if (!shouldSkipReset) {
        if (!checkCacheOnly) {
          // 1. Set UI state for loading and reset all stream states
          setLoadingStart(true);
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
          setStreamProgress((prev) => ({ ...prev, start: "" })); // Reset progress
          // Reset manual progress target for start (start from 0, then slowly towards 10%)
          setStreamProgressTargets((prev) => ({ ...prev, start: 0 }));
          setStartStreamState({
            status: "",
            thought: "",
            isGeneratingResult: false,
            isStreamActive: false,
            isWaitingForChart: false,
            isGeneratingAudio: false,
            teamKey: teamKey, // NEW: Associate the stream with the team
          });
          setHasStartAnimationCompleted(false);
          setStartData({ start_analysis: "", chart_data: [], request_id: null });
          // updateSectionData("start", null);
          setSummaryAudios((prev) => ({ ...prev, start: null })); // Reset the audio blob
          hasAudioBeenPlayed.current.start = false; // CRITICAL: Reset the played flag for the new analysis
        }
      }

      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        // The 'finally' block will handle the cleanup. Correct.
        return;
      }

      const startTime = Date.now(); // Start timer for initial feedback
      // Smoothly target up to 10% until first thought arrives
      // setStreamProgressTargets((prev) => {
      //   const current = prev.start || 0;
      //   const next = Math.max(current, 10);
      //   return { ...prev, start: next };
      // });

      const stopInitialStartRamp = () => {
        if (startInitialRampTimerRef.current) {
          clearInterval(startInitialRampTimerRef.current);
          startInitialRampTimerRef.current = null;
        }
      };

      if (!checkCacheOnly) {
        // Ensure any previous ramp is stopped before starting a new one
        stopInitialStartRamp();
        startInitialRampTimerRef.current = setInterval(() => {
          setStreamProgressTargets((prev) => {
            const current = prev.start || 0;
            if (current >= 10) {
              stopInitialStartRamp();
              return prev;
            }
            // const next = Math.min(10, current + 0.1); // +0.1% for each 300ms → ~30s from 0% to 10%
            const next = Math.min(10, current + 0.2); // +0.2% for each 300ms → ~15s from 0% to 10%
            // const next = Math.min(10, current + 0.3); // +0.3% for each 300ms → ~10s from 0% to 10%
            //const next = Math.min(10, current + 0.4); // +0.4% for each 300ms → ~7.5s from 0% to 10%
            // const next = Math.min(10, current + 0.5); // +0.5% for each 300ms → ~6s from 0% to 10%
            return { ...prev, start: next };
          });
        }, 300); // 300ms = 0.3s means 0.3s between each increment
      }
      // 3. Call the backend API to get the stream
      // Use modelOverride if provided, otherwise use selectedModel state
      const modelToUse = modelOverride || selectedModel;
      const response = await getStartStream(
        authId,
        currentUserDataPlatform,
        leagueId,
        teamName,
        pocToken,
        accessTokenFromAuth0,
        signal,
        yahooToken,
        aiSettings.audio,
        forceRefresh,
        calculatorSettings,
        modelToUse,
        checkCacheOnly
      );

      // NEW, ROBUST LOGIC:
      // If the response is INCOMPLETE, hand off control to the polling function.
      if (response === "INCOMPLETE") {
        console.log("Start stream received INCOMPLETE. Starting polling.");

        //  transfer responsibility to the polling function:
        // tell the polling function that it will be responsible for the cleanup.
        pollingIsResponsibleForCleanup = true;

        startPollingForStartCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          // When the polling finishes (success or failure), clean up its own lock.
          // THIS IS NO LONGER NEEDED HERE. The polling function will manage its own lifecycle.
          activeStreamRequests.current.delete(requestKey);
          console.log(`Polling for ${requestKey} finished. Lock released.`);
        });
        // We exit, knowing that the polling is in charge.
        return "INCOMPLETE";
      }

      if (response === "NOT_CACHED") {
        console.log("Start data not cached. checkCacheOnly=true returned NOT_CACHED.");
        if (!checkCacheOnly) setLoadingStart(false);
        stopInitialStartRamp();
        return "NOT_CACHED";
      }

      // Don't set setLoadingStart(true) or start ramp timer for cache-only loads (same rationale as Waivers)
      if (checkCacheOnly) {
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
        setStreamProgress((prev) => ({ ...prev, start: "" }));
        setStreamProgressTargets((prev) => ({ ...prev, start: 0 }));

        setStartStreamState({
          status: "",
          thought: "",
          isGeneratingResult: false,
          isStreamActive: false,
          isWaitingForChart: false,
          isGeneratingAudio: false,
          teamKey: teamKey,
        });
        setHasStartAnimationCompleted(false);
        setStartData({ start_analysis: "", chart_data: [], request_id: null });
        setSummaryAudios((prev) => ({ ...prev, start: null }));
        hasAudioBeenPlayed.current.start = false;
      }

      // Type guard: ensure response is Response type, not a different string.
      if (!response) {
        if (response === undefined) return; // Aborted, error handled in catch block
        throw new Error("Streaming request failed");
      } else if (typeof response === "string") {
        // This should not happen now due to the lock, but as a safeguard:
        throw new Error(`Unexpected error response`);
      } else if (!response.body) {
        throw new Error("Streaming request failed - no body");
      }

      // Update credits/freeCredits after successful start execution
      // Premium models consume credits, FREE model consumes freeCredits
      updateCreditsAfterEndpoint("start");

      // 4. Setup stream processing logic
      const reader = response.body.getReader();
      // Initialize the decoder to decode the data chunks
      const decoder = new TextDecoder();
      // Initialize the buffer to store the data chunks
      let buffer = "";
      // finalData accumulates the full response for caching
      let finalData: {
        start_analysis: string;
        chart_data: any[];
        request_id: string | null;
        fromCache?: boolean;
        context?: { platformId: string; leagueId: string; userId: string | undefined };
        model?: "RotoGPT 5.0" | "RotoGPT FREE"; // Track which model generated this response (for UI rendering of UpgradeSubscriptionFree, NOT for polling)
      } = {
        start_analysis: "",
        chart_data: [],
        request_id: null,
      };

      // Read and process the stream until it's complete
      const readStream = async () => {
        startLipSyncChunksRef.current = {};
        while (true) {
          // Read the next chunk of data from the stream
          const { done, value } = await reader.read();
          // Append this chunk to buffer before checking done so we never skip api_error in the last chunk
          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }
          // 6. Process incoming data chunks (chunks of data from the stream)
          // Split the buffer into complete SSE messages
          const parts = buffer.split("\n\n");
          // Keep incomplete message in buffer (to avoid storing the event: end_stream message)
          buffer = parts.pop() || "";

          // Iterate through the parts (each part is a complete SSE message)
          for (const part of parts) {
            // If the part does not start with 'data:', skip it
            if (!part.startsWith("data: ")) continue;

            let event;
            try {
              // Remove the "data: " from the part
              const jsonData = part.substring(6);
              // Parse the part as JSON
              event = JSON.parse(jsonData);
            } catch (e) {
              console.error("Failed to parse JSON part from stream:", e, part);
              continue; // Skip this malformed message part
            }

            // 7. Update state based on the event type using functional updates
            // to prevent stale state issues within the stream's closure.
            switch (event.type) {
              // If the event type is cache_status, set fromCache flag immediately
              case "cache_status":
                const fromCacheStart = Boolean(event?.payload?.fromCache);
                setStartData((prev) => ({
                  ...prev,
                  fromCache: fromCacheStart,
                }));
                finalData.fromCache = fromCacheStart; // Update finalData
                break;
              // If the event type is status, set the stream status
              case "status":
                // Only track slow operation on the very first status message.
                if (event.payload === "Starting conversation with RotoGPT...") {
                  const durationSeconds = (Date.now() - startTime) / 1000;
                  if (durationSeconds > 30) {
                    void trackSlowOperation(authId, "fetch_start", durationSeconds, platformId, leagueId, teamName);
                  }
                }
                // If the stream is already generating result, return the previous state
                setStartStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const statusText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, status: statusText, isStreamActive: true };
                  // return { ...prev, status: statusText };
                });
                break;
              case "thought":
                // Stop the initial ramp once first thought arrives
                if (startInitialRampTimerRef.current) {
                  clearInterval(startInitialRampTimerRef.current);
                  startInitialRampTimerRef.current = null;
                }
                // If the stream is already generating result, return the previous state
                setStartStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const thoughtText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, thought: thoughtText, isStreamActive: true };
                  // return { ...prev, thought: thoughtText };
                });

                //set the thought to the common thoughts state
                setThoughtsCommon(event.payload);
                // Each thought nudges +1% up to 90% (smooth target)
                setStreamProgressTargets((prev) => {
                  const current = prev.start || 0;
                  const next = Math.min(Math.max(current, 10) + 1, 90);
                  return { ...prev, start: next };
                });
                break;
              case "text_chunk":
                // Stop the initial ramp once first thought arrives
                if (startInitialRampTimerRef.current) {
                  clearInterval(startInitialRampTimerRef.current);
                  startInitialRampTimerRef.current = null;
                }
                // Each text chunk nudges +1.5% up to 90% (smooth target)
                setStreamProgressTargets((prev) => {
                  const current = prev.start || 0;
                  const next = Math.min(Math.max(current, 10) + 1.5, 90);
                  return { ...prev, start: next };
                });
                // If the chunk has actual content, switch to result mode
                if (event.payload) {
                  setStartStreamState((prev) => {
                    if (!prev.isGeneratingResult) {
                      clearSignThoughtsOnResultPhase();
                      return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                      // return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false, teamKey };
                    }
                    return { ...prev, isStreamActive: true, isWaitingForChart: false };
                    // return { ...prev, isStreamActive: true, isWaitingForChart: false, teamKey };
                  });
                  // Update the waiver data
                  setStartData((prev) => {
                    const updatedData = {
                      ...prev,
                      context: contextAtRequestStart,
                      start_analysis: (prev.start_analysis || "") + event.payload,
                    };
                    finalData.start_analysis = updatedData.start_analysis; // Update finalData
                    return updatedData;
                  });
                }
                break;
              // case "final_text":
              //   if (event.payload) {
              //     setStartStreamState((prev) => ({
              //       ...prev,
              //       status: "",
              //       thought: "",
              //       isGeneratingResult: true,
              //       isStreamActive: true,
              //       isWaitingForChart: false,
              //     }));
              //     setStartData((prev) => {
              //       const updatedData = {
              //         ...prev,
              //         context: contextAtRequestStart,
              //         start_analysis: event.payload,
              //       };
              //       finalData.start_analysis = updatedData.start_analysis;
              //       return updatedData;
              //     });
              //   }
              //   break;
              case "chart_data":
                setStartStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                    // return { ...prev, status: "", thought: "", isGeneratingResult: true, isWaitingForChart: false };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: false };
                  // return { ...prev, isWaitingForChart: false };
                });
                setStartData((prev) => {
                  const updatedData = {
                    ...prev,
                    context: contextAtRequestStart,
                    // The text already contains the correct [CHART:key] placeholders from the 'text_chunk' event.
                    // We no longer need to add artificial placeholders like [chart_0].
                    // waiver_analysis: (prev.waiver_analysis || "") + `[chart_${prev.chart_data.length}]`,
                    chart_data: [...prev.chart_data, event.payload], // Add the chart data to the waiver data
                  };
                  // finalData.start_analysis = updatedData.start_analysis; // Update finalData
                  finalData.chart_data = updatedData.chart_data;
                  return updatedData;
                });
                break;
              case "tool_call":
                setStartStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: true };
                    // return { ...prev, status: "", thought: "", isGeneratingResult: true, isWaitingForChart: true };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: true };
                  // return { ...prev, isWaitingForChart: true };
                });
                break;

              case "audio_generation_start":
                // Stop the initial ramp once first thought arrives
                if (startInitialRampTimerRef.current) {
                  clearInterval(startInitialRampTimerRef.current);
                  startInitialRampTimerRef.current = null;
                }
                setStartStreamState((prev) => ({ ...prev, isGeneratingAudio: true }));
                break;
              case "lipsync_data": {
                const payload = event.payload || {};
                const chunkIndex = payload.chunkIndex;
                const mouthCues = payload.mouthCues || [];
                const currentMergedCount = Object.keys(startLipSyncChunksRef.current).length
                  ? Object.keys(startLipSyncChunksRef.current).map(Number).sort((a, b) => a - b).flatMap((k) => startLipSyncChunksRef.current[k] ?? []).length
                  : 0;
                const selStart = selectedButtonRef.current;
                const applyStart = characterRef?.current && selStart === "start";
                console.log(`[LipsyncTrace] Start client lipsync_data chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} mergedBefore=${currentMergedCount} selectedButton=${selStart} applyToCharacter=${!!applyStart}`);
                if (chunkIndex === 0) console.log(`[LipSyncDebug] Start: CLIENT_RECEIVED first lipsync_data chunkIndex=0 cues=${mouthCues.length}`);
                console.log(`[Start Stream] lipsync_data chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} currentMergedCues=${currentMergedCount}`);
                if (chunkIndex === undefined) {
                  // We no longer expect a full replacement from backend; keep accumulated cues
                  if (mouthCues.length > 0) {
                    console.warn(
                      `[Start Stream] Ignoring lipsync_data without chunkIndex (backend sent ${mouthCues.length} cues, we have ${currentMergedCount} merged) – using accumulated chunk cues only`,
                    );
                  }
                } else {
                  startLipSyncChunksRef.current[chunkIndex] = mouthCues;
                  const keys = Object.keys(startLipSyncChunksRef.current).map(Number).sort((a, b) => a - b);
                  const mergedCues = keys.flatMap((k) => startLipSyncChunksRef.current[k] ?? []);
                  const merged = { mouthCues: mergedCues };
                  setLipSyncData((prev) => {
                    const next = { ...prev, start: merged };
                    if (characterRef?.current && selectedButtonRef.current === "start") {
                      console.log(`[LipSyncDebug] Start: CHARACTER_VISEMAS_APPLIED cues=${mergedCues.length}`);
                      applyLegacyLipSyncOrSkip(characterRef.current, merged, "stream lipsync_data");
                    }
                    console.log(`[Start Stream] Merged chunks [${keys.join(",")}] → ${mergedCues.length} cues`);
                    return next;
                  });
                }
                break;
              }
              case "audio_summary_chunk": {
                // Stop the initial ramp once first thought arrives
                if (startInitialRampTimerRef.current) {
                  clearInterval(startInitialRampTimerRef.current);
                  startInitialRampTimerRef.current = null;
                }
                const payload = event.payload || event; // support {data,...} or raw
                const base64Data = payload?.data || payload;
                const mimeType = payload?.mimeType || payload?.mime_type || "audio/pcm";
                if (typeof base64Data === "string" && base64Data.length > 0) {
                  // Mark first audio chunk arrived (used by button gating)
                  setStartData((prev) => ({ ...prev, hasAudioChunk: true }));

                  // Jump target from <=90 to 100 when first audio chunk arrives
                  setStreamProgressTargets((prev) => {
                    return { ...prev, start: 100 };
                  });
                  // IMPORTANT: use refs to avoid stale closure inside long-running stream
                  const canPlayLiveStart = isDrawerOpenRef.current && selectedButtonRef.current === "start";
                  console.log(`[LipsyncTrace] Start client audio_summary_chunk drawerOpen=${isDrawerOpenRef.current} selectedButton=${selectedButtonRef.current} canPlayLive=${canPlayLiveStart} creatingPlayer=${!startPcmPlayerRef.current}`);
                  if (!canPlayLiveStart) {
                    // Drawer cerrado: guardar chunk en buffer para reproducir después
                    startBufferedChunksRef.current.push({ data: base64Data, mimeType });
                    break;
                  }
                  // Ensure player exists
                  if (!startPcmPlayerRef.current) {
                    // Stop other endpoints' audio so only this endpoint plays (fixes wrong audio at start)
                    stopOtherEndpointPcmPlayersAndService("start");
                    startPcmPlayerRef.current = new PcmChunkPlayer({
                      sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
                      channels: 1,
                      label: "start",
                      vegaCharacterRef: characterRef,
                      onPlaybackStarted: () => {
                        console.log("[LipSyncDebug] Start: PCM_PLAYBACK_STARTED");
                        hasAudioBeenPlayed.current.start = true;
                        const chunksStart = startLipSyncChunksRef.current;
                        const keysStart = Object.keys(chunksStart).map(Number).sort((a, b) => a - b);
                        const mergedCuesStart = keysStart.length > 0 ? keysStart.flatMap((k) => chunksStart[k] ?? []) : (lipSyncDataRef.current?.start?.mouthCues ?? []);
                        const startStart = performance.now() / 1000;
                        startPcmStartTimeRef.current = startStart;
                        console.log(`[LipsyncTrace] Start client onPlaybackStarted mergedCues=${mergedCuesStart.length} characterRef=${!!characterRef?.current} calling setLipSyncData setPlaybackStartTime setIsTalking`);
                        if (characterRef?.current) {
                          const data = { mouthCues: mergedCuesStart };
                          console.log(`[LipSyncDebug] Start: CHARACTER_VISEMAS_APPLIED onPlaybackStarted cues=${mergedCuesStart.length}`);
                          applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
                          characterRef.current.setPlaybackStartTime(startStart);
                          characterRef.current.setIsTalking(true);
                          console.log("[Start Stream] Playback started - syncing lipsync with", mergedCuesStart.length, "cues");
                        }
                      },
                      onPlaybackEnded: () => {
                        console.log("[LipSyncDebug] Start: PCM_PLAYBACK_ENDED");
                        if (characterRef?.current) {
                          characterRef.current.setIsTalking(false);
                        }
                      },
                    });
                    await startPcmPlayerRef.current.start();
                    // Reproducir chunks buffered primero (si hay)
                    if (startBufferedChunksRef.current.length > 0) {
                      for (const buffered of startBufferedChunksRef.current) {
                        startPcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
                      }
                      // startBufferedChunksRef.current = []; // Limpiar buffer
                    }
                  }
                  startPcmPlayerRef.current.addChunkBase64(base64Data, mimeType);
                  // Audio is arriving; stop the loader
                  setStartStreamState((prev) => ({ ...prev, isGeneratingAudio: false, isStreamActive: true }));
                  // Mark first audio chunk arrived (used by button gating)
                  // setStartData((prev) => ({ ...prev, hasAudioChunk: true }));
                }
                break;
              }
              case "audio_summary": {
                const startCues = lipSyncDataRef.current?.start?.mouthCues?.length ?? 0;
                console.log(`[LipsyncTrace] Start client audio_summary received switching to WAV lipSyncCuesAvailable=${startCues}`);
                console.log("[LipSyncDebug] Start: CLIENT_RECEIVED audio_summary (endpoint has URL)");
                // Stop the initial ramp once first thought arrives
                if (startInitialRampTimerRef.current) {
                  clearInterval(startInitialRampTimerRef.current);
                  startInitialRampTimerRef.current = null;
                }
                // --- TIMING LOG END ---
                if (event.timestamp) {
                  const networkTime = Date.now() - event.timestamp;
                  const seconds = networkTime / 1000;
                  console.log(`[Start Audio] Network delivery took ${seconds.toFixed(2)}s.`);
                }
                // IMPORTANT: Do NOT stop live PCM player here.
                // We no longer switch from live PCM to final WAV mid-stream.
                // The final WAV is only used for later replays (e.g. user opens endpoint after everything finished).
                // Convert the Base64 string to a Blob
                // const startAudioBlob = base64ToBlob(event.payload);
                // setSummaryAudios((prev) => ({ ...prev, start: startAudioBlob })); // Store the audio data in state.
                setSummaryAudios((prev) => ({ ...prev, start: event.payload })); // Store the audio URL directly
                // Ensure target is 100 on final audio summary
                // setStreamProgressTargets((prev) => ({ ...prev, start: 100 }));
                // Store the audio URL in the start data
                setStartData((prev) => ({
                  ...prev,
                  audioUrl: event.payload,
                }));
                // Store the audio data in state.
                // When audio arrives, stop the loader.
                setStartStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                break;
              }
              case "share_url":
                setStartData((prev) => ({ ...prev, share_url: event.payload }));
                console.log("Received share_url from POC start:", event.payload);
                break;
              // If the event type is complete, set the request id
              case "complete":
                // Stop the initial ramp once first thought arrives
                if (startInitialRampTimerRef.current) {
                  clearInterval(startInitialRampTimerRef.current);
                  startInitialRampTimerRef.current = null;
                }
                // No more PCM chunks will arrive; allow PcmChunkPlayer to fire onPlaybackEnded after queue drains (avoids false end during long gaps between chunks)
                if (startPcmPlayerRef.current) {
                  startPcmPlayerRef.current.signalEndOfPcmStream();
                }
                // Set the request id to the waiver data
                setStartData((prev) => {
                  // const updatedData = { ...prev, request_id: event.payload.request_id };
                  const alreadyHasAudio = prev?.hasAudioChunk === true;
                  const updatedData = {
                    ...prev,
                    request_id: event.payload.request_id,
                    // If no valid audio_summary_chunk arrived, mark explicitly that there's no audio (fallback)
                    ...(alreadyHasAudio ? {} : { hasAudioChunk: false }),
                  };
                  finalData.request_id = updatedData.request_id; // Update finalData
                  // finalData.fromCache = updatedData.fromCache; // Preserve fromCache flag for sectionData
                  return updatedData;
                });
                // Safety cleanup for audio loader
                setStartStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                // As a safeguard, if no audio chunk was detected, still move to 100% on complete.
                setStreamProgressTargets((prev) => ({ ...prev, start: 100 }));
                // Clear buffered chunks when stream completes (cleanup)
                // startBufferedChunksRef.current = [];
                // Clear thoughts when stream completes
                setThoughtsCommon("");
                break;
              case "error":
                // This handles other generic errors (e.g., chart generation failure)
                // reader.cancel();
                throw new Error(event.payload); // Throws a generic error
              case "api_error":
                // This handles our specific, fatal API error from the backend
                // reader.cancel();
                throw new ApiError(event.payload); // Throws our custom ApiError
            }
          }
          // 5. Handle the end of the stream only after processing buffer (so api_error in last chunk is not skipped)
          if (done) {
            setLoadingStart(false); // Stop the loading animation
            setStartStreamState((prev) => ({ ...prev, isStreamActive: false })); // Deactivate stream button
            // Cache the final data when the stream completes
            finalData.context = contextAtRequestStart;
            finalData.model = modelToUse; // Store which model generated this response (for UI rendering, NOT polling)
            // Update local state with model + context so AnimatedButton's hasValidDataForCurrentTeam() returns true immediately
            setStartData((prev) => ({ ...prev, model: modelToUse, context: contextAtRequestStart }));
            updateSectionData("start", finalData);
            if (!finalData.fromCache) {
              void trackEndpointResponseTime(authId, "start", (Date.now() - startTime) / 1000, platformId, leagueId);
            }
            break;
          }
        }
      };

      // Read the stream
      await readStream();
    } catch (error) {
      // If stream fails, clear stale thought bubble/sign immediately.
      setThoughtsCommon("");
      console.log("=== ERROR DEBUG ===");
      console.log("Error name:", error.name);
      console.log("Error type:", typeof error);
      console.log("Error instanceof ApiError:", error instanceof ApiError);
      console.log("Error message:", error.message);
      console.log("Full error:", error);
      console.log("==================");

      // Rate limit: open drawer and mark button as failed so retry icon shows, never poll
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingStart(false);
        setStartStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        return;
      }
      // NEW, SURGICAL LOGIC FOR MOBILE BACKGROUNDING:
      if (Capacitor.isNativePlatform() && error.name !== "AbortError" && !(error instanceof ApiError)) {
        // Before starting the polling, check if the previous error was due to no draft.
        if (error.message && error.message.trim() === noDraftErrorMessage) {
          console.error("[HomePage fetchStartDataStream] Native error is 'no draft'. Aborting recovery poll.", error);
          setErrorMessages((prev) => ({ ...prev, Start: noDraftErrorMessage }));
          setErrorMessageNoDraft((prev) => ({ ...prev, start: true }));
          // No call setEndpointErrors(true) to avoid showing the Retry button.
          setLoadingStart(false);
          setStartData({ start_analysis: "", chart_data: [], request_id: null });
          setStartStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
          return; // Stop execution here. DO NOT DO POLLING.
        } else {
          // Hand off responsibility to the polling function, just like in the "INCOMPLETE" case.
          pollingIsResponsibleForCleanup = true;
          startPollingForStartCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform);
        } // Important: we return here to prevent the generic error handling below from running.
        return;
      }

      // If the request was cancelled by the user or a capacitor error (probably typerror instead of aborterror), clean up the state and stop.
      if (error.name === "AbortError" || error.name === "TypeError") {
        console.log("Start stream request was aborted by the user.");
        // We MUST clean up the state, otherwise the UI will be stuck in a loading state.
        // setLoadingStart(false);
        // setStartData({ start_analysis: "", chart_data: [], request_id: null });
        // setStartStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
        // setStartStreamState((prev) => ({ ...prev, isStreamActive: false, status: "", thought: "" }));

        pollingIsResponsibleForCleanup = true;

        startPollingForStartCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          activeStreamRequests.current.delete(requestKey);
          console.log(`Polling (from abort) for ${requestKey} finished. Lock released.`);
        });

        // No setStartData(null). This is crucial to not delete the data
        // that a polling in the background could have completed successfully.
        // if (!activeStreamRequests.current.has(requestKey)) {
        //   setLoadingStart(false);
        //   setStartStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        // }
        return; // Stop further execution.
      }

      if (error instanceof ApiError) {
        // This is our specific API error. Show the message AND the Retry button.
        console.error("[HomePage fetchStartDataStream] Displaying API error:", error);
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Start: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, start: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("start"); }, 1500);
      } else if (error.name !== "TypeError") {
        // Any other error, like a parsing issue or generic stream error.
        // It's not the freeze bug, so show the Retry button.
        console.error("[HomePage fetchStartDataStream] Displaying generic stream error:", error);
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Start: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, start: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("start"); }, 1500);
      } else {
        // This is a "TypeError", likely the iOS freeze bug.
        // We only log it for debugging. We DO NOT show an error message to the user,
        // as the polling mechanism will handle the recovery silently.
        console.error("[HomePage fetchStartDataStream] Network error (freeze bug) detected. Polling will handle recovery.", error);
      }

      // In ALL error cases, we must stop the loader and clean up the state.
      setLoadingStart(false);
      setStartData({ start_analysis: "", chart_data: [], request_id: null });
      setStartStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
    } finally {
      // 4. Clean up the lock.
      // This block will only run if polling did not take over AND the request was not aborted.
      if (!pollingIsResponsibleForCleanup) {
        activeStreamRequests.current.delete(requestKey);
      }
    }
  };

  //--------------------------------------------

  //Function to fetch Rosters data
  const fetchRostersData = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any
  ) => {
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `rosters-${platformId}-${leagueId}`;
    // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
    const currentUserDataPlatform = userDataCache[platformId];

    try {
      setLoadingRoster(true);
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        setLoadingRoster(false);
        return null;
      }

      const startTime = Date.now(); // Start timer for initial feedback
      const data = await getRosters(authId, currentUserDataPlatform, leagueId, teamName, pocToken, accessTokenFromAuth0, signal, yahooToken, forceRefresh, calculatorSettings);

      setRostersData(data);
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
      updateSectionData("rosters", data);
      setLoadingRoster(false);
      // Set shouldPlayAudio to true if selectedButton is "rosters"
      if (selectedButton === "rosters") {
        setShouldPlayAudio(false);
        setTimeout(() => setShouldPlayAudio(true), 10);
      }

      const durationSeconds = (Date.now() - startTime) / 1000;
      if (durationSeconds > 30) {
        void trackSlowOperation(authId, "fetch_rosters", durationSeconds, platformId, leagueId, teamName);
      }
      return data;
    } catch (error) {
      // Rate limit: open drawer and mark button as failed so retry icon shows
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingRoster(false);
        return null;
      }
      if (error.name !== "AbortError") {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Trades: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, rosters: true }));
        }
        setRostersData(null);
        setLoadingRoster(false);
        return null;
      }
      throw error;
    } finally {
      // if (Capacitor.isNativePlatform() && taskId) {
      //   console.log(`Rosters: Finishing background task: ${taskId}`);
      //   BackgroundTask.finish({ taskId });
      // }
    }
  };

  //--------------------------------------------

  // Function to fetch Rankings data
  const fetchRankingsData = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any
  ) => {
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `rankings-${platformId}-${leagueId}`;
    // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
    const currentUserDataPlatform = userDataCache[platformId];

    try {
      setLoadingRankings(true);
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        setLoadingRankings(false);
        return null;
      }

      const startTime = Date.now(); // Start timer for initial feedback
      const data = await getRankings(authId, currentUserDataPlatform, leagueId, teamName, pocToken, accessTokenFromAuth0, signal, yahooToken, forceRefresh, calculatorSettings);
      setRankingsData(data);
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
      updateSectionData("rankings", data);
      setLoadingRankings(false);
      // Set shouldPlayAudio to true if selectedButton is "rankings"
      // if (selectedButton === "rankings") {
      //   setShouldPlayAudio(false);
      //   setTimeout(() => setShouldPlayAudio(true), 10);
      // }

      const durationSeconds = (Date.now() - startTime) / 1000;
      if (durationSeconds > 30) {
        void trackSlowOperation(authId, "fetch_rankings", durationSeconds, platformId, leagueId, teamName);
      }
      return data;
    } catch (error) {
      // Rate limit: open drawer and mark button as failed so retry icon shows
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingRankings(false);
        return null;
      }
      if (error.name !== "AbortError") {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Rankings: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, rankings: true }));
        }
        setRankingsData(null);
        setLoadingRankings(false);
        return null;
      }
      throw error;
    } finally {
      // if (Capacitor.isNativePlatform() && taskId) {
      //   console.log(`Rankings: Finishing background task: ${taskId}`);
      //   BackgroundTask.finish({ taskId });
      // }
    }
  };

  //--------------------------------------------

  // Function to fetch All Rankings data
  const fetchAllRankingsData = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any,
    // rankingsFilter: "all" | "current" | "rookies" = "all"
  ) => {
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `allRankings-${platformId}-${leagueId}`;
    // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
    const currentUserDataPlatform = userDataCache[platformId];

    try {
      setLoadingAllRankings(true);
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        setLoadingAllRankings(false);
        return null;
      }

      const startTime = Date.now(); // Start timer for initial feedback
      const data = await getAllRankings(
        authId,
        currentUserDataPlatform,
        leagueId,
        teamName,
        pocToken,
        accessTokenFromAuth0,
        // rankingsFilter,
        signal,
        yahooToken,
        forceRefresh,
        calculatorSettings,
        platformId,
      );
      setAllRankingsData(data);
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
      updateSectionData("allRankings", data);
      setLoadingAllRankings(false);
      // Set shouldPlayAudio to true if selectedButton is "allRankings"
      // if (selectedButton === "allRankings") {
      //   setShouldPlayAudio(false);
      //   setTimeout(() => setShouldPlayAudio(true), 10);
      // }

      const durationSeconds = (Date.now() - startTime) / 1000;
      if (durationSeconds > 30) {
        void trackSlowOperation(authId, "fetch_all_rankings", durationSeconds, platformId, leagueId, teamName);
      }
      return data;
    } catch (error) {
      // Rate limit: open drawer and mark button as failed so retry icon shows
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingAllRankings(false);
        return null;
      }
      if (error.name !== "AbortError") {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, AllRankings: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, allRankings: true }));
        }
        setAllRankingsData(null);
        setLoadingAllRankings(false);
        return null;
      }
      throw error;
    } finally {
      // if (Capacitor.isNativePlatform() && taskId) {
      //   console.log(`All Rankings: Finishing background task: ${taskId}`);
      //   BackgroundTask.finish({ taskId });
      // }
    }
  };

  //--------------------------------------------

  // Function to fetch Rundown data stream
  const fetchRundownDataStream = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any,
    modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE",
    checkCacheOnly = false
  ) => {
    // CAPTURE CONTEXT: Take a snapshot of the user, team, and platform this request is for.
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `rundown-${platformId}-${leagueId}`;
    const teamKey = `${platformId}-${leagueId}`; // NEW: Create a unique key for the team
    // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
    const currentUserDataPlatform = userDataCache[platformId];

    // 1. Create a unique key for this request.
    const requestKey = `${authId}-rundown-${platformId}-${leagueId}`;

    // 2. The guardian: if a request for this key is already running, do nothing.
    if (activeStreamRequests.current.has(requestKey)) {
      console.log(`Request for ${requestKey} is already in flight. Skipping new request.`);
      return;
    }

    // This flag controls who is responsible for cleaning up the lock.
    let pollingIsResponsibleForCleanup = false;

    try {
      // It's a new request, so we are no longer in a recovery state for this stream.
      setIsRecoveringFromBackground((prev) => ({ ...prev, rundown: false }));
      // 3. Register the new request to block duplicates.
      activeStreamRequests.current.add(requestKey);

      // Check if we should skip resetting states when returning from background with valid content
      const hasValidContent = rundownData?.rundown_analysis && rundownData.rundown_analysis.length > 0;
      const shouldSkipReset = returnedFromBackground && hasValidContent && isDrawerOpen;

      if (!shouldSkipReset) {
        if (!checkCacheOnly) {
          // 1. Set UI state for loading and reset all stream states
          setLoadingRundown(true);
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
          setStreamProgress((prev) => ({ ...prev, rundown: "" })); // Reset progress
          // Reset manual progress target for rundown (start from 0, then slowly towards 10%)
          setStreamProgressTargets((prev) => ({ ...prev, rundown: 0 }));
          setRundownStreamState({
            status: "",
            thought: "",
            isGeneratingResult: false,
            isStreamActive: false,
            isWaitingForChart: false,
            isGeneratingAudio: false, // Reset audio loader state
            teamKey: teamKey, // NEW: Associate the stream with the team
          });
          setHasRundownAnimationCompleted(false);
          setRundownData({ rundown_analysis: "", chart_data: [], request_id: null });
          // updateSectionData("rundown", null);
          setSummaryAudios((prev) => ({ ...prev, rundown: null })); // Reset the audio blob
          hasAudioBeenPlayed.current.rundown = false; // CRITICAL: Reset the played flag for the new analysis
        }
      }

      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        // The 'finally' block will handle the cleanup. Correct.
        return;
      }

      const startTime = Date.now(); // Start timer for initial feedback
      console.log("[PERF] fetchRundownDataStream: Initiating fetch to chat-backend.");

      // Start a gentle initial ramp from 0% → 10% while waiting first thought
      const stopInitialRundownRamp = () => {
        if (rundownInitialRampTimerRef.current) {
          clearInterval(rundownInitialRampTimerRef.current);
          rundownInitialRampTimerRef.current = null;
        }
      };

      if (!checkCacheOnly) {
        // Ensure any previous ramp is stopped before starting a new one
        stopInitialRundownRamp();
        rundownInitialRampTimerRef.current = setInterval(() => {
          setStreamProgressTargets((prev) => {
            const current = prev.rundown || 0;
            if (current >= 10) {
              stopInitialRundownRamp();
              return prev;
            }
            // const next = Math.min(10, current + 0.1); // +0.1% for each 300ms → ~30s from 0% to 10%
            const next = Math.min(10, current + 0.2); // +0.2% for each 300ms → ~15s from 0% to 10%
            // const next = Math.min(10, current + 0.3); // +0.3% for each 300ms → ~10s from 0% to 10%
            //const next = Math.min(10, current + 0.4); // +0.4% for each 300ms → ~7.5s from 0% to 10%
            // const next = Math.min(10, current + 0.5); // +0.5% for each 300ms → ~6s from 0% to 10%
            return { ...prev, rundown: next };
          });
        }, 300); // 300ms = 0.3s means 0.3s between each increment
      }
      // 3. Call the backend API to get the stream
      // Use modelOverride if provided, otherwise use selectedModel state
      const modelToUse = modelOverride || selectedModel;
      const response = await getRundownStream(
        authId,
        currentUserDataPlatform,
        leagueId,
        teamName,
        pocToken,
        accessTokenFromAuth0,
        signal,
        yahooToken,
        aiSettings.audio,
        forceRefresh,
        calculatorSettings,
        modelToUse,
        checkCacheOnly
      );

      // NEW, ROBUST LOGIC:
      // If the response is INCOMPLETE, hand off control to the polling function.
      if (response === "INCOMPLETE") {
        console.log("Rundown stream received INCOMPLETE. Starting polling.");

        //  transfer responsibility to the polling function:
        // tell the polling function that it will be responsible for the cleanup.
        pollingIsResponsibleForCleanup = true;

        startPollingForRundownCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          // When the polling finishes (success or failure), clean up its own lock.
          // THIS IS NO LONGER NEEDED HERE. The polling function will manage its own lifecycle.
          activeStreamRequests.current.delete(requestKey);
          console.log(`Polling for ${requestKey} finished. Lock released.`);
        });
        // We exit, knowing that the polling is in charge.
        return "INCOMPLETE";
      }

      if (response === "NOT_CACHED") {
        console.log("Rundown data not cached. checkCacheOnly=true returned NOT_CACHED.");
        if (!checkCacheOnly) setLoadingRundown(false);
        stopInitialRundownRamp();
        return "NOT_CACHED";
      }

      // Don't set setLoadingRundown(true) or start ramp timer for cache-only loads (same rationale as Waivers)
      if (checkCacheOnly) {
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
        setStreamProgress((prev) => ({ ...prev, rundown: "" }));
        setStreamProgressTargets((prev) => ({ ...prev, rundown: 0 }));

        setRundownStreamState({
          status: "",
          thought: "",
          isGeneratingResult: false,
          isStreamActive: false,
          isWaitingForChart: false,
          isGeneratingAudio: false,
          teamKey: teamKey,
        });
        setHasRundownAnimationCompleted(false);
        setRundownData({ rundown_analysis: "", chart_data: [], request_id: null });
        setSummaryAudios((prev) => ({ ...prev, rundown: null }));
        hasAudioBeenPlayed.current.rundown = false;
      }

      // Type guard: ensure response is Response type, not a different string.
      if (!response) {
        if (response === undefined) return; // Aborted, error handled in catch block
        throw new Error("Streaming request failed");
      } else if (typeof response === "string") {
        // This should not happen now due to the lock, but as a safeguard:
        throw new Error(`Unexpected error response`);
      } else if (!response.body) {
        throw new Error("Streaming request failed - no body");
      }

      // Update credits/freeCredits after successful rundown execution
      // Premium models consume credits, FREE model consumes freeCredits
      updateCreditsAfterEndpoint("rundown");
      // 4. Setup stream processing logic
      const reader = response.body.getReader();
      // Initialize the decoder to decode the data chunks
      const decoder = new TextDecoder();
      // Initialize the buffer to store the data chunks
      let buffer = "";
      // finalData accumulates the full response for caching
      let finalData: {
        rundown_analysis: string;
        chart_data: any[];
        request_id: string | null;
        fromCache?: boolean;
        context?: { platformId: string; leagueId: string; userId: string | undefined };
        model?: "RotoGPT 5.0" | "RotoGPT FREE"; // Track which model generated this response (for UI rendering of UpgradeSubscriptionFree, NOT for polling)
      } = {
        rundown_analysis: "",
        chart_data: [],
        request_id: null,
      };

      // Read and process the stream until it's complete
      const readStream = async () => {
        rundownLipSyncChunksRef.current = {};
        rundownLipsyncReceiptCountRef.current = 0;
        while (true) {
          // Read the next chunk of data from the stream
          const { done, value } = await reader.read();
          // Append this chunk to buffer before checking done so we never skip api_error in the last chunk
          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }
          // 6. Process incoming data chunks (chunks of data from the stream)
          // Split the buffer into complete SSE messages
          const parts = buffer.split("\n\n");
          // Keep incomplete message in buffer (to avoid storing the event: end_stream message)
          buffer = parts.pop() || "";

          // Iterate through the parts (each part is a complete SSE message)
          for (const part of parts) {
            // If the part does not start with 'data:', skip it
            if (!part.startsWith("data: ")) continue;

            let event;
            try {
              // Remove the "data: " from the part
              const jsonData = part.substring(6);
              // Parse the part as JSON
              event = JSON.parse(jsonData);
            } catch (e) {
              console.error("Failed to parse JSON part from stream:", e, part);
              continue; // Skip this malformed message part
            }

            // 7. Update state based on the event type using functional updates
            // to prevent stale state issues within the stream's closure.
            switch (event.type) {
              // If the event type is cache_status, set fromCache flag immediately
              case "cache_status":
                const fromCacheRundown = Boolean(event?.payload?.fromCache);
                setRundownData((prev) => ({
                  ...prev,
                  fromCache: fromCacheRundown,
                }));
                finalData.fromCache = fromCacheRundown; // Update finalData
                break;
              // If the event type is status, set the stream status
              case "status":
                // Only track slow operation on the very first status message.
                if (event.payload === "Starting conversation with RotoGPT...") {
                  const durationSeconds = (Date.now() - startTime) / 1000;
                  if (durationSeconds > 30) {
                    void trackSlowOperation(authId, "fetch_rundown", durationSeconds, platformId, leagueId, teamName);
                  }
                }
                // // Smoothly target up to 10% until first thought arrives
                // setStreamProgressTargets((prev) => {
                //   const current = prev.rundown || 0;
                //   const next = Math.max(current, 10);
                //   return { ...prev, rundown: next };
                // });
                // If the stream is already generating result, return the previous state
                setRundownStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const statusText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, status: statusText, isStreamActive: true };
                  // return { ...prev, status: statusText };
                });
                break;
              case "thought":
                // Stop the initial ramp once first thought arrives
                if (rundownInitialRampTimerRef.current) {
                  clearInterval(rundownInitialRampTimerRef.current);
                  rundownInitialRampTimerRef.current = null;
                }
                // If the stream is already generating result, return the previous state
                setRundownStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const thoughtText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, thought: thoughtText, isStreamActive: true };
                  // return { ...prev, thought: thoughtText };
                });
                //set the thought to the common thoughts state
                setThoughtsCommon(event.payload);
                // Each thought nudges +1% up to 90% (smooth target)
                setStreamProgressTargets((prev) => {
                  const current = prev.rundown || 0;
                  const next = Math.min(Math.max(current, 10) + 1, 90);
                  return { ...prev, rundown: next };
                });
                break;
              case "text_chunk":
                // Stop the initial ramp if still running
                if (rundownInitialRampTimerRef.current) {
                  clearInterval(rundownInitialRampTimerRef.current);
                  rundownInitialRampTimerRef.current = null;
                }
                // Each text chunk nudges +1.5% up to 90% (smooth target)
                setStreamProgressTargets((prev) => {
                  const current = prev.rundown || 0;
                  const next = Math.min(Math.max(current, 10) + 1.5, 90);
                  return { ...prev, rundown: next };
                });
                // If the chunk has actual content, switch to result mode
                if (event.payload) {
                  setRundownStreamState((prev) => {
                    if (!prev.isGeneratingResult) {
                      clearSignThoughtsOnResultPhase();
                      return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                      // return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false, teamKey };
                    }
                    return { ...prev, isStreamActive: true, isWaitingForChart: false };
                    // return { ...prev, isStreamActive: true, isWaitingForChart: false, teamKey };
                  });
                  // Update the waiver data
                  setRundownData((prev) => {
                    const updatedData = {
                      ...prev,
                      context: contextAtRequestStart,
                      rundown_analysis: (prev.rundown_analysis || "") + event.payload,
                    };
                    finalData.rundown_analysis = updatedData.rundown_analysis; // Update finalData
                    return updatedData;
                  });
                }
                break;
              // case "final_text":
              //   if (event.payload) {
              //     setRundownStreamState((prev) => ({
              //       ...prev,
              //       status: "",
              //       thought: "",
              //       isGeneratingResult: true,
              //       isStreamActive: true,
              //       isWaitingForChart: false,
              //     }));
              //     setRundownData((prev) => {
              //       const updatedData = {
              //         ...prev,
              //         context: contextAtRequestStart,
              //         rundown_analysis: event.payload,
              //       };
              //       finalData.rundown_analysis = updatedData.rundown_analysis;
              //       return updatedData;
              //     });
              //   }
              //   break;
              case "chart_data":
                setRundownStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                    // return { ...prev, status: "", thought: "", isGeneratingResult: true, isWaitingForChart: false };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: false };
                  // return { ...prev, isWaitingForChart: false };
                });
                setRundownData((prev) => {
                  const updatedData = {
                    ...prev,
                    context: contextAtRequestStart,
                    // rundown_analysis: (prev.rundown_analysis || "") + `[chart_${prev.chart_data.length}]`, // Add a placeholder to the text for correct positioning in the cache.
                    chart_data: [...prev.chart_data, event.payload], // Add the chart data to the waiver data
                  };
                  // finalData.rundown_analysis = updatedData.rundown_analysis; // Update finalData
                  finalData.chart_data = updatedData.chart_data;
                  return updatedData;
                });
                break;
              case "tool_call":
                setRundownStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: true };
                    // return { ...prev, status: "", thought: "", isGeneratingResult: true, isWaitingForChart: true };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: true };
                  // return { ...prev, isWaitingForChart: true };
                });
                break;

              case "audio_generation_start":
                // Stop the initial ramp if still running
                if (rundownInitialRampTimerRef.current) {
                  clearInterval(rundownInitialRampTimerRef.current);
                  rundownInitialRampTimerRef.current = null;
                }
                setRundownStreamState((prev) => ({ ...prev, isGeneratingAudio: true }));
                break;
              case "lipsync_data": {
                const payload = event.payload || {};
                const chunkIndex = payload.chunkIndex;
                const mouthCues = payload.mouthCues || [];
                const receiptNum = ++rundownLipsyncReceiptCountRef.current;
                const currentMergedCount = Object.keys(rundownLipSyncChunksRef.current).length
                  ? Object.keys(rundownLipSyncChunksRef.current).map(Number).sort((a, b) => a - b).flatMap((k) => rundownLipSyncChunksRef.current[k] ?? []).length
                  : 0;
                const selRd = selectedButtonRef.current;
                const applyRd = characterRef?.current && selRd === "rundown";
                console.log(`[LipsyncTrace] Rundown client lipsync_data chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} mergedBefore=${currentMergedCount} selectedButton=${selRd} applyToCharacter=${!!applyRd}`);
                console.log(`[Rundown Stream] lipsync_data #${receiptNum} chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} currentMergedCues=${currentMergedCount}`);
                if (chunkIndex === 0) {
                  console.log(`[LipSyncDebug] Rundown: CLIENT_RECEIVED first lipsync_data chunkIndex=0 cues=${mouthCues.length}`);
                  console.log('[Rundown Stream] >>> CHUNK 0 received (0–5s coverage)');
                }
                if (chunkIndex === undefined) {
                  // We no longer expect a full replacement from backend; keep accumulated cues
                  if (mouthCues.length > 0) {
                    console.warn(
                      `[Rundown Stream] Ignoring lipsync_data without chunkIndex (backend sent ${mouthCues.length} cues, we have ${currentMergedCount} merged) – using accumulated chunk cues only`,
                    );
                  }
                } else {
                  rundownLipSyncChunksRef.current[chunkIndex] = mouthCues;
                  const keys = Object.keys(rundownLipSyncChunksRef.current).map(Number).sort((a, b) => a - b);
                  const mergedCues = keys.flatMap((k) => rundownLipSyncChunksRef.current[k] ?? []);
                  const merged = { mouthCues: mergedCues };
                  setLipSyncData((prev) => {
                    const next = { ...prev, rundown: merged };
                    if (characterRef?.current && selectedButtonRef.current === "rundown") {
                      console.log(`[LipSyncDebug] Rundown: CHARACTER_VISEMAS_APPLIED cues=${mergedCues.length}`);
                      applyLegacyLipSyncOrSkip(characterRef.current, merged, "stream lipsync_data");
                    }
                    console.log(`[Rundown Stream] Merged chunks [${keys.join(",")}] → ${mergedCues.length} cues`);
                    return next;
                  });
                }
                break;
              }
              case "audio_summary_chunk": {
                // Stop the initial ramp if still running
                if (rundownInitialRampTimerRef.current) {
                  clearInterval(rundownInitialRampTimerRef.current);
                  rundownInitialRampTimerRef.current = null;
                }
                const payload = event.payload || event; // support {data,...} or raw
                const base64Data = payload?.data || payload;
                const mimeType = payload?.mimeType || payload?.mime_type || "audio/pcm";
                if (typeof base64Data === "string" && base64Data.length > 0) {
                  // Mark first audio chunk arrived (used by button gating)
                  setRundownData((prev) => ({ ...prev, hasAudioChunk: true }));
                  // Jump target from <=90 to 100 when first audio chunk arrives
                  setStreamProgressTargets((prev) => {
                    return { ...prev, rundown: 100 };
                  });
                  // IMPORTANT: use refs to avoid stale closure inside long-running stream
                  const canPlayLiveRundown = isDrawerOpenRef.current && selectedButtonRef.current === "rundown";
                  console.log(`[LipsyncTrace] Rundown client audio_summary_chunk drawerOpen=${isDrawerOpenRef.current} selectedButton=${selectedButtonRef.current} canPlayLive=${canPlayLiveRundown} creatingPlayer=${!rundownPcmPlayerRef.current}`);
                  if (!canPlayLiveRundown) {
                    // Drawer cerrado: guardar chunk en buffer para reproducir después
                    rundownBufferedChunksRef.current.push({ data: base64Data, mimeType });
                    break;
                  }
                  // Ensure player exists
                  if (!rundownPcmPlayerRef.current) {
                    // Stop other endpoints' audio so only this endpoint plays (fixes wrong audio at start)
                    stopOtherEndpointPcmPlayersAndService("rundown");
                    rundownPcmPlayerRef.current = new PcmChunkPlayer({
                      sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
                      channels: 1,
                      label: "rundown",
                      vegaCharacterRef: characterRef,
                      onPlaybackStarted: () => {
                        console.log("[LipSyncDebug] Rundown: PCM_PLAYBACK_STARTED");
                        hasAudioBeenPlayed.current.rundown = true;
                        const chunksRd = rundownLipSyncChunksRef.current;
                        const keysRd = Object.keys(chunksRd).map(Number).sort((a, b) => a - b);
                        const mergedCuesRd = keysRd.length > 0 ? keysRd.flatMap((k) => chunksRd[k] ?? []) : (lipSyncDataRef.current?.rundown?.mouthCues ?? []);
                        const rdStart = performance.now() / 1000;
                        rundownPcmStartTimeRef.current = rdStart;
                        console.log(`[LipsyncTrace] Rundown client onPlaybackStarted mergedCues=${mergedCuesRd.length} characterRef=${!!characterRef?.current} calling setLipSyncData setPlaybackStartTime setIsTalking`);
                        if (characterRef?.current) {
                          const data = { mouthCues: mergedCuesRd };
                          console.log(`[LipSyncDebug] Rundown: CHARACTER_VISEMAS_APPLIED onPlaybackStarted cues=${mergedCuesRd.length}`);
                          applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
                          characterRef.current.setPlaybackStartTime(rdStart);
                          characterRef.current.setIsTalking(true);
                          console.log("[Rundown Stream] Playback started - syncing lipsync with", mergedCuesRd.length, "cues");
                        }
                      },
                      onPlaybackEnded: () => {
                        console.log("[LipSyncDebug] Rundown: PCM_PLAYBACK_ENDED");
                        if (characterRef?.current) {
                          characterRef.current.setIsTalking(false);
                        }
                      },
                    });
                    await rundownPcmPlayerRef.current.start();
                    // Reproducir chunks buffered primero (si hay)
                    if (rundownBufferedChunksRef.current.length > 0) {
                      for (const buffered of rundownBufferedChunksRef.current) {
                        rundownPcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
                      }
                      // rundownBufferedChunksRef.current = []; // Limpiar buffer
                    }
                  }
                  rundownPcmPlayerRef.current.addChunkBase64(base64Data, mimeType);
                  // Audio is arriving; stop the loader
                  setRundownStreamState((prev) => ({ ...prev, isGeneratingAudio: false, isStreamActive: true }));
                  // Mark first audio chunk arrived (used by button gating)
                  //  setRundownData((prev) => ({ ...prev, hasAudioChunk: true }));
                }
                break;
              }
              case "audio_summary": {
                const rdCues = lipSyncDataRef.current?.rundown?.mouthCues?.length ?? 0;
                console.log(`[LipsyncTrace] Rundown client audio_summary received switching to WAV lipSyncCuesAvailable=${rdCues}`);
                console.log("[LipSyncDebug] Rundown: CLIENT_RECEIVED audio_summary (endpoint has URL)");
                console.log(`[Rundown Stream] Received audio_summary event:`, event.payload?.substring?.(0, 100) || event.payload);
                // Stop the initial ramp if still running
                if (rundownInitialRampTimerRef.current) {
                  clearInterval(rundownInitialRampTimerRef.current);
                  rundownInitialRampTimerRef.current = null;
                }
                // --- TIMING LOG END ---
                if (event.timestamp) {
                  const networkTime = Date.now() - event.timestamp;
                  const seconds = networkTime / 1000;
                  console.log(`[Rundown Audio] Network delivery took ${seconds.toFixed(2)}s.`);
                }
                // IMPORTANT: Do NOT stop live PCM player here.
                // We no longer switch from live PCM to final WAV mid-stream.
                // The final WAV is only used for later replays (e.g. user opens endpoint after everything finished).
                // Convert the Base64 string to a Blob
                // const rundownAudioBlob = base64ToBlob(event.payload);
                // setSummaryAudios((prev) => ({ ...prev, rundown: rundownAudioBlob })); // Store the audio data in state.
                setSummaryAudios((prev) => ({ ...prev, rundown: event.payload })); // Store the audio URL directly
                // Ensure target is 100 on final audio summary
                // setStreamProgressTargets((prev) => ({ ...prev, rundown: 100 }));

                // Store the audio URL in the rundown data
                setRundownData((prev) => ({
                  ...prev,
                  audioUrl: event.payload,
                }));
                // When audio arrives, stop the loader.
                setRundownStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                break;
              }
              case "share_url":
                setRundownData((prev) => ({ ...prev, share_url: event.payload }));
                console.log("Received share_url from POC rundown:", event.payload);
                break;
              // If the event type is complete, set the request id
              case "complete":
                // Stop the initial ramp if still running
                if (rundownInitialRampTimerRef.current) {
                  clearInterval(rundownInitialRampTimerRef.current);
                  rundownInitialRampTimerRef.current = null;
                }
                if (rundownPcmPlayerRef.current) {
                  rundownPcmPlayerRef.current.signalEndOfPcmStream();
                }
                // Set the request id to the waiver data
                setRundownData((prev) => {
                  // const updatedData = { ...prev, request_id: event.payload.request_id };
                  const alreadyHasAudio = prev?.hasAudioChunk === true;
                  const updatedData = {
                    ...prev,
                    request_id: event.payload.request_id,
                    // If no valid audio_summary_chunk arrived, mark explicitly that there's no audio (fallback)
                    ...(alreadyHasAudio ? {} : { hasAudioChunk: false }),
                  };
                  finalData.request_id = updatedData.request_id; // Update finalData
                  // finalData.fromCache = updatedData.fromCache; // Preserve fromCache flag for sectionData
                  return updatedData;
                });
                // Safety cleanup for audio loader
                setRundownStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                // As a safeguard, if no audio chunk was detected, still move to 100% on complete.
                setStreamProgressTargets((prev) => ({ ...prev, rundown: 100 }));
                // Clear buffered chunks when stream completes (cleanup)
                // rundownBufferedChunksRef.current = [];
                // Clear thoughts when stream completes
                setThoughtsCommon("");
                break;
              case "error":
                // This handles other generic errors (e.g., chart generation failure)
                // reader.cancel();
                throw new Error(event.payload); // Throws a generic error
              case "api_error":
                // This handles our specific, fatal API error from the backend
                // reader.cancel();
                throw new ApiError(event.payload); // Throws our custom ApiError
            }
          }
          // 5. Handle the end of the stream only after processing buffer (so api_error in last chunk is not skipped)
          if (done) {
            const duration = Date.now() - startTime;
            const totalSeconds = Math.floor(duration / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            const formattedTime = minutes > 0 ? `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")} m` : `${String(seconds).padStart(2, "0")} s`;
            console.log(`[PERF] fetchRundownDataStream: Finished receiving stream from chat-backend in ${formattedTime}.`);
            if (!finalData.fromCache) {
              void trackEndpointResponseTime(authId, "team_analysis", (Date.now() - startTime) / 1000, platformId, leagueId);
            }
            setLoadingRundown(false); // Stop the loading animation
            setRundownStreamState((prev) => ({ ...prev, isStreamActive: false })); // Deactivate stream button
            // Cache the final data when the stream completes
            finalData.context = contextAtRequestStart;
            finalData.model = modelToUse; // Store which model generated this response (for UI rendering, NOT polling)
            // Update local state with model + context so AnimatedButton's hasValidDataForCurrentTeam() returns true immediately
            setRundownData((prev) => ({ ...prev, model: modelToUse, context: contextAtRequestStart }));
            updateSectionData("rundown", finalData);
            break;
          }
        }
      };

      // Read the stream
      await readStream();
    } catch (error) {
      // If stream fails, clear stale thought bubble/sign immediately.
      setThoughtsCommon("");
      console.log("=== ERROR DEBUG ===");
      console.log("Error name:", error.name);
      console.log("Error type:", typeof error);
      console.log("Error instanceof ApiError:", error instanceof ApiError);
      console.log("Error message:", error.message);
      console.log("Full error:", error);
      console.log("==================");

      // Rate limit: open drawer and mark button as failed so retry icon shows, never poll
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingRundown(false);
        setRundownStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        return;
      }
      // NEW, SURGICAL LOGIC FOR MOBILE BACKGROUNDING:
      if (Capacitor.isNativePlatform() && error.name !== "AbortError" && !(error instanceof ApiError)) {
        // Before starting the polling, check if the previous error was due to no draft.
        if (error.message && error.message.trim() === noDraftErrorMessage) {
          console.error("[HomePage fetchRundownDataStream] Native error is 'no draft'. Aborting recovery poll.", error);
          setErrorMessages((prev) => ({ ...prev, Rundown: noDraftErrorMessage }));
          setErrorMessageNoDraft((prev) => ({ ...prev, rundown: true }));
          // No call setEndpointErrors(true) to avoid showing the Retry button.
          setLoadingRundown(false);
          setRundownData({ rundown_analysis: "", chart_data: [], request_id: null });
          setRundownStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
          return; // Stop execution here. DO NOT DO POLLING.
        } else {
          // Hand off responsibility to the polling function, just like in the "INCOMPLETE" case.
          pollingIsResponsibleForCleanup = true;
          startPollingForRundownCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform);
        } // Important: we return here to prevent the generic error handling below from running.
        return;
      }

      // If the request was cancelled by the user or a capacitor error (probably typerror instead of aborterror), clean up the state and stop.
      if (error.name === "AbortError" || error.name === "TypeError") {
        console.log("Rundown stream request was aborted by the user.");
        // We MUST clean up the state, otherwise the UI will be stuck in a loading state
        // setLoadingRundown(false);
        // setRundownData({ rundown_analysis: "", chart_data: [], request_id: null });
        // setRundownStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
        // setRundownStreamState((prev) => ({ ...prev, isStreamActive: false, status: "", thought: "" }));
        pollingIsResponsibleForCleanup = true;

        startPollingForRundownCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          activeStreamRequests.current.delete(requestKey);
          console.log(`Polling (from abort) for ${requestKey} finished. Lock released.`);
        });

        // No setRundownData(null). This is crucial to not delete the data
        // that a polling in the background could have completed successfully.
        // if (!activeStreamRequests.current.has(requestKey)) {
        //   setLoadingRundown(false);
        //   setRundownStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        // }
        return; // Stop further execution.
      }

      if (error instanceof ApiError) {
        // This is our specific API error. Show the message AND the Retry button.
        console.error("[HomePage fetchRundownDataStream] Displaying API error:", error);
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Rundown: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, rundown: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("rundown"); }, 1500);
      } else if (error.name !== "TypeError") {
        // Any other error, like a parsing issue or generic stream error.
        // It's not the freeze bug, so show the Retry button.
        console.error("[HomePage fetchRundownDataStream] Displaying generic stream error:", error);
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, Rundown: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, rundown: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("rundown"); }, 1500);
      } else {
        // This is a "TypeError", likely the iOS freeze bug.
        // We only log it for debugging. We DO NOT show an error message to the user,
        // as the polling mechanism will handle the recovery silently.
        console.error("[HomePage fetchRundownDataStream] Network error (freeze bug) detected. Polling will handle recovery.", error);
      }

      // In ALL error cases, we must stop the loader and clean up the state.
      setLoadingRundown(false);
      setRundownData({ rundown_analysis: "", chart_data: [], request_id: null });
      setRundownStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
    } finally {
      // 4. Clean up the lock.
      // This block will only run if polling did not take over AND the request was not aborted.
      if (!pollingIsResponsibleForCleanup) {
        activeStreamRequests.current.delete(requestKey);
      }
    }
  };

  //--------------------------------------------

  // Function to fetch Draft Advice data stream (mirrors fetchRundownDataStream)
  const fetchDraftAdviceDataStream = async (
    leagueId: string,
    platformId: string,
    teamName: string,
    signal: AbortSignal,
    yahooToken?: string,
    forceRefresh = false,
    calculatorSettings?: any,
    modelOverride?: "RotoGPT 5.0" | "RotoGPT FREE",
    checkCacheOnly = false
  ) => {
    const contextAtRequestStart = { platformId, leagueId, userId: authId };
    const errorKey = `DraftAdvice-${platformId}-${leagueId}`;
    const teamKey = `${platformId}-${leagueId}`;
    const currentUserDataPlatform = userDataCache[platformId];
    const requestKey = `${authId}-DraftAdvice-${platformId}-${leagueId}`;

    if (activeStreamRequests.current.has(requestKey)) {
      console.log(`Request for ${requestKey} is already in flight. Skipping new request.`);
      return;
    }

    let pollingIsResponsibleForCleanup = false;

    // Forced refresh while the Draft Advice drawer is already open → block audio/lipsync
    // until the stream finishes (LoaderFacts overlay is showing the whole time).
    if (forceRefresh === true && isDrawerOpenRef.current && selectedButtonRef.current === "DraftAdvice") {
      setSignThoughtsDrawerSource("DraftAdvice");
      draftAdviceAudioBlockedForForcedRefreshRef.current = true;
      DraftAdviceBufferedChunksRef.current = []; // clear stale buffer from any prior run
      if (DraftAdvicePcmPlayerRef.current) {
        DraftAdvicePcmPlayerRef.current.stop();
        DraftAdvicePcmPlayerRef.current = null;
      }
      // Ensure previous endpoint audio cannot keep the talking state while refresh loader is active
      if (audioService.getNowPlayingPriority() === AudioPriority.HIGH) {
        audioService.stopCurrentAudio();
      }
      if (characterRef?.current) {
        characterRef.current.setIsTalking(false);
      }
    }

    try {
      setIsRecoveringFromBackground((prev) => ({ ...prev, DraftAdvice: false }));
      activeStreamRequests.current.add(requestKey);

      const hasValidContent = DraftAdviceData?.rundown_analysis && DraftAdviceData.rundown_analysis.length > 0;
      const shouldSkipReset = returnedFromBackground && hasValidContent && isDrawerOpen;

      if (!shouldSkipReset) {
        if (!checkCacheOnly) {
          setLoadingDraftAdvice(true);
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
          setStreamProgress((prev) => ({ ...prev, DraftAdvice: "" }));
          setStreamProgressTargets((prev) => ({ ...prev, DraftAdvice: 0 }));
          setDraftAdviceStreamState({
            status: "",
            thought: "",
            isGeneratingResult: false,
            isStreamActive: false,
            isWaitingForChart: false,
            isGeneratingAudio: false,
            teamKey,
          });
          setHasDraftAdviceAnimationCompleted(false);
          setDraftAdviceData({ rundown_analysis: "", chart_data: [], request_id: null });
          setSummaryAudios((prev) => ({ ...prev, DraftAdvice: null }));
          hasAudioBeenPlayed.current.DraftAdvice = false;
        }
      }

      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) return;

      const startTime = Date.now();
      console.log("[PERF] fetchDraftAdviceDataStream: Initiating fetch to chat-backend.");

      const stopInitialDraftAdviceRamp = () => {
        if (DraftAdviceInitialRampTimerRef.current) {
          clearInterval(DraftAdviceInitialRampTimerRef.current);
          DraftAdviceInitialRampTimerRef.current = null;
        }
      };

      if (!checkCacheOnly) {
        stopInitialDraftAdviceRamp();
        DraftAdviceInitialRampTimerRef.current = setInterval(() => {
          setStreamProgressTargets((prev) => {
            const current = prev.DraftAdvice || 0;
            if (current >= 10) {
              stopInitialDraftAdviceRamp();
              return prev;
            }
            return { ...prev, DraftAdvice: Math.min(10, current + 0.2) };
          });
        }, 300);
      }

      const modelToUse = modelOverride || selectedModel;
      const isDraftAdviceFooterForcedRefreshContext =
        forceRefresh === true &&
        isDrawerOpenRef.current &&
        selectedButtonRef.current === "DraftAdvice";

      const response = await getDraftAdviceStream(
        authId,
        currentUserDataPlatform,
        leagueId,
        teamName,
        pocToken,
        accessTokenFromAuth0,
        signal,
        yahooToken,
        aiSettings.audio,
        forceRefresh,
        calculatorSettings,
        modelToUse,
        checkCacheOnly,
        isDraftAdviceFooterForcedRefreshContext
      );

      if (response === "INCOMPLETE") {
        console.log("Draft advice stream received INCOMPLETE. Starting polling.");
        pollingIsResponsibleForCleanup = true;
        startPollingForDraftAdviceCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          activeStreamRequests.current.delete(requestKey);
          console.log(`Polling for ${requestKey} finished. Lock released.`);
        });
        return "INCOMPLETE";
      }

      if (response === "NOT_CACHED") {
        console.log("Draft Advice data not cached. checkCacheOnly=true returned NOT_CACHED.");
        if (!checkCacheOnly) setLoadingDraftAdvice(false);
        stopInitialDraftAdviceRamp();
        return "NOT_CACHED";
      }

      // Don't set setLoadingDraftAdvice(true) or start ramp timer for cache-only loads (same rationale as Waivers)
      if (checkCacheOnly) {
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: false }));
        setStreamProgress((prev) => ({ ...prev, DraftAdvice: "" }));
        setStreamProgressTargets((prev) => ({ ...prev, DraftAdvice: 0 }));

        setDraftAdviceStreamState({
          status: "",
          thought: "",
          isGeneratingResult: false,
          isStreamActive: false,
          isWaitingForChart: false,
          isGeneratingAudio: false,
          teamKey: teamKey,
        });
        setHasDraftAdviceAnimationCompleted(false);
        setDraftAdviceData({ DraftAdvice_analysis: "", chart_data: [], request_id: null });
        setSummaryAudios((prev) => ({ ...prev, DraftAdvice: null }));
        hasAudioBeenPlayed.current.DraftAdvice = false;
      }

      if (!response || typeof response === "string") {
        if (response === undefined) return;
        throw new Error("Streaming request failed");
      }
      if (!response.body) {
        throw new Error("Streaming request failed - no body");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let shouldSkipDraftAdviceCreditRefresh = false;
      let draftAdviceCreditRefreshTriggered = false;
      const normalizeNonLlmGenerated = (value: unknown): boolean | null => {
        if (typeof value === "boolean") return value;
        if (typeof value === "number") {
          if (value === 1) return true;
          if (value === 0) return false;
        }
        if (typeof value === "string") {
          const normalized = value.trim().toLowerCase();
          if (normalized === "true" || normalized === "1") return true;
          if (normalized === "false" || normalized === "0") return false;
        }
        return null;
      };
      const triggerDraftAdviceCreditsRefreshIfNeeded = () => {
        if (draftAdviceCreditRefreshTriggered || shouldSkipDraftAdviceCreditRefresh) return;
        draftAdviceCreditRefreshTriggered = true;
        void updateCreditsAfterEndpoint("draft_advice");
      };
      let finalData: {
        rundown_analysis: string;
        chart_data: any[];
        request_id: string | null;
        fromCache?: boolean;
        non_llm_generated?: boolean;
        context?: { platformId: string; leagueId: string; userId: string | undefined };
        model?: "RotoGPT 5.0" | "RotoGPT FREE";
      } = {
        rundown_analysis: "",
        chart_data: [],
        request_id: null,
      };

      const readStream = async () => {
        DraftAdviceLipSyncChunksRef.current = {};
        DraftAdviceLipsyncReceiptCountRef.current = 0;
        while (true) {
          const { done, value } = await reader.read();
          // Append this chunk to buffer before checking done so we never skip api_error in the last chunk
          if (value) {
            buffer += decoder.decode(value, { stream: true });
          }
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";

          for (const part of parts) {
            if (!part.startsWith("data: ")) continue;
            let event: any;
            try {
              event = JSON.parse(part.substring(6));
            } catch (e) {
              console.error("Failed to parse JSON part from draft advice stream:", e, part);
              continue;
            }

            switch (event.type) {
              case "cache_status":
                const fromCacheDraftAdvice = Boolean(event?.payload?.fromCache);
                setDraftAdviceData((prev) => ({ ...prev, fromCache: fromCacheDraftAdvice }));
                finalData.fromCache = fromCacheDraftAdvice;
                break;
              case "status":
                if (event.payload === "Starting conversation with RotoGPT...") {
                  const durationSeconds = (Date.now() - startTime) / 1000;
                  if (durationSeconds > 30) {
                    void trackSlowOperation(authId, "fetch_draft_advice", durationSeconds, platformId, leagueId, teamName);
                  }
                }
                setDraftAdviceStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const statusText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, status: statusText, isStreamActive: true };
                });
                break;
              case "thought":
                triggerDraftAdviceCreditsRefreshIfNeeded();
                if (DraftAdviceInitialRampTimerRef.current) {
                  clearInterval(DraftAdviceInitialRampTimerRef.current);
                  DraftAdviceInitialRampTimerRef.current = null;
                }
                setDraftAdviceStreamState((prev) => {
                  if (prev.isGeneratingResult) return prev;
                  const thoughtText = typeof event.payload === "string" ? event.payload : JSON.stringify(event.payload);
                  return { ...prev, thought: thoughtText, isStreamActive: true };
                });
                setThoughtsCommon(event.payload);
                if (isDrawerOpenRef.current && selectedButtonRef.current === "DraftAdvice") {
                  setSignThoughtsDrawerSource("DraftAdvice");
                }
                setStreamProgressTargets((prev) => {
                  const current = prev.DraftAdvice || 0;
                  return { ...prev, DraftAdvice: Math.min(Math.max(current, 10) + 1, 90) };
                });
                break;
              case "text_chunk":
                triggerDraftAdviceCreditsRefreshIfNeeded();
                if (DraftAdviceInitialRampTimerRef.current) {
                  clearInterval(DraftAdviceInitialRampTimerRef.current);
                  DraftAdviceInitialRampTimerRef.current = null;
                }
                setStreamProgressTargets((prev) => {
                  const current = prev.DraftAdvice || 0;
                  return { ...prev, DraftAdvice: Math.min(Math.max(current, 10) + 1.5, 90) };
                });
                if (event.payload) {
                  setDraftAdviceStreamState((prev) => {
                    if (!prev.isGeneratingResult) {
                      clearSignThoughtsOnResultPhase();
                      return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                    }
                    return { ...prev, isStreamActive: true, isWaitingForChart: false };
                  });
                  setDraftAdviceData((prev) => {
                    const updatedData = {
                      ...prev,
                      context: contextAtRequestStart,
                      rundown_analysis: (prev.rundown_analysis || "") + event.payload,
                    };
                    finalData.rundown_analysis = updatedData.rundown_analysis;
                    return updatedData;
                  });
                }
                break;
              // case "final_text":
              //   if (event.payload) {
              //     setDraftAdviceStreamState((prev) => ({
              //       ...prev,
              //       status: "",
              //       thought: "",
              //       isGeneratingResult: true,
              //       isStreamActive: true,
              //       isWaitingForChart: false,
              //     }));
              //     setDraftAdviceData((prev) => {
              //       const updatedData = {
              //         ...prev,
              //         context: contextAtRequestStart,
              //         rundown_analysis: event.payload,
              //       };
              //       finalData.rundown_analysis = updatedData.rundown_analysis;
              //       return updatedData;
              //     });
              //   }
              //   break;
              case "chart_data":
                setDraftAdviceStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: false };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: false };
                });
                setDraftAdviceData((prev) => {
                  const updatedData = {
                    ...prev,
                    context: contextAtRequestStart,
                    chart_data: [...prev.chart_data, event.payload],
                  };
                  finalData.chart_data = updatedData.chart_data;
                  return updatedData;
                });
                break;
              case "tool_call":
                triggerDraftAdviceCreditsRefreshIfNeeded();
                setDraftAdviceStreamState((prev) => {
                  if (!prev.isGeneratingResult) {
                    clearSignThoughtsOnResultPhase();
                    return { ...prev, status: "", thought: "", isGeneratingResult: true, isStreamActive: true, isWaitingForChart: true };
                  }
                  return { ...prev, isStreamActive: true, isWaitingForChart: true };
                });
                break;
              case "non_llm_generated": {
                const nonLlmGenerated = normalizeNonLlmGenerated(event.payload);
                if (nonLlmGenerated !== null) {
                  shouldSkipDraftAdviceCreditRefresh = nonLlmGenerated;
                  if (!nonLlmGenerated) {
                    triggerDraftAdviceCreditsRefreshIfNeeded();
                  }
                  setDraftAdviceData((prev) => ({ ...prev, non_llm_generated: nonLlmGenerated }));
                  finalData.non_llm_generated = nonLlmGenerated;
                }
                break;
              }
              case "audio_generation_start":
                if (DraftAdviceInitialRampTimerRef.current) {
                  clearInterval(DraftAdviceInitialRampTimerRef.current);
                  DraftAdviceInitialRampTimerRef.current = null;
                }
                setDraftAdviceStreamState((prev) => ({ ...prev, isGeneratingAudio: true }));
                break;
              case "lipsync_data": {
                const payload = event.payload || {};
                const chunkIndex = payload.chunkIndex;
                const mouthCues = payload.mouthCues || [];
                const receiptNum = ++DraftAdviceLipsyncReceiptCountRef.current;
                const currentMergedCount = Object.keys(DraftAdviceLipSyncChunksRef.current).length
                  ? Object.keys(DraftAdviceLipSyncChunksRef.current).map(Number).sort((a, b) => a - b).flatMap((k) => DraftAdviceLipSyncChunksRef.current[k] ?? []).length
                  : 0;
                const selRd = selectedButtonRef.current;
                const applyRd = characterRef?.current && selRd === "DraftAdvice";
                console.log(`[LipsyncTrace] Draft Advice client lipsync_data chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} mergedBefore=${currentMergedCount} selectedButton=${selRd} applyToCharacter=${!!applyRd}`);
                console.log(`[Draft Advice Stream] lipsync_data #${receiptNum} chunkIndex=${chunkIndex} payloadCues=${mouthCues.length} currentMergedCues=${currentMergedCount}`);
                if (chunkIndex === 0) {
                  console.log(`[LipSyncDebug] Draft Advice: CLIENT_RECEIVED first lipsync_data chunkIndex=0 cues=${mouthCues.length}`);
                  console.log('[Draft Advice Stream] >>> CHUNK 0 received (0–5s coverage)');
                }
                if (chunkIndex === undefined) {
                  // We no longer expect a full replacement from backend; keep accumulated cues
                  if (mouthCues.length > 0) {
                    console.warn(
                      `[Draft Advice Stream] Ignoring lipsync_data without chunkIndex (backend sent ${mouthCues.length} cues, we have ${currentMergedCount} merged) – using accumulated chunk cues only`,
                    );
                  }
                } else {
                  DraftAdviceLipSyncChunksRef.current[chunkIndex] = mouthCues;
                  const keys = Object.keys(DraftAdviceLipSyncChunksRef.current).map(Number).sort((a, b) => a - b);
                  const mergedCues = keys.flatMap((k) => DraftAdviceLipSyncChunksRef.current[k] ?? []);
                  const merged = { mouthCues: mergedCues };
                  setLipSyncData((prev) => {
                    const next = { ...prev, DraftAdvice: merged };
                    if (characterRef?.current && selectedButtonRef.current === "DraftAdvice") {
                      console.log(`[LipSyncDebug] Draft Advice: CHARACTER_VISEMAS_APPLIED cues=${mergedCues.length}`);
                      applyLegacyLipSyncOrSkip(characterRef.current, merged, "stream lipsync_data");
                    }
                    console.log(`[Draft Advice Stream] Merged chunks [${keys.join(",")}] → ${mergedCues.length} cues`);
                    return next;
                  });
                }
                break;
              }
              case "audio_summary_chunk": {
                // Stop the initial ramp if still running
                if (DraftAdviceInitialRampTimerRef.current) {
                  clearInterval(DraftAdviceInitialRampTimerRef.current);
                  DraftAdviceInitialRampTimerRef.current = null;
                }
                const payload = event.payload || event; // support {data,...} or raw
                const base64Data = payload?.data || payload;
                const mimeType = payload?.mimeType || payload?.mime_type || "audio/pcm";
                if (typeof base64Data === "string" && base64Data.length > 0) {
                  // Mark first audio chunk arrived (used by button gating)
                  setDraftAdviceData((prev) => ({ ...prev, hasAudioChunk: true }));
                  // Jump target from <=90 to 100 when first audio chunk arrives
                  setStreamProgressTargets((prev) => {
                    return { ...prev, DraftAdvice: 100 };
                  });
                  // IMPORTANT: use refs to avoid stale closure inside long-running stream
                  const canPlayLiveDraftAdvice = isDrawerOpenRef.current && selectedButtonRef.current === "DraftAdvice" && !draftAdviceAudioBlockedForForcedRefreshRef.current;
                  console.log(`[LipsyncTrace] Draft Advice client audio_summary_chunk drawerOpen=${isDrawerOpenRef.current} selectedButton=${selectedButtonRef.current} canPlayLive=${canPlayLiveDraftAdvice} creatingPlayer=${!DraftAdvicePcmPlayerRef.current}`);
                  if (!canPlayLiveDraftAdvice) {
                    // Drawer cerrado: guardar chunk en buffer para reproducir después
                    DraftAdviceBufferedChunksRef.current.push({ data: base64Data, mimeType });
                    break;
                  }
                  // Ensure player exists
                  if (!DraftAdvicePcmPlayerRef.current) {
                    // Stop other endpoints' audio so only this endpoint plays (fixes wrong audio at start)
                    stopOtherEndpointPcmPlayersAndService("DraftAdvice");
                    DraftAdvicePcmPlayerRef.current = new PcmChunkPlayer({
                      sourceSampleRate: STREAMING_AUDIO_SAMPLE_RATE,
                      channels: 1,
                      label: "DraftAdvice",
                      vegaCharacterRef: characterRef,
                      onPlaybackStarted: () => {
                        console.log("[LipSyncDebug] Draft Advice: PCM_PLAYBACK_STARTED");
                        hasAudioBeenPlayed.current.DraftAdvice = true;
                        const chunksRd = DraftAdviceLipSyncChunksRef.current;
                        const keysRd = Object.keys(chunksRd).map(Number).sort((a, b) => a - b);
                        const mergedCuesRd = keysRd.length > 0 ? keysRd.flatMap((k) => chunksRd[k] ?? []) : (lipSyncDataRef.current?.DraftAdvice?.mouthCues ?? []);
                        const rdStart = performance.now() / 1000;
                        DraftAdvicePcmStartTimeRef.current = rdStart;
                        console.log(`[LipsyncTrace] Draft Advice client onPlaybackStarted mergedCues=${mergedCuesRd.length} characterRef=${!!characterRef?.current} calling setLipSyncData setPlaybackStartTime setIsTalking`);
                        if (characterRef?.current) {
                          const data = { mouthCues: mergedCuesRd };
                          console.log(`[LipSyncDebug] Draft Advice: CHARACTER_VISEMAS_APPLIED onPlaybackStarted cues=${mergedCuesRd.length}`);
                          applyLegacyLipSyncOrSkip(characterRef.current, data, "azure-cues");
                          characterRef.current.setPlaybackStartTime(rdStart);
                          characterRef.current.setIsTalking(true);
                          console.log("[Draft Advice Stream] Playback started - syncing lipsync with", mergedCuesRd.length, "cues");
                        }
                      },
                      onPlaybackEnded: () => {
                        console.log("[LipSyncDebug] Draft Advice: PCM_PLAYBACK_ENDED");
                        if (characterRef?.current) {
                          characterRef.current.setIsTalking(false);
                        }
                      },
                    });
                    await DraftAdvicePcmPlayerRef.current.start();
                    // Reproducir chunks buffered primero (si hay)
                    if (DraftAdviceBufferedChunksRef.current.length > 0) {
                      for (const buffered of DraftAdviceBufferedChunksRef.current) {
                        DraftAdvicePcmPlayerRef.current.addChunkBase64(buffered.data, buffered.mimeType);
                      }
                      // DraftAdviceBufferedChunksRef.current = []; // Limpiar buffer
                    }
                  }
                  DraftAdvicePcmPlayerRef.current.addChunkBase64(base64Data, mimeType);
                  // Audio is arriving; stop the loader
                  setDraftAdviceStreamState((prev) => ({ ...prev, isGeneratingAudio: false, isStreamActive: true }));
                  // Mark first audio chunk arrived (used by button gating)
                  //  setDraftAdviceData((prev) => ({ ...prev, hasAudioChunk: true }));
                }
                break;
              }
              case "audio_summary": {
                const rdCues = lipSyncDataRef.current?.DraftAdvice?.mouthCues?.length ?? 0;
                console.log(`[LipsyncTrace] Draft Advice client audio_summary received switching to WAV lipSyncCuesAvailable=${rdCues}`);
                console.log("[LipSyncDebug] Draft Advice: CLIENT_RECEIVED audio_summary (endpoint has URL)");
                console.log(`[Draft Advice Stream] Received audio_summary event:`, event.payload?.substring?.(0, 100) || event.payload);
                // Stop the initial ramp if still running
                if (DraftAdviceInitialRampTimerRef.current) {
                  clearInterval(DraftAdviceInitialRampTimerRef.current);
                  DraftAdviceInitialRampTimerRef.current = null;
                }
                // --- TIMING LOG END ---
                if (event.timestamp) {
                  const networkTime = Date.now() - event.timestamp;
                  const seconds = networkTime / 1000;
                  console.log(`[Draft Advice Audio] Network delivery took ${seconds.toFixed(2)}s.`);
                }
                // IMPORTANT: Do NOT stop live PCM player here.
                // We no longer switch from live PCM to final WAV mid-stream.
                // The final WAV is only used for later replays (e.g. user opens endpoint after everything finished).
                // Convert the Base64 string to a Blob
                // const DraftAdviceAudioBlob = base64ToBlob(event.payload);
                // setSummaryAudios((prev) => ({ ...prev, DraftAdvice: DraftAdviceAudioBlob })); // Store the audio data in state.
                setSummaryAudios((prev) => ({ ...prev, DraftAdvice: event.payload })); // Store the audio URL directly
                // Ensure target is 100 on final audio summary
                // setStreamProgressTargets((prev) => ({ ...prev, DraftAdvice: 100 }));

                // Store the audio URL in the DraftAdvice data
                setDraftAdviceData((prev) => ({
                  ...prev,
                  audioUrl: event.payload,
                }));
                // When audio arrives, stop the loader.
                setDraftAdviceStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                break;
              }
              case "share_url":
                setDraftAdviceData((prev) => ({ ...prev, share_url: event.payload }));
                break;
              case "complete":
                if (DraftAdviceInitialRampTimerRef.current) {
                  clearInterval(DraftAdviceInitialRampTimerRef.current);
                  DraftAdviceInitialRampTimerRef.current = null;
                }
                if (DraftAdvicePcmPlayerRef.current) {
                  DraftAdvicePcmPlayerRef.current.signalEndOfPcmStream();
                }
                setDraftAdviceData((prev) => {
                  const alreadyHasAudio = prev?.hasAudioChunk === true;
                  const updatedData = {
                    ...prev,
                    request_id: event.payload.request_id,
                    ...(alreadyHasAudio ? {} : { hasAudioChunk: false }),
                  };
                  finalData.request_id = updatedData.request_id;
                  return updatedData;
                });
                setDraftAdviceStreamState((prev) => ({ ...prev, isGeneratingAudio: false }));
                setStreamProgressTargets((prev) => ({ ...prev, DraftAdvice: 100 }));
                setThoughtsCommon("");
                break;
              case "error":
                throw new Error(event.payload);
              case "api_error":
                throw new ApiError(event.payload);
            }
          }
          // Handle the end of the stream only after processing buffer (so api_error in last chunk is not skipped)
          if (done) {
            const duration = Date.now() - startTime;
            const totalSeconds = Math.floor(duration / 1000);
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            const formattedTime =
              minutes > 0 ? `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")} m` : `${String(seconds).padStart(2, "0")} s`;
            console.log(`[PERF] fetchDraftAdviceDataStream: Finished receiving stream from chat-backend in ${formattedTime}.`);
            if (!finalData.fromCache) {
              void trackEndpointResponseTime(authId, "draft_advice", (Date.now() - startTime) / 1000, platformId, leagueId);
            }
            setLoadingDraftAdvice(false);
            setDraftAdviceStreamState((prev) => ({ ...prev, isStreamActive: false }));
            finalData.context = contextAtRequestStart;
            finalData.model = modelToUse;
            // Update local state with model + context so AnimatedButton's hasValidDataForCurrentTeam() returns true immediately
            setDraftAdviceData((prev) => ({ ...prev, model: modelToUse, context: contextAtRequestStart, non_llm_generated: finalData.non_llm_generated }));
            updateSectionData("DraftAdvice", finalData);
            break;
          }
        }
      };

      await readStream();
    } catch (error) {
      // If stream fails, clear stale thought bubble/sign immediately.
      setThoughtsCommon("");
      // Rate limit: open drawer and mark button as failed so retry icon shows, never poll
      if ((error as any).isRateLimit) {
        openRateLimitDrawer((error as any).retryAfter);
        setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        setLoadingDraftAdvice(false);
        setDraftAdviceStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false }));
        return;
      }
      if (Capacitor.isNativePlatform() && error.name !== "AbortError" && !(error instanceof ApiError)) {
        if (error.message && error.message.trim() === noDraftErrorMessage) {
          setErrorMessages((prev) => ({ ...prev, DraftAdvice: noDraftErrorMessage }));
          setErrorMessageNoDraft((prev) => ({ ...prev, DraftAdvice: true }));
          setLoadingDraftAdvice(false);
          setDraftAdviceData({ rundown_analysis: "", chart_data: [], request_id: null });
          setDraftAdviceStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
          return;
        }
        pollingIsResponsibleForCleanup = true;
        startPollingForDraftAdviceCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform);
        return;
      }

      if (error.name === "AbortError" || error.name === "TypeError") {
        pollingIsResponsibleForCleanup = true;
        startPollingForDraftAdviceCompletion(leagueId, teamName, undefined, yahooToken, requestKey, activeStreamRequests, contextAtRequestStart, currentTeamInfoRef, currentUserDataPlatform).finally(() => {
          activeStreamRequests.current.delete(requestKey);
        });
        return;
      }

      if (error instanceof ApiError) {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, DraftAdvice: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, DraftAdvice: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("draft_advice"); }, 1500);
      } else if (error.name !== "TypeError") {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, DraftAdvice: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
        } else {
          setErrorMessageNoDraft((prev) => ({ ...prev, DraftAdvice: true }));
        }
        // Refresh credits UI with a small delay to ensure backend Mongo refund completes first
        setTimeout(() => { void updateCreditsAfterEndpoint("draft_advice"); }, 1500);
      }

      setLoadingDraftAdvice(false);
      setDraftAdviceData({ rundown_analysis: "", chart_data: [], request_id: null });
      setDraftAdviceStreamState((prev) => ({ ...prev, isStreamActive: false, isGeneratingResult: false, isGeneratingAudio: false }));
    } finally {
      if (!pollingIsResponsibleForCleanup) {
        activeStreamRequests.current.delete(requestKey);
      }
      // Always unblock audio when this stream call returns (covers success, error, and abort paths)
      draftAdviceAudioBlockedForForcedRefreshRef.current = false;
    }
  };

  //--------------------------------------------

  // Function to fetch SuperFunction data
  const fetchSuperFunctionData = async (leagueId: string, signal: AbortSignal, yahooToken?: string) => {
    setLoadingSuperFunction(true);

    try {
      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        setLoadingSuperFunction(false);
        return null;
      }

      const data = await getSuperFunction(authId, userDataPlatform, leagueId, pocToken, accessTokenFromAuth0, signal, yahooToken);
      setSuperFunctionData(data);
      setEndpointErrors((prev) => ({ ...prev, superFunction: false }));
      updateSectionData("superFunction", data);
      setLoadingSuperFunction(false);
      // Set shouldPlayAudio to true if selectedButton is "superFunction"
      // if (selectedButton === "superFunction") {
      //   setShouldPlayAudio(false);
      //   setTimeout(() => setShouldPlayAudio(true), 10);
      // }
      return data;
    } catch (error) {
      if (error.name !== "AbortError") {
        const errorMessage = error.message || "";
        setErrorMessages((prev) => ({ ...prev, SuperFunction: errorMessage }));
        if (errorMessage.trim() !== noDraftErrorMessage) {
          setEndpointErrors((prev) => ({ ...prev, superFunction: true }));
        } else {
          // setErrorMessageNoDraft(true);
        }
        setSuperFunctionData(null);
        setLoadingSuperFunction(false);
        return null;
      }
      throw error;
    }
  };

  //--------------------------------------------

  const checkAllDataLoaded = () => {
    if (waiverData !== null || startData !== null || newsData !== null || rostersData !== null || rankingsData !== null) {
      setMessage("Use the buttons below for quick detailed insights into your team");
    }
  };

  //--------------------------------------------

  // Function to check if all data is loaded
  useEffect(() => {
    checkAllDataLoaded();
  }, [waiverData, startData, newsData, rostersData, rankingsData]);
  const isExecutingRef = useRef(false);

  //--------------------------------------------

  //State to know if the user has clicked on a new platform
  const [newClickOnPlatofrm, setNewClickOnPlatofrm] = useState(false);
  //useEffect to clear thoughtsCommon when newClickOnPlatofrm is true
  useEffect(() => {
    if (newClickOnPlatofrm) {
      setThoughtsCommon(""); // clear stale thoughts instantly
      clearSignThoughtsDrawerSource();
    }
  }, [newClickOnPlatofrm, clearSignThoughtsDrawerSource]);

  const loadPlatformAbortController = useRef<AbortController | null>(null);

  // useEffect dedicated to fetching allowed platforms periodically
  //  useEffect(() => {
  //   const fetchPlatforms = async () => {
  //     try {
  //       const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
  //       // We only fetch if we have the necessary tokens
  //       if (accessTokenFromAuth0 && pocToken) {
  //         const platformsFromServer = await getAllowedPlatforms(accessTokenFromAuth0, pocToken);
  //         console.log("HOMEPAGE loadPlatformData: Allowed platforms from server:", platformsFromServer);
  //         setAllowedPlatforms(platformsFromServer);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching allowed platforms:", error);
  //     }
  //   };

  //   // 1. Fetch immediately on component load if platforms are not set yet.
  //   if (!allowedPlatforms) {
  //     fetchPlatforms();
  //   }

  //   // 2. Set up an interval to fetch every 30 minutes.
  //   // const intervalId = setInterval(fetchPlatforms, 30 * 60 * 1000);
  //   // const intervalId = setInterval(fetchPlatforms, 5 * 60 * 1000);

  //   // 3. Cleanup function to clear the interval when the component unmounts
  //   // return () => clearInterval(intervalId);
  // }, [pocToken, getSafeAccessAuth0Token]); // Dependencies needed for the fetch operation

  // Function to load platform data
  useEffect(() => {
    const loadPlatformData = async () => {
      if (loadPlatformAbortController.current) {
        loadPlatformAbortController.current.abort();
        loadPlatformAbortController.current = null;
      }

      loadPlatformAbortController.current = new AbortController();
      const signal = loadPlatformAbortController.current.signal;

      const selectedPlatform = leaguePlatforms.find((platform) => platform.isSelected);
      // console.log("HOMEPAGE selectPlatform", selectedPlatform);

      if (!selectedPlatform || isExecutingRef.current) return;
      let platformTeamsData;
      try {
        // Get data from cache for the platform
        platformTeamsData = teamsDataCache[selectedPlatform.id];

        const platformUserData = userDataCache[selectedPlatform.id];
        const previousTeamInfo = selectedTeams[selectedPlatform.id];

        if (platformTeamsData) {
          setTeamsdata(platformTeamsData);
          // panel1 open on refresh is handled in useLayoutEffect (avoids Articles flash before paint)
        } else {
          setTeamsdata(null);
          console.log("HOMEPAGE loadPlatformData: No cached data for the new platform");
        }

        if (platformUserData) {
          setUserDataPlatform(platformUserData);
        }

        // [AUTO-SELECTION DISABLED] Do not restore team automatically when switching platforms
        /*
        // Only restore team if it exists in the platform data
        if (previousTeamInfo && platformTeamsData?.leagues) {
          const teamExists = platformTeamsData.leagues.some((team) => team.name === previousTeamInfo.teamName && team.league_id === previousTeamInfo.leagueId);

          if (teamExists && teamsHomePageRef.current) {
            // setSelectedTeamInfo(previousTeamInfo);
            // setSelectedPartner(previousTeamInfo.teamName);
            // setLeagueIdPlatform(previousTeamInfo.leagueId);

            // Check if we need to load data or use cache
            if (!isCacheValid(sectionData, currentPlatform, previousTeamInfo.teamName, previousTeamInfo.leagueId)) {
              // Small delay before activating loadings
              await new Promise((resolve) => setTimeout(resolve, 50));

              setLoadingNews(true);
              setLoadingWaiver(true);
              setLoadingStart(true);
              setLoadingRoster(true);
              setLoadingRankings(true);
              setLoadingRundown(true);
              setHasLoadingStarted(true);
            }

            await teamsHomePageRef.current.handlePartnerClick(previousTeamInfo.teamName, previousTeamInfo.leagueId, previousTeamInfo.platformId, signal);
            // }
          } else if (platformTeamsData?.leagues?.length > 0 && teamsHomePageRef.current) {
            // If previous team doesn't exist but platform has teams, select first team
            const firstTeam = platformTeamsData?.leagues[0];
            await teamsHomePageRef.current.handlePartnerClick(firstTeam.name, firstTeam.league_id, selectedPlatform.id, signal);
          } else {
            setMessage("Click on the team you want to look at below");
            setSelectedTeamInfo(null);
            setSelectedPartner("");
          }
        } else {
          setMessage("Click on the team you want to look at below");
          setSelectedTeamInfo(null);
          setSelectedPartner("");
        }
        */
        // [MODIFIED] Always show the select team message
        // guard to avoid bug that refresh token of yahoo clean this states (and then bugs happens on all sections endpoints)
        if (!selectedTeamInfo) {
          setSelectedTeamInfo(null);
          setSelectedPartner("");
        }
      } catch (error) {
        console.error("Error loading platform data:", error);
        setMessage("Error loading platform data");
      } finally {
        if (loadPlatformAbortController.current) {
          loadPlatformAbortController.current = null;
        }
      }
      // });
    };
    // setNewClickOnPlatofrm(false);
    if (!isLoadingPreferences) {
      loadPlatformData();
    }

    return () => {
      if (loadPlatformAbortController.current) {
        loadPlatformAbortController.current.abort();
        loadPlatformAbortController.current = null;
      }
    };
  }, [leaguePlatforms, yahooToken, isLoadingPreferences]);

  // Open teams accordion before first paint after prefs load (avoids 1-frame Articles flash on refresh)
  useLayoutEffect(() => {
    if (isLoadingPreferences) return;
    const selectedPlatform = leaguePlatforms.find((p) => p.isSelected);
    if (!selectedPlatform) return;
    const platformTeamsData = teamsDataCache[selectedPlatform.id];
    if (!platformTeamsData) return;
    const platformHasTeams = Array.isArray(platformTeamsData)
      ? platformTeamsData.length > 0
      : Boolean(platformTeamsData?.leagues?.length);
    // Un-Synced: league is auto-selected — keep accordion closed (Capacitor remount re-ran this and covered Chatbot/Rankings).
    // Synced platforms still open panel1 for manual pick. To show Un-Synced list on boot, drop the !isUnsyncedPlatform check.
    if (platformHasTeams && !isUnsyncedPlatform(selectedPlatform.id)) {
      setExpanded("panel1");
    }
    // Intentionally only when prefs gate flips — do not re-open after manual team pick
  }, [isLoadingPreferences]);

  //--------------------------------------------

  // Function to check if loading has started
  useEffect(() => {
    // When any loading changes to true, it means that the loading has started
    if (loadingStart || loadingNews || loadingRankings || loadingWaiver || loadingRoster || loadingRundown || loadingDraftAdvice) {
      // setHasLoadingStarted(true);
    }
  }, [loadingStart, loadingNews, loadingRankings, loadingWaiver, loadingRoster, loadingRundown, loadingDraftAdvice]);

  //--------------------------------------------
  // useEffect to restore initial state
  useEffect(() => {
    const restoreInitialState = async () => {
      if (isHandlingTeamSelection.current) {
        // console.log("Skipping loadPlatformData for handleYahooCallback or handleTeamSelection");
        return;
      }

      const accessTokenFromAuth0 = await getSafeAccessAuth0Token();
      if (!accessTokenFromAuth0) {
        // Logout is in progress, stop.
        return;
      }

      // The old check `if (accessTokenFromAuth0 && ...)` is now simplified
      if (!isLoadingPreferences && isAuthenticated && pocToken) {
        try {
          // setIsChangingPlatform(true);
          // await new Promise((resolve) => setTimeout(resolve, 500));
          const preferences = await getUserPreferences(accessTokenFromAuth0);

          if (preferences && preferences.loggedPlatforms) {
            const activePlatform = preferences.loggedPlatforms.find((p) => p.isLoggedIn && p.isSelected);

            if (activePlatform) {
              const platformCache = preferences.teamsData?.[activePlatform.id];
              if (platformCache) {
                setTeamsdata(platformCache);
              }
            }
          }

          const activePlatform = leaguePlatforms.find((p) => p.isLoggedIn && p.isSelected);

          // [AUTO-SELECTION DISABLED] Do not restore team automatically on login/refresh
          /*
          // General platform handling
          if (activePlatform) {
            const platformCache = teamsDataCache[activePlatform.id];
            const userPlatformData = userDataCache[activePlatform.id];
            const previousTeamInfo = selectedTeams[activePlatform.id];

            if (platformCache && userPlatformData && previousTeamInfo) {
              const teamExists = validateTeamData(previousTeamInfo, platformCache);

              if (teamExists) {
                // Update all the states needed
                setSelectedTeamInfo(previousTeamInfo);
                setSelectedPartner(previousTeamInfo.teamName);
                setLeagueIdPlatform(previousTeamInfo.leagueId);
                setUserDataPlatform(userPlatformData);
                setTeamsdata(platformCache);

                // Ensure that selectedTeams is updated
                const updatedSelectedTeams = {
                  ...selectedTeams,
                  [activePlatform.id]: previousTeamInfo,
                };
                setSelectedTeams(updatedSelectedTeams);

                await postUserPreferences(accessTokenFromAuth0, {
                  loggedPlatforms: leaguePlatforms,
                  teamsData: teamsDataCache,
                  userDataPlatform: userDataCache,
                  selectedTeams: updatedSelectedTeams,
                });

                setMessage("Use the buttons below for quick detailed insights into your team");
              }
            }
            // Specific handling for Yahoo when there is no cache
            else if (activePlatform.id === "Yahoo") {
              console.log("🔄 restoreInitialState: Getting valid Yahoo token...");
              const validToken = await getValidYahooToken();
              if (validToken) {
                console.log("✅ restoreInitialState: Valid Yahoo token obtained:", validToken.substring(0, 10) + "...");

                let yahooTeamsData;

                // Try to use cached data first
                if (preferences?.teamsData?.Yahoo?.leagues?.length > 0) {
                  yahooTeamsData = preferences.teamsData.Yahoo;
                } else {
                  //TAMPOCO BORRAR ESTE CODIGO COMENTADO
                  // If there is no cached data, get it directly
                  // yahooTeamsData = await getYahooTeamsData(validToken);
                  // if (yahooTeamsData?.leagues?.length > 0) {
                  //   // Update cache
                  //   setTeamsDataCache((prev) => ({
                  //     ...prev,
                  //     Yahoo: yahooTeamsData,
                  //   }));
                  // }
                }

                // Set data and select first team if necessary
                if (yahooTeamsData?.leagues?.length > 0) {
                  setTeamsdata(yahooTeamsData);

                  // If there is no selected team, use the first one
                  if (!preferences?.selectedTeams?.Yahoo) {
                    const firstTeam = {
                      platformId: "Yahoo",
                      teamName: yahooTeamsData.leagues[0].name,
                      leagueId: yahooTeamsData.leagues[0].league_id,
                    };
                    setSelectedTeamInfo(firstTeam);

                    // Create an updatedSelectedTeams object with the selectedTeams and the new teamInfo
                    const updatedSelectedTeams: SelectedTeams = {
                      ...selectedTeams,
                      Yahoo: firstTeam,
                    };
                    setSelectedTeams(updatedSelectedTeams);

                    // Persist the changes
                    await postUserPreferences(accessTokenFromAuth0, {
                      loggedPlatforms: leaguePlatforms,
                      teamsData: teamsDataCache,
                      userDataPlatform: userDataCache,
                      selectedTeams: updatedSelectedTeams,
                    });

                    setMessage("Use the buttons below for quick detailed insights into your team");
                  }
                }
              }
            }
          }
          */
        } catch (error) {
          console.error("❌ Error restoring state:", error);
        }
      }
    };

    restoreInitialState();
  }, [getSafeAccessAuth0Token, isLoadingPreferences, isAuthenticated, pocToken]); // Dependency updated

  //--------------------------------------------

  // Function to scroll to the last platform
  useEffect(() => {
    // Get the platforms container
    const platformsContainer = document.querySelector(".platforms-scroll-container");
    if (!platformsContainer) return;

    // Get the selected platform
    const selectedPlatform = leaguePlatforms.find((p) => p.isSelected);
    if (!selectedPlatform) return;

    // Get the index of the selected platform
    const selectedIndex = leaguePlatforms.findIndex((p) => p.id === selectedPlatform.id);
    const loggedInPlatforms = leaguePlatforms.filter((p) => p.isLoggedIn);

    // Calculate the width of each card (including the margin)
    const cardWidth = 90; // 78px width + 12px margin-right

    if (loggedInPlatforms.length > 3) {
      if (selectedIndex === loggedInPlatforms.length - 1) {
        // If it is the last platform, scroll to the end
        platformsContainer.scrollLeft = platformsContainer.scrollWidth;
      } else if (selectedIndex === 0) {
        // If it is the first platform, scroll to the start
        platformsContainer.scrollLeft = 0;
      } else {
        // For the intermediate platforms, center the selected one
        platformsContainer.scrollLeft = cardWidth * selectedIndex - (platformsContainer.clientWidth - cardWidth) / 2;
      }
    }
  }, [leaguePlatforms]); // Run when leaguePlatforms changes

  const hasTeams = Array.isArray(teamsData) ? teamsData.length > 0 : teamsData?.leagues && teamsData.leagues.length > 0;

  const isYahooSelected = leaguePlatforms.some((platform) => platform.id === "Yahoo" && platform.isSelected);

  const activePlatform = leaguePlatforms.find((p) => p.isLoggedIn && p.isSelected);

  //State for shaking platform
  const [shakingPlatform, setShakingPlatform] = useState<string | null>(null);
  //State for deletion mode
  const [deletionMode, setDeletionMode] = useState<string | null>(null);

  // Ref to store the long press timer so it persists across re-renders
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Ref to avoid double-start when both touch and synthetic mouse events fire (e.g. mobile web)
  const longPressGestureRef = useRef<"touch" | "mouse" | null>(null);

  //Function to handle platform long press (works on desktop=mouse, mobile web=touch, Capacitor=touch, DevTools=either)
  const handlePlatformLongPress = (platformId: string) => {
    //Function to start shaking platform
    const startShake = () => {
      // Clear any existing timer just in case
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }

      //Set the shaking platform to the platformId
      longPressTimerRef.current = setTimeout(() => {
        //Set the shaking platform to the platformId
        setShakingPlatform(platformId);
        //Set the deletion mode to the platformId
        setDeletionMode(platformId);
      }, 2000); // 2 seconds for shaking platform
    };

    //Function to stop the timer
    const stopTimer = () => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    };

    return {
      onMouseDown: () => {
        if (longPressGestureRef.current === "touch") return; // touch already claimed this gesture
        longPressGestureRef.current = "mouse";
        startShake();
      },
      onMouseUp: () => {
        stopTimer();
        longPressGestureRef.current = null;
      },
      onMouseLeave: () => {
        stopTimer();
        longPressGestureRef.current = null;
      },
      onTouchStart: (e: React.TouchEvent) => {
        if (longPressGestureRef.current === "mouse") return; // mouse already claimed this gesture
        longPressGestureRef.current = "touch";
        e.preventDefault(); // prevent context menu so 2s long-press can complete on mobile web
        startShake();
      },
      onTouchEnd: () => {
        stopTimer();
        longPressGestureRef.current = null;
      },
      onTouchMove: () => {
        stopTimer();
        longPressGestureRef.current = null;
      },
      onTouchCancel: () => {
        stopTimer();
        longPressGestureRef.current = null;
      },
    };
  };

  //Function to handle click outside of the platform card
  useEffect(() => {
    if (!deletionMode) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as HTMLElement;
      const platformCard = target.closest("[data-platform-id]");
      const isClickInsideCard = platformCard && platformCard.getAttribute("data-platform-id") === deletionMode;

      if (!isClickInsideCard) {
        setShakingPlatform(null);
        setDeletionMode(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [deletionMode]);

  //state to open modal of endpoint errors or payment errors
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  //function to open modal of endpoint errors or payment errors
  const handleOpenPaymentDialog = () => setIsPaymentDialogOpen(true);
  //function to close modal of endpoint errors or payment errors
  const handleClosePaymentDialog = () => setIsPaymentDialogOpen(false);

  //function to close modal of endpoint errors messages for EndpointErrorsMessage.tsx
  const handleCloseErrorMessage = (endpoint: string) => {
    setErrorMessages((prev) => ({ ...prev, [endpoint]: "" }));
  };

  //Error messages for the endpoints to avoid showing the modal in this cases
  const gcpError = "Load failed";
  const gcpError2 = "Internal server error";
  const llmError = "There was an error with RotoGPT's AI services processing your request";
  const failedYahooToken = "Failed to get valid Yahoo token";
  const commonLeagueDataFailedError = "There was an error with your league. Please confirm your league is active and has players";
  const noPlayersFoundInTeamError = "Rosters are empty as this league has not had it's draft yet. Try a different league.";
  const platformNotAllowedError = "Platform is not allowed. Please try with a different platform.";

  // useffect to listen for app state changes
  useEffect(() => {
    // THIS LISTENER SHOULD ONLY BE ACTIVE ON NATIVE PLATFORMS OR WEB MOBILE (NOT DESKTOP)
    if (!Capacitor.isNativePlatform() && !isMobileWebBrowser()) {
      return;
    }

    //render this SuspenseLoader componente for 10seconds when the user came back from background please



    let listener: any;

    const reTriggerIfStuck = () => {
      if (!selectedTeamInfo) {
        return;
      }
      console.log("App/tab visible. Re-triggering checks for incomplete streams.");

      // Create a single AbortController for this polling session.
      const controller = new AbortController();
      setCurrentAbortController(controller); // Make it cancellable if the user navigates away.
      // --- REVISED LOGIC ---
      // Instead of calling fetch...DataStream (which would be blocked by the guard),
      // we now directly call the polling function, but only if we can confirm
      // that a request was actually "stuck" (i.e., the lock is still held).

      const { platformId, leagueId } = selectedTeamInfo;
      const contextAtRequestStart = { platformId, leagueId, userId: authId };
      const bgPlatformData = userDataCache[platformId];

      // Waiver
      if (loadingWaiver && !waiverStreamState.isStreamActive && selectedTeamInfo) {
        setIsRecoveringFromBackground((prev) => ({ ...prev, waiver: true }));
        const requestKey = `${authId}-waiver-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;
        if (activeStreamRequests.current.has(requestKey)) {
          console.log("Waiver was loading. Re-triggering POLLING to check status.");
          startPollingForWaiverCompletion(
            selectedTeamInfo.leagueId,
            selectedTeamInfo.teamName,
            undefined,
            yahooToken,
            requestKey,
            activeStreamRequests,
            contextAtRequestStart,
            currentTeamInfoRef,
            bgPlatformData
          ).finally(() => {
            activeStreamRequests.current.delete(requestKey);
            console.log(`Polling (from background) for ${requestKey} finished. Lock released.`);
          });
        }
      }

      // Start/Sit
      if (loadingStart && !startStreamState.isStreamActive && selectedTeamInfo) {
        setIsRecoveringFromBackground((prev) => ({ ...prev, start: true }));
        const requestKey = `${authId}-start-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;
        if (activeStreamRequests.current.has(requestKey)) {
          console.log("Start/Sit was loading. Re-triggering POLLING to check status.");
          startPollingForStartCompletion(
            selectedTeamInfo.leagueId,
            selectedTeamInfo.teamName,
            undefined,
            yahooToken,
            requestKey,
            activeStreamRequests,
            contextAtRequestStart,
            currentTeamInfoRef,
            bgPlatformData
          ).finally(() => {
            activeStreamRequests.current.delete(requestKey);
            console.log(`Polling (from background) for ${requestKey} finished. Lock released.`);
          });
        }
      }

      // Rundown
      if (loadingRundown && !rundownStreamState.isStreamActive && selectedTeamInfo) {
        setIsRecoveringFromBackground((prev) => ({ ...prev, rundown: true }));
        const requestKey = `${authId}-rundown-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;
        if (activeStreamRequests.current.has(requestKey)) {
          console.log("Rundown was loading. Re-triggering POLLING to check status.");
          startPollingForRundownCompletion(
            selectedTeamInfo.leagueId,
            selectedTeamInfo.teamName,
            undefined,
            yahooToken,
            requestKey,
            activeStreamRequests,
            contextAtRequestStart,
            currentTeamInfoRef,
            bgPlatformData
          ).finally(() => {
            activeStreamRequests.current.delete(requestKey);
            console.log(`Polling (from background) for ${requestKey} finished. Lock released.`);
          });
        }
      }

      // Draft Advice
      if (loadingDraftAdvice && !DraftAdviceStreamState.isStreamActive && selectedTeamInfo) {
        setIsRecoveringFromBackground((prev) => ({ ...prev, DraftAdvice: true }));
        const requestKey = `${authId}-DraftAdvice-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;
        if (activeStreamRequests.current.has(requestKey)) {
          console.log("Draft advice was loading. Re-triggering POLLING to check status.");
          startPollingForDraftAdviceCompletion(
            selectedTeamInfo.leagueId,
            selectedTeamInfo.teamName,
            undefined,
            yahooToken,
            requestKey,
            activeStreamRequests,
            contextAtRequestStart,
            currentTeamInfoRef,
            bgPlatformData
          ).finally(() => {
            activeStreamRequests.current.delete(requestKey);
            console.log(`Polling (from background) for ${requestKey} finished. Lock released.`);
          });
        }
      }
    };

    // const setupListener = async () => {
    //   // Detect when the app comes back from the background
    //   listener = await App.addListener("appStateChange", ({ isActive }) => {
    //     // if (!isActive) {
    //     //   // Close any open drawer immediately for a better UX when user goes to background.
    //     //   handlePartnerSelectAndCloseDrawer();
    //     //   return;
    //     // }

    //     // If the app is active, we came back from the background
    //     if (isActive) {
    //       // Your great solution: close any open drawer to prevent a blank screen.
    //       // handlePartnerSelectAndCloseDrawer();

    //       console.log("App returned from background. Re-triggering checks for incomplete streams.");

    //       // Create a single AbortController for this polling session.
    //       const controller = new AbortController();
    //       setCurrentAbortController(controller); // Make it cancellable if the user navigates away.
    //       // --- REVISED LOGIC ---
    //       // Instead of calling fetch...DataStream (which would be blocked by the guard),
    //       // we now directly call the polling function, but only if we can confirm
    //       // that a request was actually "stuck" (i.e., the lock is still held).
    //       const { platformId, leagueId } = selectedTeamInfo;

    //       const contextAtRequestStart = { platformId, leagueId, userId: authId };

    //       // For Waiver
    //       if (loadingWaiver && !waiverStreamState.isStreamActive && selectedTeamInfo) {
    //         setIsRecoveringFromBackground((prev) => ({ ...prev, waiver: true }));
    //         const requestKey = `${authId}-waiver-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;
    //         // Only start polling if the lock is still held for this specific request.
    //         if (activeStreamRequests.current.has(requestKey)) {
    //           console.log("Waiver was loading. Re-triggering POLLING to check status.");
    //           startPollingForWaiverCompletion(
    //             selectedTeamInfo.leagueId,
    //             selectedTeamInfo.teamName,
    //             undefined,
    //             yahooToken,
    //             requestKey,
    //             activeStreamRequests,
    //             contextAtRequestStart,
    //             currentTeamInfoRef
    //           ).finally(() => {
    //             activeStreamRequests.current.delete(requestKey);
    //             console.log(`Polling (from background) for ${requestKey} finished. Lock released.`);
    //           });
    //         }
    //       }
    //       // For Start/Sit
    //       if (loadingStart && !startStreamState.isStreamActive && selectedTeamInfo) {
    //         setIsRecoveringFromBackground((prev) => ({ ...prev, start: true }));
    //         const requestKey = `${authId}-start-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;
    //         if (activeStreamRequests.current.has(requestKey)) {
    //           console.log("Start/Sit was loading. Re-triggering POLLING to check status.");
    //           startPollingForStartCompletion(
    //             selectedTeamInfo.leagueId,
    //             selectedTeamInfo.teamName,
    //             undefined,
    //             yahooToken,
    //             requestKey,
    //             activeStreamRequests,
    //             contextAtRequestStart,
    //             currentTeamInfoRef
    //           ).finally(() => {
    //             activeStreamRequests.current.delete(requestKey);
    //             console.log(`Polling (from background) for ${requestKey} finished. Lock released.`);
    //           });
    //         }
    //       }
    //       // For Rundown
    //       if (loadingRundown && !rundownStreamState.isStreamActive && selectedTeamInfo) {
    //         setIsRecoveringFromBackground((prev) => ({ ...prev, rundown: true }));
    //         const requestKey = `${authId}-rundown-${selectedTeamInfo.platformId}-${selectedTeamInfo.leagueId}`;
    //         if (activeStreamRequests.current.has(requestKey)) {
    //           console.log("Rundown was loading. Re-triggering POLLING to check status.");
    //           startPollingForRundownCompletion(
    //             selectedTeamInfo.leagueId,
    //             selectedTeamInfo.teamName,
    //             undefined,
    //             yahooToken,
    //             requestKey,
    //             activeStreamRequests,
    //             contextAtRequestStart,
    //             currentTeamInfoRef
    //           ).finally(() => {
    //             activeStreamRequests.current.delete(requestKey);
    //             console.log(`Polling (from background) for ${requestKey} finished. Lock released.`);
    //           });
    //         }
    //       }
    //     }
    //   });
    // };

    // Setup listeners (native + web mobile) following pattern used elsewhere
    let visibilityListener: (() => void) | undefined;

    const setupNativeListener = async () => {
      if (!Capacitor.isNativePlatform()) return;
      // Native (Capacitor): appStateChange → isActive
      listener = await App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          // Debounce: Capacitor iOS fires isActive:true twice in quick succession
          const now = Date.now();
          if (now - lastBackgroundResumeRef.current < 500) return;
          lastBackgroundResumeRef.current = now;

          setIsQuanReady(false);
          setBackgroundReturnId((id) => id + 1);
          setIsReturningFromBackground(true);
          if (backgroundReturnTimeoutRef.current) clearTimeout(backgroundReturnTimeoutRef.current);
          backgroundReturnTimeoutRef.current = setTimeout(() => {
            setIsReturningFromBackground(false);
          }, 10000);

          reTriggerIfStuck();
        }
      });
    };

    const setupWebMobileListener = () => {
      if (Capacitor.isNativePlatform()) return;
      // Web mobile: document.visibilitychange → visible
      const onVis = () => {
        if (document.visibilityState === "visible") {
          setIsQuanReady(false);
          setBackgroundReturnId((id) => id + 1);
          setIsReturningFromBackground(true);
          if (backgroundReturnTimeoutRef.current) clearTimeout(backgroundReturnTimeoutRef.current);
          backgroundReturnTimeoutRef.current = setTimeout(() => {
            setIsReturningFromBackground(false);
          }, 10000);

          reTriggerIfStuck();
        }
      };
      visibilityListener = onVis;
      document.addEventListener("visibilitychange", onVis);
    };

    setupNativeListener();
    setupWebMobileListener();

    // Cleanup nativo
    return () => {
      if (listener && listener.remove) {
        listener.remove();
      }
      if (visibilityListener) {
        document.removeEventListener("visibilitychange", visibilityListener);
      }
    };

    // Dependencies must be here so the listener has access to the latest state values when it fires.
  }, [
    loadingWaiver,
    loadingStart,
    loadingRundown,
    loadingDraftAdvice,
    waiverStreamState.isStreamActive,
    startStreamState.isStreamActive,
    rundownStreamState.isStreamActive,
    DraftAdviceStreamState.isStreamActive,
    selectedTeamInfo,
    yahooToken,
    setCurrentAbortController,
    authId,
    // handlePartnerSelectAndCloseDrawer,
  ]);

  // get the current user data platform from widget context instead of userDataPlatform (homepage state) to avoid race condition
  const currentUserDataPlatform = userDataCache[selectedTeamInfo?.platformId];

  // Generalized polling function for waiver using utility
  const startPollingForWaiverCompletion = async (
    leagueId: string,
    teamName: string, // Add teamName
    signal: AbortSignal | undefined,
    yahooToken: string | null | undefined,
    requestKey: string,
    activeStreamRequests: React.MutableRefObject<Set<string>>,
    context: { platformId: string; leagueId: string; userId: string | undefined },
    currentTeamInfoRef: React.MutableRefObject<any>,
    platformDataOverride?: any
  ) => {
    console.log("Polling started for waiver completion.");
    try {
      const isSameTeamContext =
        !!selectedTeamInfo && selectedTeamInfo.platformId === context.platformId && selectedTeamInfo.leagueId === context.leagueId && selectedTeamInfo.teamName === teamName;
      // Use override from caller when available to avoid stale closure after page refresh
      const platformData = platformDataOverride ?? currentUserDataPlatform;
      // The only change is adding "return await" here
      return await startPollingForCompletion({
        endpointType: "waiver",
        leagueId,
        teamName, // Pass teamName
        yahooToken,
        signal,
        context,
        currentTeamInfoRef,
        requestKey,
        activeStreamRequests,
        // API & Auth
        getAccessTokenSilently: getSafeAccessAuth0Token,
        authId,
        currentUserDataPlatform: platformData,
        pocToken,
        // API Call
        apiCall: getWaiverStream,
        // State Setters
        setStreamState: setWaiverStreamState,
        setData: setWaiverData,
        setLoading: setLoadingWaiver,
        updateSectionData,
        setEndpointErrors,
        setErrorMessages,
        setHasAnimationCompleted: setHasWaiverAnimationCompleted,
        // Suppress polling only when user clicked refresh for this SAME team and refresh is active
        suppressPolling: !!forceRefresh && !!refreshingLeagueData && isSameTeamContext,
        // Messages
        retrievingMessage: "Retrieving your waiver analysis...",
        errorKey: "Waiver",
        setThoughtsCommon,
        onAudioUrl: (url) => applyPolledAgentAudioUrl("waiver", url),
      });
    } catch (error) {
      console.error("Polling for waiver completion failed:", error);
      setLoadingWaiver(false);
      const errorKey = `waiver-${context.platformId}-${context.leagueId}`;
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
    }
  };

  // Generalized polling function for start using utility
  const startPollingForStartCompletion = async (
    leagueId: string,
    teamName: string, // Add teamName
    signal: AbortSignal | undefined,
    yahooToken: string | null | undefined,
    requestKey: string,
    activeStreamRequests: React.MutableRefObject<Set<string>>,
    context: { platformId: string; leagueId: string; userId: string | undefined },
    currentTeamInfoRef: React.MutableRefObject<any>,
    platformDataOverride?: any
  ) => {
    console.log("Polling started for start completion.");
    try {
      const isSameTeamContext =
        !!selectedTeamInfo && selectedTeamInfo.platformId === context.platformId && selectedTeamInfo.leagueId === context.leagueId && selectedTeamInfo.teamName === teamName;
      const platformData = platformDataOverride ?? currentUserDataPlatform;
      // The only change is adding "return await" here
      return await startPollingForCompletion({
        endpointType: "start",
        leagueId,
        teamName, // Pass teamName
        yahooToken,
        signal,
        context,
        currentTeamInfoRef,
        requestKey,
        activeStreamRequests,
        // API & Auth
        getAccessTokenSilently: getSafeAccessAuth0Token,
        authId,
        currentUserDataPlatform: platformData,
        pocToken,
        // API Call
        apiCall: getStartStream,
        // State Setters
        setStreamState: setStartStreamState,
        setData: setStartData,
        setLoading: setLoadingStart,
        updateSectionData,
        setEndpointErrors,
        setErrorMessages,
        setHasAnimationCompleted: setHasStartAnimationCompleted,
        // Suppress polling only when user clicked refresh for this SAME team and refresh is active
        suppressPolling: !!forceRefresh && !!refreshingLeagueData && isSameTeamContext,
        // Messages
        retrievingMessage: "Retrieving your start analysis...",
        errorKey: "Start",
        setThoughtsCommon,
        onAudioUrl: (url) => applyPolledAgentAudioUrl("start", url),
      });
    } catch (error) {
      console.error("Polling for start completion failed:", error);
      setLoadingStart(false);
      const errorKey = `start-${context.platformId}-${context.leagueId}`;
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
    }
  };

  // Generalized polling function for rundown using utility
  const startPollingForRundownCompletion = async (
    leagueId: string,
    teamName: string, // Add teamName
    signal: AbortSignal | undefined,
    yahooToken: string | null | undefined,
    requestKey: string,
    activeStreamRequests: React.MutableRefObject<Set<string>>,
    context: { platformId: string; leagueId: string; userId: string | undefined },
    currentTeamInfoRef: React.MutableRefObject<any>,
    platformDataOverride?: any
  ) => {
    console.log("Polling started for rundown completion.");
    try {
      const isSameTeamContext =
        !!selectedTeamInfo && selectedTeamInfo.platformId === context.platformId && selectedTeamInfo.leagueId === context.leagueId && selectedTeamInfo.teamName === teamName;
      const platformData = platformDataOverride ?? currentUserDataPlatform;
      // The only change is adding "return await" here
      return await startPollingForCompletion({
        endpointType: "rundown",
        leagueId,
        teamName, // Pass teamName
        yahooToken,
        signal,
        context, // <-- PASS THE CONTEXT HERE
        currentTeamInfoRef,
        requestKey,
        activeStreamRequests,
        // API & Auth
        getAccessTokenSilently: getSafeAccessAuth0Token,
        authId,
        currentUserDataPlatform: platformData,
        pocToken,
        // API Call
        apiCall: getRundownStream,
        // State Setters
        setStreamState: setRundownStreamState,
        setData: setRundownData,
        setLoading: setLoadingRundown,
        updateSectionData,
        setEndpointErrors,
        setErrorMessages,
        setHasAnimationCompleted: setHasRundownAnimationCompleted,
        // Suppress polling only when user clicked refresh for this SAME team and refresh is active
        suppressPolling: !!forceRefresh && !!refreshingLeagueData && isSameTeamContext,
        // Messages
        retrievingMessage: "Retrieving your rundown analysis...",
        errorKey: "Rundown",
        setThoughtsCommon,
        onAudioUrl: (url) => applyPolledAgentAudioUrl("rundown", url),
      });
    } catch (error) {
      console.error("Polling for rundown completion failed:", error);
      setLoadingRundown(false);
      const errorKey = `rundown-${context.platformId}-${context.leagueId}`;
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
    }
  };

  // Generalized polling function for draft advice using utility
  const startPollingForDraftAdviceCompletion = async (
    leagueId: string,
    teamName: string,
    signal: AbortSignal | undefined,
    yahooToken: string | null | undefined,
    requestKey: string,
    activeStreamRequests: React.MutableRefObject<Set<string>>,
    context: { platformId: string; leagueId: string; userId: string | undefined },
    currentTeamInfoRef: React.MutableRefObject<any>,
    platformDataOverride?: any
  ) => {
    console.log("Polling started for draft advice completion.");
    try {
      const isSameTeamContext =
        !!selectedTeamInfo && selectedTeamInfo.platformId === context.platformId && selectedTeamInfo.leagueId === context.leagueId && selectedTeamInfo.teamName === teamName;
      const platformData = platformDataOverride ?? currentUserDataPlatform;
      return await startPollingForCompletion({
        endpointType: "DraftAdvice",
        leagueId,
        teamName,
        yahooToken,
        signal,
        context,
        currentTeamInfoRef,
        requestKey,
        activeStreamRequests,
        getAccessTokenSilently: getSafeAccessAuth0Token,
        authId,
        currentUserDataPlatform: platformData,
        pocToken,
        apiCall: getDraftAdviceStream,
        setStreamState: setDraftAdviceStreamState,
        setData: setDraftAdviceData,
        setLoading: setLoadingDraftAdvice,
        updateSectionData,
        setEndpointErrors,
        setErrorMessages,
        setHasAnimationCompleted: setHasDraftAdviceAnimationCompleted,
        suppressPolling: !!forceRefresh && !!refreshingLeagueData && isSameTeamContext,
        retrievingMessage: "Retrieving your draft advice...",
        errorKey: "DraftAdvice",
        sectionKey: "DraftAdvice",
        setThoughtsCommon,
        onAudioUrl: (url) => applyPolledAgentAudioUrl("DraftAdvice", url),
      });
    } catch (error) {
      console.error("Polling for draft advice completion failed:", error);
      setLoadingDraftAdvice(false);
      const errorKey = `DraftAdvice-${context.platformId}-${context.leagueId}`;
      setEndpointErrors((prev) => ({ ...prev, [errorKey]: true }));
    }
  };

  // This Ref will hold a set of all dynamically created audio summary elements that are currently playing.
  const activeSummaryAudiosRef = useRef(new Set<HTMLAudioElement>());

  // This effect subscribes to mute changes from the audio service for summary audios.
  useEffect(() => {
    const handleMuteChange = (isMuted: boolean) => {
      activeSummaryAudiosRef.current.forEach((audio) => {
        audio.muted = isMuted;
      });
    };
    const unsubscribe = audioService.subscribe(handleMuteChange);
    return unsubscribe; // Cleanup on unmount
  }, []);

  // Function to refetch allowed platforms
  const refetchAllowedPlatforms = useCallback(async () => {
    console.warn("A platform is no longer allowed. Refetching updated list...");
    try {
      const accessToken = await getSafeAccessAuth0Token();
      if (accessToken && pocToken) {
        const platformsFromServer = await getAllowedPlatforms(accessToken, pocToken);
        setAllowedPlatforms(platformsFromServer);
      }
    } catch (e) {
      console.error("Failed to refetch allowed platforms:", e);
    }
  }, [getSafeAccessAuth0Token, pocToken, setAllowedPlatforms]);

  // useEffect to react to "Platform not allowed" errors
  useEffect(() => {
    // Check if any of the current error messages is the platformNotAllowedError
    const hasPlatformError = Object.values(errorMessages).some((message) => message === platformNotAllowedError);

    if (hasPlatformError) {
      refetchAllowedPlatforms();
    }
  }, [errorMessages, refetchAllowedPlatforms]);

  // State to track active requests
  const activeRequests = useRef({
    // rundown: { isLoading: false, platformId: null, teamId: null, leagueId: null },
    // start: { isLoading: false, platformId: null, teamId: null, leagueId: null },
    trades: { isLoading: false, platformId: null, teamId: null, leagueId: null },
    news: { isLoading: false, platformId: null, teamId: null, leagueId: null },
    rankings: { isLoading: false, platformId: null, teamId: null, leagueId: null },
    // waiver: { isLoading: false, platformId: null, teamId: null, leagueId: null },
  });

  // Nuevo: ref del contenedor con scroll y helper
  const pageScrollRef = useRef<HTMLDivElement | null>(null);

  const scrollHomeToTop = useCallback(() => {
    const el = pageScrollRef.current;
    if (el) {
      el.scrollTo({ top: 0, behavior: "auto" }); // instantáneo
    }
    // Fallbacks (iOS/Safari) por si el scroll container no es el esperado
    window.scrollTo({ top: 0, behavior: "auto" });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (isDrawerOpen) scrollHomeToTop();
  }, [isDrawerOpen, scrollHomeToTop]);



  // Keep Quan's sign anchored forward while ANY manual stream is still running (active),
  // even after it entered the result phase. Otherwise, when one endpoint finishes mid-run
  // (clears thoughtsCommon) the sign briefly clears before the next thought from another
  // still-active endpoint repopulates it (the ~1s "goes back then forward" gap).
  const anyManualStreamActive = [
    waiverStreamState,
    startStreamState,
    rundownStreamState,
    DraftAdviceStreamState
  ].some(
    state => state.teamKey === currentTeamKey && state.isStreamActive
  );

  // Analyze Trade runs in its own drawer (not in the stream-state list); track its thought phase
  // via its sign source so the "Loading..." fallback also covers it (prevents the sign flicker).
  const tradeStreamActive = signThoughtsDrawerSource === "trades";

  // RUN ALL only: bridge the gap after super/common thoughts clear until first SSE for a kicked-off endpoint.
  // Use per-stream loading + !isStreamActive + !isGeneratingResult (not global loading OR) so a stuck
  // loadingStart (e.g. Start disabled) does not keep the sign on "Loading..." after others finished.
  const manualStreamLoadingPairs = [
    { state: waiverStreamState, loading: loadingWaiver },
    { state: startStreamState, loading: loadingStart },
    { state: rundownStreamState, loading: loadingRundown },
    { state: DraftAdviceStreamState, loading: loadingDraftAdvice },
  ] as const;

  const runAllAgentsSignBridge =
    Boolean(aiSettings?.autoProcessAI) &&
    !!currentTeamKey && // currentTeamKey is not null to avoid show "Loading..." when no team is selected
    isSuperAnalysisComplete &&
    signThoughtsDrawerSource !== "trades-result" &&
    manualStreamLoadingPairs.some(
      ({ state, loading }) =>
        state.teamKey === currentTeamKey &&
        loading &&
        !state.isStreamActive &&
        !state.isGeneratingResult
    );

  const effectiveThoughtsCommon = thoughtsCommon || ((anyManualStreamActive || tradeStreamActive || runAllAgentsSignBridge) ? "Loading..." : "");
  //TEST TO SHOW HARCODED TEST THOGUHTS (ALSO YOU HAVE TO DISPLAY THE THOUGHTSCOMMONITEM IN SETTINGSHOMEPAGE.TSX (WITHOUT QUAN) OR CHARACTERBUBBLE2.TSX (WITH QUAN))
  // const effectiveThoughtsCommon = "a";

  // Fixed FooterSignUp: non-logged or logged free — also drives bottom padding so last section buttons stay visible
  // Gate on the same ready conditions as main content so the footer is not shown over SuspenseLoader
  const isHomeContentReady =
    !isTokenLoading &&
    !isLoadingPreferences &&
    (!isYahooSelected || !isYahooLoading);


  //GATE FOR THE FOOTER SIGN UP
  const footerSignUpSnoozeHours = frontEnvNumber("REACT_APP_FOOTER_SIGNUP_SNOOZE_HOURS", 24);
  const footerSignUpDismissedAtMs = footerSignUpDismissedAt ? Date.parse(footerSignUpDismissedAt) : NaN;
  const isFooterSignUpSnoozed =
    Number.isFinite(footerSignUpDismissedAtMs) &&
    Date.now() - footerSignUpDismissedAtMs < footerSignUpSnoozeHours * 60 * 60 * 1000; 
  const showFooterSignUp =
    isHomeContentReady &&
    !isFooterSignUpSnoozed &&
    (!isLoggedInAuth0ExperienceGlobal ||
      (userSubscription || "free").toLowerCase() === "free");

  const handleFooterSignUpDismiss = useCallback(() => {
    const dismissedAt = new Date().toISOString(); // readable UTC in Mongo, e.g. 2026-10-14T16:43:00.000Z
    // console.log("FooterSignUp: dismissedAt", dismissedAt);
    setFooterSignUpDismissedAt(dismissedAt);
    void forceSavePreferences({ footerSignUpDismissedAt: dismissedAt }).catch((error) => {
      console.error("FooterSignUp: failed to persist dismiss:", error);
    });
  }, [setFooterSignUpDismissedAt, forceSavePreferences]);


  // useEffect(()=> {
  // // console.log("Waiver stream complete HOMEPAGE:", waiverData);
  // // console.log("Start stream complete HOMEPAGE:", startData);
  // // console.log("Rundown stream HOMEPAGE rundownData:", rundownData);
  // // console.log("Rundown stream HOMEPAGE rundownStreamState:", rundownStreamState);
  // console.log("selectedteam info leagueId", selectedTeamInfo?.leagueId)
  // console.log("selectedteam info platformId", selectedTeamInfo?.platformId)
  // console.log("selectedteam info teamName", selectedTeamInfo?.teamName)
  //   console.log("authId HOMEPAGE:", authId);
  // }, [selectedTeamInfo?.leagueId, selectedTeamInfo?.platformId, selectedTeamInfo?.teamName])

  // useEffect(() => {
  //   console.log("aiSettings", aiSettings);
  // }, [aiSettings]);
  return (
    <>
      {showBackgroundLoader && (
        <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}>
          <SuspenseLoader />
        </Box>
      )}
      <Box
        display="flex"
        flexDirection="column"
        bgcolor="#000000"
        className={`${styles.boxContainerApp} ${styles.appRoot}`}
        sx={{
          height: "100vh", // Fallback for older browsers
          "@supports (height: 100dvh)": {
            height: "100dvh", // Dynamic viewport height
          },
          "@supports (height: 100svh)": {
            height: "100svh", // Small viewport height - better for mobile
            maxHeight: "100svh",
          },
          boxSizing: "border-box",
          // overflowY: "hidden",
          overflowY: isDrawerOpen ? "hidden" : "auto", // Block scroll when drawer open

          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          touchAction: isDrawerOpen ? "none" : "auto",
          pl: 1,
          pr: 1,

          // EXCEPTION: For the platform scroller, allow ONLY horizontal scroll
          "& .platforms-scroll-container": {
            touchAction: "pan-x",
            overflowX: "auto",
          },
          // EXCEPTION: teams accordion must keep vertical pan when logged-in (pageScrollRef sits below)
          "& .accordionDetailTeams": {
            touchAction: "pan-y",
          },
        }}
      >
        {!isTokenLoading && !isLoadingPreferences && (!isYahooSelected || !isYahooLoading) ? (
          <>
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                minHeight: 0,

              }}
            >
              <Stack
                direction="column"
                spacing={0}
                sx={{
                  width: "100%",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                  minHeight: 0,
                }}
              >
                {/* Settings Home Page ALWAYS SHOW when quan is off (audio sound-gear settings-credits-free credits section) */}
                {!aiSettings.quan && (
                  <SettingsHomePage shouldPlayAudio={shouldPlayAudio} authId={authId} setIsSliderOpen={setIsSliderOpen} characterRef={characterRef} thoughtsCommon={effectiveThoughtsCommon} isDrawerOpen={isDrawerOpen} isSliderOpen={isSliderOpen} isMobile={isMobile} isChangingPlatform={isChangingPlatform} handlePartnerSelectAndCloseDrawer={handlePartnerSelectAndCloseDrawer} isAiDialogOpen={isAiDialogOpen} setIsAiDialogOpen={setIsAiDialogOpen} handleConfirmAiSettings={handleConfirmAiSettings} aiSettings={aiSettings} isUpdatingAiSettings={isUpdatingAiSettings} isPaymentOpen={isPaymentOpen} setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} initialPaymentView={initialPaymentView} enableCreditValidation={ENABLE_CREDIT_VALIDATION} userSubscription={userSubscription} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef} />
                )}
                {/* <Box p={0} sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "flex-end", height: isMobile ? "90px" : "110px", width: "100%" }}>
                <SettingsSpeedDial onClose={handlePartnerSelectAndCloseDrawer} />
              </Box> */}

                {/* <Typography variant="body1" color="white"> {aiSettings.quan ? "Quan is enabled" : "No Quan"}</Typography> */}

                {/* Endpoint errors messages modal*/}
                {/* {Object.entries(errorMessages).map(
                  // used object.entries to get the key and value of the errorMessages state (convert object to array whit key and value)
                  ([endpoint, message]) =>
                    //this next is to show the modal ONLY for the common league data failed error
                    (message === commonLeagueDataFailedError || message === platformNotAllowedError || message === noPlayersFoundInTeamError) && (
                      // if the message is not empty, show the modal
                      <EndpointErrorsMessage
                        key={endpoint}
                        open={Boolean(message)}
                        onClose={() => handleCloseErrorMessage(endpoint)}
                        endpointName={endpoint}
                        errorMessage={errorMessages[endpoint]}
                      />
                    )
                )} */}
                {/* </Box> */}

                {/* 3D Character Viewer - full width on mobile, padded on larger screens */}
                {aiSettings.quan && (
                  <Box sx={{ flexShrink: 0, px: { xs: 0, sm: 0 }, py: 0, pt: 1, pb: 0, zIndex: 1400 }}>
                    {/* <CharacterBubble */}
                    <CharacterBubble2
                      ref={characterRef}
                      isDrawerOpen={isDrawerOpen}
                      allowSignWhileDrawerOpen={allowSignWhileDrawerOpen}
                      hideSignForTradeResult={hideSignForTradeResult}
                      backgroundColor="#000000"
                      // // Camera lower to see front of character
                      // cameraPosition={{ x: -0.05, y: 0.5, z: 1.0 }}
                      // cameraTarget={{ x: -0.05, y: 1, z: 0.7 }}
                      // cameraFov={40}
                      // modelScale={1.15}
                      onLoad={() => console.log('Character loaded')}
                      onReadyStateChange={handleQuanReadyStateChange}
                      onError={handleQuanLoadError}
                      onLoadTimeout={handleQuanLoadTimeout}
                      selectedButton={selectedButton}
                      shouldPlayAudio={shouldPlayAudio} authId={authId} setIsSliderOpen={setIsSliderOpen} characterRef={characterRef} thoughtsCommon={effectiveThoughtsCommon} isSliderOpen={isSliderOpen} isMobile={isMobile} isChangingPlatform={isChangingPlatform} handlePartnerSelectAndCloseDrawer={handlePartnerSelectAndCloseDrawer} isAiDialogOpen={isAiDialogOpen} setIsAiDialogOpen={setIsAiDialogOpen} handleConfirmAiSettings={handleConfirmAiSettings} aiSettings={aiSettings} isUpdatingAiSettings={isUpdatingAiSettings} isPaymentOpen={isPaymentOpen} setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} initialPaymentView={initialPaymentView} enableCreditValidation={ENABLE_CREDIT_VALIDATION} userSubscription={userSubscription} isCreditsAnimating={isCreditsAnimating} creditsBoxRef={creditsBoxRef}
                    />
                  </Box>
                )}

                {/* Button to add a new platform */}
                <Box ref={leagueSelectorBoxRef} sx={{ flexShrink: 0 }}>
                  <LeagueSelector
                    expanded={expanded}
                    setExpanded={setExpanded}
                    setIsAddIcon={setIsAddIcon}
                    isSpeedDialOpen={isSpeedDialOpen}
                    setIsSpeedDialOpen={setIsSpeedDialOpen}
                    hasTeams={hasTeams}
                    teamsData={teamsData}
                    setTeamsdata={setTeamsdata}
                    isChangingPlatform={isChangingPlatform}
                    setIsChangingPlatform={setIsChangingPlatform}
                    isYahooSelected={isYahooSelected}
                    setYahooToken={setYahooToken}
                    shakingPlatform={shakingPlatform}
                    setShakingPlatform={setShakingPlatform}
                    deletionMode={deletionMode}
                    setDeletionMode={setDeletionMode}
                    setLoadingNews={setLoadingNews}
                    setLoadingWaiver={setLoadingWaiver}
                    setLoadingStart={setLoadingStart}
                    setLoadingRoster={setLoadingRoster}
                    setLoadingRankings={setLoadingRankings}
                    setLoadingAllRankings={setLoadingAllRankings}
                    setLoadingRundown={setLoadingRundown}
                    setLoadingDraftAdvice={setLoadingDraftAdvice}
                    setHasLoadingStarted={setHasLoadingStarted}
                    setWaiverData={setWaiverData}
                    setStartData={setStartData}
                    setRostersData={setRostersData}
                    setNewsData={setNewsData}
                    setRankingsData={setRankingsData}
                    setAllRankingsData={setAllRankingsData}
                    setRundownData={setRundownData}
                    setDraftAdviceData={setDraftAdviceData}
                    setEndpointErrors={setEndpointErrors}
                    fetchNewsData={fetchNewsData}
                    fetchWaiverData={fetchWaiverData}
                    fetchWaiverDataStream={fetchWaiverDataStream}
                    fetchStartDataStream={fetchStartDataStream}
                    fetchRostersData={fetchRostersData}
                    fetchRankingsData={fetchRankingsData}
                    fetchAllRankingsData={fetchAllRankingsData}
                    fetchRundownDataStream={fetchRundownDataStream}
                    fetchDraftAdviceDataStream={fetchDraftAdviceDataStream}
                    handleLeagueId={handleLeagueId}
                    handlePartnerSelected={handlePartnerSelected}
                    handlePlatformLongPress={handlePlatformLongPress}
                    userDataPlatform={userDataPlatform}
                    teamsHomePageRef={teamsHomePageRef}
                    setErrorMessages={setErrorMessages}
                    fetchSuperFunctionData={fetchSuperFunctionData}
                    // setSuperFunctionData={setSuperFunctionData}
                    setLoadingSuperFunction={setLoadingSuperFunction}
                    commonLeagueDataFailed={commonLeagueDataFailed}
                    setCommonLeagueDataFailed={setCommonLeagueDataFailed}
                    returnedFromBackground={returnedFromBackground}
                    setReturnedFromBackground={setReturnedFromBackground}
                    isHandlingTeamSelection={isHandlingTeamSelection}
                    newClickOnPlatofrm={newClickOnPlatofrm}
                    setNewClickOnPlatofrm={setNewClickOnPlatofrm}
                    setErrorMessageNoDraft={setErrorMessageNoDraft}
                    // Pass stream state setters down
                    setWaiverStreamState={setWaiverStreamState}
                    setStartStreamState={setStartStreamState}
                    setRundownStreamState={setRundownStreamState}
                    setDraftAdviceStreamState={setDraftAdviceStreamState}
                    setThoughtsCommon={setThoughtsCommon}
                    isSuperAnalysisComplete={isSuperAnalysisComplete}
                    setIsSuperAnalysisComplete={setIsSuperAnalysisComplete}
                    setSummaryAudios={setSummaryAudios}
                    hasAudioBeenPlayed={hasAudioBeenPlayed}
                    forceRefresh={forceRefresh}
                    setForceRefresh={setForceRefresh}
                    refreshingLeagueData={refreshingLeagueData}
                    setRefreshingLeagueData={setRefreshingLeagueData}
                    activeRequests={activeRequests}
                    setEstimatedTimes={setEstimatedTimes}
                    activeStreamRequests={activeStreamRequests}
                    calculatorSettings={calculatorSettings}
                    setCalculatorSettings={setCalculatorSettings}
                    leagueSettingsChanged={leagueSettingsChanged}
                    setLeagueSettingsChanged={setLeagueSettingsChanged}
                    selectedModel={selectedModel}
                    userSubscription={userSubscription}
                    characterRef={characterRef}
                    enableCreditValidation={ENABLE_CREDIT_VALIDATION}
                    setIsBenchDrawerOpen={setIsBenchDrawerOpen}
                    setIsPaymentOpen={setIsPaymentOpen}
                    setInitialPaymentView={setInitialPaymentView}
                    openRateLimitDrawer={openRateLimitDrawer}
                  />
                </Box>



                {/* 6 Buttons options in home page when is login and partner selected */}
                {leaguePlatforms.filter((p) => p.isSelected && p.isLoggedIn).length && selectedPartner && selectedTeamInfo?.platformId === leaguePlatforms.find((p) => p.isSelected)?.id && (
                  <Box
                    ref={pageScrollRef}
                    sx={{
                      // Hide SectionHomePage grid while Sync League (panel0) or teams list (panel1) is open — keep mounted
                      display: expanded === "panel0" || expanded === "panel1" ? "none" : undefined,
                      flex: 1,
                      overflowY: isDrawerOpen || expanded === "panel1" ? "hidden" : "auto", // Directly block scroll on the scrolling element
                      overflowX: "hidden",
                      // panel1: teams dropdown overlays this flex region — stop it from stealing touch scroll (Capacitor)
                      pointerEvents: expanded === "panel1" ? "none" : undefined,
                      WebkitOverflowScrolling: "touch",
                      minHeight: 0,
                      maxHeight: "100%",
                      overscrollBehavior: "contain",
                      // In capacitor, we need to add padding for the fixed footer (footer height + safe-area)
                      ...(showFooterSignUp && (mobile || isMobileWebBrowser())
                        ? {
                          paddingBottom: "calc(100px + max(env(safe-area-inset-bottom), 10px))", // space for footer + safe-area
                        }
                        : {}),
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      touchAction: expanded === "panel1" ? "none" : "pan-y",
                    }}
                  >
                    <Box sx={{ p: 0, pt: 0, pb: showFooterSignUp && !(mobile || isMobileWebBrowser()) ? 7 : 0 }}>
                      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <SectionHomePage
                          showPartnerList={showPartnerList}
                          selectedButton={selectedButton}
                          handleButtonSelected={handleButtonSelected}
                          handlePartnerSelectAndOpenDrawer={handlePartnerSelectAndOpenDrawer}
                          handlePartnerSelectAndCloseDrawer={handlePartnerSelectAndCloseDrawer}
                          selectedPartner={selectedPartner}
                          hasLoadingStarted={hasLoadingStarted}
                          userDataPlatform={userDataPlatform}
                          leagueIdPlatform={leagueIdPlatform}
                          loadingRundown={loadingRundown}
                          loadingDraftAdvice={loadingDraftAdvice}
                          loadingStart={loadingStart}
                          loadingRoster={loadingRoster}
                          loadingNews={loadingNews}
                          loadingRankings={loadingRankings}
                          loadingAllRankings={loadingAllRankings}
                          loadingWaiver={loadingWaiver}
                          rundownData={rundownData}
                          DraftAdviceData={DraftAdviceData}
                          startData={startData}
                          rostersData={rostersData}
                          newsData={newsData}
                          rankingsData={rankingsData}
                          allRankingsData={allRankingsData}
                          waiverData={waiverData}
                          setLoadingRundown={setLoadingRundown}
                          setLoadingDraftAdvice={setLoadingDraftAdvice}
                          setLoadingStart={setLoadingStart}
                          setLoadingRoster={setLoadingRoster}
                          setLoadingNews={setLoadingNews}
                          setLoadingRankings={setLoadingRankings}
                          setLoadingAllRankings={setLoadingAllRankings}
                          setLoadingWaiver={setLoadingWaiver}
                          fetchRundownDataStream={fetchRundownDataStream}
                          fetchDraftAdviceDataStream={fetchDraftAdviceDataStream}
                          fetchStartDataStream={fetchStartDataStream}
                          fetchRostersData={fetchRostersData}
                          fetchNewsData={fetchNewsData}
                          fetchRankingsData={fetchRankingsData}
                          fetchAllRankingsData={fetchAllRankingsData}
                          fetchWaiverData={fetchWaiverData}
                          fetchWaiverDataStream={fetchWaiverDataStream}
                          yahooToken={yahooToken}
                          endpointErrors={endpointErrors}
                          setEndpointErrors={setEndpointErrors}
                          selectedTeamInfo={selectedTeamInfo}
                          setErrorMessages={setErrorMessages}
                          commonLeagueDataFailed={commonLeagueDataFailed}
                          setCommonLeagueDataFailed={setCommonLeagueDataFailed}
                          returnedFromBackground={returnedFromBackground}
                          setReturnedFromBackground={setReturnedFromBackground}
                          setShouldPlayAudio={setShouldPlayAudio}
                          waiverStreamState={waiverStreamState}
                          setHasWaiverAnimationCompleted={setHasWaiverAnimationCompleted}
                          hasWaiverAnimationCompleted={hasWaiverAnimationCompleted}
                          startStreamState={startStreamState}
                          hasStartAnimationCompleted={hasStartAnimationCompleted}
                          setHasStartAnimationCompleted={setHasStartAnimationCompleted}
                          rundownStreamState={rundownStreamState}
                          hasRundownAnimationCompleted={hasRundownAnimationCompleted}
                          setHasRundownAnimationCompleted={setHasRundownAnimationCompleted}
                          DraftAdviceStreamState={DraftAdviceStreamState}
                          hasDraftAdviceAnimationCompleted={hasDraftAdviceAnimationCompleted}
                          setHasDraftAdviceAnimationCompleted={setHasDraftAdviceAnimationCompleted}
                          setDraftAdviceData={setDraftAdviceData}
                          setDraftAdviceStreamState={setDraftAdviceStreamState}
                          streamProgress={streamProgress}
                          setStreamProgress={setStreamProgress}
                          streamProgressTargets={streamProgressTargets}
                          // --- NEW PROP FOR RECOVERY ---
                          isRecoveringFromBackground={isRecoveringFromBackground}
                          // --- NEW PROPS FOR AUDIO ---
                          summaryAudios={summaryAudios}
                          summaryAudioRefs={summaryAudioRefs} // Pass the ref down
                          errorMessages={errorMessages}
                          errorMessageNoDraft={errorMessageNoDraft}
                          activeStreamRequests={activeStreamRequests}
                          aiSettings={aiSettings}
                          isSuperAnalysisComplete={isSuperAnalysisComplete}
                          setIsSuperAnalysisComplete={setIsSuperAnalysisComplete}
                          setHasLoadingStarted={setHasLoadingStarted}
                          forceRefresh={forceRefresh}
                          setWaiverData={setWaiverData}
                          setStartData={setStartData}
                          setRundownData={setRundownData}
                          setWaiverStreamState={setWaiverStreamState}
                          setStartStreamState={setStartStreamState}
                          setRundownStreamState={setRundownStreamState}
                          setSummaryAudios={setSummaryAudios}
                          hasAudioBeenPlayed={hasAudioBeenPlayed}
                          activeRequests={activeRequests}
                          estimatedTimes={estimatedTimes}
                          credits={credits}
                          setIsPaymentOpen={setIsPaymentOpen}
                          setInitialPaymentView={setInitialPaymentView}
                          setIsBenchDrawerOpen={setIsBenchDrawerOpen}
                          enableCreditValidation={ENABLE_CREDIT_VALIDATION}
                          getCreditsBoxPosition={getCreditsBoxPosition}
                          calculatorSettings={calculatorSettings}
                          leagueSettingsChanged={leagueSettingsChanged}
                          setLeagueSettingsChanged={setLeagueSettingsChanged}
                          selectedModel={selectedModel}
                          setSelectedModel={setSelectedModel}
                          userSubscription={userSubscription}
                          setUserSubscription={setUserSubscription}
                          setStreamProgressTargets={setStreamProgressTargets}
                          characterRef={characterRef}
                          updateCreditsAfterEndpoint={updateCreditsAfterEndpoint}
                          isCreditsAnimating={isCreditsAnimating}
                          creditsBoxRef={creditsBoxRef}
                          leagueSelectorBoxRef={leagueSelectorBoxRef}
                          setThoughtsCommon={setThoughtsCommon}
                          markSignThoughtsForDrawer={markSignThoughtsForDrawer}
                          clearSignThoughtsDrawerSource={clearSignThoughtsDrawerSource}
                        />
                      </Grid>
                    </Box>
                  </Box>
                )}

                {/* Articles only: shown whenever SectionHomePage grid is not (no platform and/or no league yet) */}
                {/* {!(leaguePlatforms.filter((p) => p.isSelected && p.isLoggedIn).length && selectedPartner) && (allowedButtonsSections?.showArticles ?? true) && (
                  <Box sx={{ p: 1, pt: 0, flexShrink: 0 }}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Box sx={{ position: "relative" }}>
                          <AnimatedButton
                            icon={<ArticleIcon sx={{ fontSize: 44 }} />}
                            label="Articles"
                            hasLoadingStarted
                            onClick={handleStandaloneArticlesClick}
                            selectedPartner=""
                            hasError={false}
                            isAnalysisPending={false}
                            isReadyForManualTrigger
                            endpointEnabled
                          />
                          <Box
                            component="a"
                            href="/articles"
                            tabIndex={-1}
                            aria-hidden
                            sx={{
                              position: "absolute",
                              width: 1,
                              height: 1,
                              p: 0,
                              m: "-1px",
                              overflow: "hidden",
                              clip: "rect(0,0,0,0)",
                              whiteSpace: "nowrap",
                              border: 0,
                            }}
                          >
                            Articles
                          </Box>
                        </Box>
                        {showPartnerList && selectedButton === "articles" && (
                          <ArticlesPartnerList onClose={handlePartnerSelectAndCloseDrawer} />
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                )} */}
              </Stack>
            </Box>

            {/* Footer + help icon should not appear while loading */}
            {/* <Box
              sx={{
                position: "relative",
                ...(mobile || isIOS || isAndroid
                  ? {
                    // In capacitor, we need to set the footer as fixed to ensure visibility
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                  }
                  : {
                    // In web, we need to set the footer as a flexbox to ensure the flow is normal
                    flexShrink: 0,
                  }),
                paddingBottom: "max(env(safe-area-inset-bottom), 10px)",
                paddingTop: 0,
                width: "100%",
                backgroundColor: "#000000",
                overflow: "visible",
                maxWidth: "720px",
                mx: "auto",
              }}
            >
              {/* Show help icon for get started page if the user is not logged in (fixed above footer, right side) */}
            {/* {!isLoggedInAuth0ExperienceGlobal && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: -35,
                    zIndex: 1100,
                    pointerEvents: "auto",
                  }}
                >
                  <HelpGetStartedPage />
                </Box>
              )} */}
            {/* <Footer />
            </Box>  */}
          </>
        ) : (
          <SuspenseLoader />
        )}
      </Box>

      {/* Notifications consent modal (UI only: simulates "user has not accepted yet" when open) */}
      {/* <NotificationsModal
        open={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
      /> */}

      {/* Fixed bottom CTA: non-logged + logged free only (overlay; does not reflow homepage layout) */}
      {showFooterSignUp && (
        <FooterSignUp
          isLoggedInAuth0={!!isLoggedInAuth0ExperienceGlobal}
          setIsPaymentOpen={setIsPaymentOpen}
          setInitialPaymentView={setInitialPaymentView}
          onDismiss={handleFooterSignUpDismiss}
        />
      )}

      {/* Welcome Drawer - One-time onboarding for brand-new users */}
      <WelcomeDrawer open={isWelcomeDrawerOpen} onDismiss={handleWelcomeDismiss} leagueSelectorBoxRef={leagueSelectorBoxRef} />

      {/* Bench Drawer - Auto-opens when freeCredits === 0 */}
      <BenchDrawer open={isBenchDrawerOpen} onClose={() => setIsBenchDrawerOpen(false)} setIsPaymentOpen={setIsPaymentOpen} setInitialPaymentView={setInitialPaymentView} />

      {/* Rate Limit Drawer - Opens when the user exceeds 30 requests per minute */}
      <RateLimitDrawer open={isRateLimitDrawerOpen} onClose={() => setIsRateLimitDrawerOpen(false)} retryAt={rateLimitRetryAt} />
    </>
  );
}

export default HomePage;
